import Decimal from "decimal.js";
import dayjs from "dayjs";
import i18n from './i18n';
import {
  BomData,
  CBomDataWithMonthly,
  Currency,
  DownsideIndex,
  EquipmentExtType,
  MergedChartData,
  MonthlyExpenseData,
  MonthlyProfitAndLossData,
  MonthValue,
  NonWorkingHoursType,
  OriginalProfitAndLossByMonthTable,
  PartNo,
  ProcessBomRowForCalculation,
  ProjectWithRdsData,
  SalesByPartNo,
  SavedEquipmentConfig,
  SavedSimulationConfig,
  UniqueProcessType,
  WorkingDaysType,
} from "../contexts/types";
import { PurposeType } from "../contexts/constants";
import {
  calcAggregateCapexByMonth,
  calcCapexAmountByMonth,
  calcEquipmentDepreciationByYearMonth,
  calcProfitRate,
  calcProfitRateArray, calcRecoverySimulation,
  calcTotalCycleTimeByMonth,
  calcTotalQuantityByMonth,
  calculateCustomerSpecifiedPartsCostByMonth,
  calculateScrapCostByMonth,
  convertEquipmentData, convertMonthlyExpenseData, convertMonthlyValueData, convertToYearMonthObject,
  generateYearMonthList,
  getProjectSalesByMonth,
  getPricesByPartNoMonth,
  getQuantitiesByPartNoMonth,
  getTotalFiscalYearsByPartNo,
  mergeYearMonthValues,
  subtractDecimalArray,
  subtractYearMonthValues,
  sumDecimalArray,
} from "./calculation";
import { convertCurrency } from "./CurrencyConverter";
import type { ConvertFn } from './reviewCalculation';
import { convertMonthlyToYearly } from "./calculation";
import { AnnotationOptions, LabelPosition } from "chartjs-plugin-annotation";
import { formatRecoveryMonths, getLocalCurrencyId, hasDecimal, resolveDisplayPartNo, resolveDisplayPartNoByKey } from "./utils";

/**
 * calcProfitByMonth から返却される個別コストカテゴリの月別配列。
 * 対象PJと同じ年度丸め（per-category convertMonthlyToYearly）を再現するために使用。
 */
export interface CostBreakdownByMonth {
  totalSales: string[];
  totalMaterialCost: string[];
  outsideCost: string[];
  laborCost: string[];
  domesticLaborCost: string[];
  overHeadCost: string[];
  equipmentDepreciation: string[];
  pastEquipmentCost: string[];
  factoryCost: string[];
  otherEquipmentCost: string[];
  domesticOverHeadCost: string[];
  domesticInvestmentDepreciation: string[];
  domesticFactoryBuildingDepreciation: string[];
  domesticOtherDepreciation: string[];
  scrapCost: string[];
  appliedLaborCost: string[];
  domesticSgaCost: string[];
  salesManagementCost: string[];
  devCost: string[];
  royalty: string[];
  otherCost: string[];
  /** pastEquipmentCostがequipmentDepreciationに含まれていない場合true（費用画面の値で別途合算が必要） */
  pastEquipmentCostSeparate?: boolean;
  /** 各コストカテゴリのソース通貨名。未設定時はローカル通貨からの変換にフォールバック */
  sourceCurrencies?: {
    outsideCost?: string;
    laborCost?: string;
    domesticLaborCost?: string;
    overHeadCost?: string;
    factoryCost?: string;
    pastEquipmentCost?: string;
    otherEquipmentCost?: string;
    domesticOverHeadCost?: string;
    domesticInvestmentDepreciation?: string;
    domesticFactoryBuildingDepreciation?: string;
    domesticOtherDepreciation?: string;
    appliedLaborCost?: string;
    domesticSgaCost?: string;
    salesManagementCost?: string;
    devCost?: string;
    royalty?: string;
    otherCost?: string;
  };
}

/**
 * ダウンサイドの「顧客製品寿命」による生産期間短縮時、コスト区分ごとの期間の扱いを揃えるための共通処理。
 * （No.411対応 / B案）
 *
 * - 減価償却費系（投資対象設備・過去投資設備・工場建物・その他設備・国内設備等）:
 *   既取得資産の費用配分であり生産終了後も発生し続けるため、フル期間のまま集計する
 * - 稼働連動費系（材料費・外注費・労務費・工場経費・販管費・仕損費・その他経費）:
 *   生産をやめれば発生しないため、短縮後の期間で打ち切る（打ち切り位置以降を0にする）
 *
 * 配列長そのものは変えない（`convertMonthlyToYearly` 等が「配列長 % 12」で年度境界を判定するため、
 * 物理的にsliceすると年度バケットの区切りがずれる）。値を0に置き換える方式とする。
 *
 * @param values 月別の値配列（稼働連動費）
 * @param activeLength 稼働期間（短縮後の月数）。これ以降の月は0にする
 * @returns 打ち切り後の月別配列（元の配列長を維持）
 */
export const truncateOperationalCostArray = <T extends { value: Decimal }>(
  values: T[],
  activeLength: number
): T[] => {
  // 打ち切り不要（短縮なし・不正値）の場合は元配列をそのまま返す
  if (activeLength < 0 || activeLength >= values.length) {
    return values;
  }
  return values.map((entry, index) =>
    index >= activeLength ? { ...entry, value: new Decimal(0) } : entry
  );
};

/**
 * 稼働連動費（Decimal[]形式）を短縮後の期間で打ち切る。
 * `truncateOperationalCostArray` のDecimal配列版。
 *
 * @param values 月別の値配列
 * @param activeLength 稼働期間（短縮後の月数）
 * @returns 打ち切り後の月別配列（元の配列長を維持）
 */
export const truncateOperationalDecimals = (values: Decimal[], activeLength: number): Decimal[] => {
  if (activeLength < 0 || activeLength >= values.length) {
    return values;
  }
  return values.map((value, index) => (index >= activeLength ? new Decimal(0) : value));
};

/**
 * 設備減価償却費を表示通貨で再計算するための入力データ。
 * 対象PJと同じ「設備金額を表示通貨に変換→表示通貨で償却計算」パスを実現するために使用。
 */
export interface EquipmentDepreciationInputs {
  /** 償却パラメータ適用済みの設備データ（通貨変換前） */
  equipments: EquipmentExtType[];
  /** changedDepreciationStartMonth適用前の元の設備データ（対象PJと一致させる計算用） */
  originalEquipments?: EquipmentExtType[];
  /** 最大年度数 */
  maxYears: number;
  /** プロジェクト開始日 */
  startDate: string;
  /**
   * ラベル数（表示期間の月数）。
   * ※No.411対応（B案）以降、減価償却費のカットオフには使用しない（減価償却費はフル期間で計上する）。
   *   稼働連動費の打ち切り位置および表示期間の判定に使用する。
   */
  labelsLength: number;
}

/**
 * 仕損費を表示通貨で再計算するための入力データ。
 * 対象PJと同じ「各入力を表示通貨に変換→表示通貨で仕損費計算」パスを実現するために使用。
 */
export interface ScrapCostInputs {
  /** BOMデータ（PartNo別、通貨変換前） */
  bomDataByPartNo: Record<string, ProcessBomRowForCalculation[]>;
  /** PartNo別の月別販売数量（structuredClone対応のためnumber[]で保存） */
  quantitiesByPartNoMonth: { partNo: string; year: number; quantities: number[] }[];
  /** 年月リスト（年度） */
  yearMonthListByFiscalYear: { year: number; month: number }[];
  /** 年月リスト（年数） */
  yearMonthListByElapsedYears: { year: number; month: number }[];
  /** 労務費単価（structuredClone対応のためstringで保存） */
  laborCostPerMinuteRaw: string;
  /** 労務費通貨名 */
  laborCostCurrency?: string;
  /** 工場経費単価（structuredClone対応のためstringで保存） */
  factoryExpensePerMinuteRaw: string;
  /** 工場経費通貨名 */
  factoryExpenseCurrency?: string;
}

/**
 * 労務費にダウンサイドシナリオの調整を適用する共通関数
 * @param laborCostByMonth 月別労務費配列
 * @param labels ラベル配列（YYYY/MM形式）
 * @param isDownside ダウンサイドONかどうか
 * @param downsideIndexGraph ダウンサイドシナリオ指数
 * @returns 調整後の月別労務費配列
 */
export const applyDownsideLaborCostAdjustment = (
  laborCostByMonth: Decimal[],
  labels: string[],
  isDownside: boolean,
  downsideIndexGraph: DownsideIndex | null,
): Decimal[] => {
  // ダウンサイドOFFの場合はそのまま返す
  if (!isDownside || !downsideIndexGraph) {
    return laborCostByMonth;
  }

  // 関数宣言
  const parseYearMonth = (label: string) => {
    const [y, m] = label.split("/");
    return { year: parseInt(y), month: parseInt(m) };
  };
  const safeNormalizeValue = (value: number | null | undefined): number => {
    if (value === null || value === undefined || isNaN(value)) return 0;
    return new Decimal(-value).div(100).toNumber();
  };

  // プロジェクト開始年度を取得
  const { year: firstYear, month: firstMonth } = parseYearMonth(labels[0]);
  const projectFiscalYearStart = firstMonth >= 4 ? firstYear : firstYear - 1;

  labels.forEach((label: string, index: number) => {
    // laborCostByMonthの範囲外の場合はスキップ
    if (index >= laborCostByMonth.length) {
      return;
    }
    // 年月を取得 （例）{ 2025, 4 }
    const { year, month } = parseYearMonth(label);
    // 年度を取得 （例）2025
    const fyStart = month >= 4 ? year : year - 1;
    // 年数を取得 （例）1
    const fiscalYearIndex = fyStart - projectFiscalYearStart + 1;

    // 労務費をストレス指数分調整（年度複利）
    const reductionFactorToPrice = (1 - safeNormalizeValue(downsideIndexGraph.laborCostIncrease[fiscalYearIndex - 1]));
    laborCostByMonth[index] = laborCostByMonth[index].times(reductionFactorToPrice);
  });

  return laborCostByMonth;
};

/**
 * 「数量・単価」「材料費」で登録したパーツNo毎の情報から、年間データ（単価、数量、材料費の単価（内部調整単価）合計）を算出する。
 * @param capexPartNoItem 「数量・単価」「材料費」で登録したパーツNo毎の情報
 * @param materialCostByMonth 月別材料単価（キー: YYYY-MM形式）
 * @param labelsOriginal ラベル情報
 * @param targetCurrencyName 対象通貨名
 * @param currencyData 通貨情報
 * @param convertFn 振り返りモード用の月別レート変換関数（未指定時は通貨マスタのレートで変換）
 * @returns 年間データ（単価、数量、材料費の単価（内部調整単価）合計）
 */
export const computeAnnualData = (capexPartNoItem: PartNo, materialCostByMonth: Record<string, Decimal>, labelsOriginal: string[], targetCurrencyName: string, currencyData: Currency[], convertFn?: ConvertFn) => {

  // PartNo型配列に変換
  const partNos = [capexPartNoItem];
  // 開始日付
  const startDate = labelsOriginal[0].replace("/", "-") || "";
  // 月次データ取得
  const quantitiesData = getQuantitiesByPartNoMonth(partNos, startDate);
  // 振り返りモード: 対象PJの損益試算画面と同じく月別レートで販売通貨→対象通貨に変換
  const pricesData = getPricesByPartNoMonth(partNos, targetCurrencyName, currencyData, startDate, convertFn);

  // 年間データ定義
  const annualData: {
    [yearMonth: string]: { price: number; materialCost: Decimal; quantity: number };
  } = {};

  // データ件数
  let globalMonthIndex = 0;

  // 月ごとのデータをループして設定する
  quantitiesData.forEach((quantityEntry) => {
    const { partNo, year, quantities } = quantityEntry;

    // 対応する単価のエントリを探す
    const correspondingPriceEntry = pricesData.find(
      (priceEntry) => priceEntry.partNo === partNo && priceEntry.year === year
    );

    if (!correspondingPriceEntry) {
      console.warn(`単価データが見つかりません: partNo=${partNo}, year=${year}`);
      return;
    }

    const prices = correspondingPriceEntry.prices;

    // 月ごとのデータを処理
    quantities.forEach((quantityDecimal, monthIndex) => {
      const quantity = quantityDecimal.toNumber(); // 数値に変換
      const price = prices[monthIndex]?.toNumber() || 0;

      if (globalMonthIndex >= labelsOriginal.length) {
        console.warn(`labelsOriginalが不足しています`);
        return;
      }

      const fiscalYearIndex = labelsOriginal[globalMonthIndex];
      // データを格納
      if (!annualData[fiscalYearIndex]) {
        annualData[fiscalYearIndex] = { price: 0, materialCost: new Decimal(0), quantity: 0 };
      }
      annualData[fiscalYearIndex].price = price;
      // 月別材料単価を取得（YYYY/MM → YYYY-MM形式に変換）
      const yearMonthKey = fiscalYearIndex.replace("/", "-");
      annualData[fiscalYearIndex].materialCost = materialCostByMonth[yearMonthKey] ?? new Decimal(0);
      annualData[fiscalYearIndex].quantity = quantity;
      globalMonthIndex++;
    });
  });
  return annualData;
};

/**
 * 価格と材料単価を算出する。
 * @param capexPartNoItem パーツNo情報
 * @param originalProfitAndLossByMonth オリジナル月別(当初損益月別）（「対象プロジェクトの利益試算」で登録）
 * @param materialCostByMonth 月別材料費単価（キー: YYYY-MM形式）
 * @param targetCurrencyName 対象通貨名
 * @param currencyData 通貨情報
 * @param downsideIndexGraph ストレス指数
 * @param convertFn 振り返りモード用の月別レート変換関数（未指定時は通貨マスタのレートで変換）
 * @returns 価格と材料単価（当初計画/ダウンサイド）
 */
export const calcMergedChartData = (capexPartNoItem: PartNo, originalProfitAndLossByMonth: OriginalProfitAndLossByMonthTable, materialCostByMonth: Record<string, Decimal>
  , targetCurrencyName: string, currencyData: Currency[], downsideIndexGraph: DownsideIndex, convertFn?: ConvertFn
): MergedChartData => {
  // // ラベル（yyyy/MMをデータ毎に保持）
  let labelsOriginal: string[] = [...originalProfitAndLossByMonth.labels];
  let labelsModified: string[] = [...originalProfitAndLossByMonth.labels];

  // 年間データ算出
  const annualData = computeAnnualData(capexPartNoItem, materialCostByMonth, labelsOriginal, targetCurrencyName, currencyData, convertFn);

  const result: MergedChartData = {
    monthlyMaterialArrayOriginal: [],
    monthlyPriceArrayOriginal: [],
    monthlyPriceArrayModified: [],
    monthlyMaterialArrayModified: [],
  };

  // 日付パース
  const parseYearMonth = (label: string) => {
    const [y, m] = label.split("/");
    return { year: parseInt(y), month: parseInt(m) };
  };
  // プロジェクト開始年度を算出
  const { year: firstYear, month: firstMonth } = parseYearMonth(labelsOriginal[0]);
  const projectFiscalYearStart = firstMonth >= 4 ? firstYear : firstYear - 1;

  labelsOriginal.forEach((label, idx) => {
    const { year, month } = parseYearMonth(label);

    // 年度算出
    const fyStart = month >= 4 ? year : year - 1;

    // 年間データ参照位置を算出
    const fiscalYearIndex = fyStart - projectFiscalYearStart + 1;
    const annualDataKey = label;

    // 対象年のデータを取得
    const { price, materialCost, quantity } =
      annualData[annualDataKey] || { price: 0, materialCost: new Decimal(0), quantity: 0 };

    // ───── オリジナル用 (左チャート) ─────
    // 単価
    result.monthlyPriceArrayOriginal.push(price.toString());
    // 材料費の単価（内部調整単価）
    result.monthlyMaterialArrayOriginal.push(materialCost.toFixed(14));

    // 変数宣言
    let priceModified = new Decimal(price);
    let costModified = materialCost;
    let quantityModified = new Decimal(quantity);
    // ダウンサイドONの場合
    // 単価をストレス指数分減少（年度複利）
    const reductionFactorToPrice = (1 - getDownsideIndexToValue(fiscalYearIndex - 1, downsideIndexGraph));
    priceModified = priceModified?.times(reductionFactorToPrice);
    // 原価をストレス指数分減少（年度複利）
    const reductionFactorToMaterialCost = (1 - getDownsideIndexToCost(fiscalYearIndex - 1, downsideIndexGraph));
    costModified = costModified?.times(reductionFactorToMaterialCost);
    // 数量をストレス指数分減少（月度単利）
    const downsindeIndex = (1 - getDownsideIndexToQuantity(fiscalYearIndex - 1, downsideIndexGraph));
    quantityModified = quantityModified.times(downsindeIndex).toDecimalPlaces(0, Decimal.ROUND_HALF_UP);

    // 単価
    result.monthlyPriceArrayModified.push(priceModified.toString());
    // 材料費の単価（内部調整単価）
    result.monthlyMaterialArrayModified.push(costModified.toString());

  });

  // ストレス指数分短縮（常に適用）
  const downsideIndex = getDownsideIndexToPeriod(downsideIndexGraph);
  const newLength = Math.floor(labelsModified.length * (1 - downsideIndex));

  // 価格と材料単価を短縮
  result.monthlyPriceArrayModified = result.monthlyPriceArrayModified.slice(0, newLength);
  result.monthlyMaterialArrayModified = result.monthlyMaterialArrayModified.slice(0, newLength);

  return result;
}

/**
 * 単価ストレス指数を取得
 * 
 * @param yearIndex 年数
 * @param downsideIndexGraph ストレス指数
 * @returns 数量ストレス指数
 */
export const getDownsideIndexToValue = (yearIndex: number, downsideIndexGraph: DownsideIndex): number => {

  // 関数宣言
  const safeNormalizeValue = (value: number | null | undefined): number => {
    if (value === null || value === undefined || isNaN(value)) return 0;
    return new Decimal(-value).div(100).toNumber();
  };

  // 変数宣言
  let downsideIndex: number = 0;

  // 単価経時変化
  downsideIndex += safeNormalizeValue(downsideIndexGraph.priceChangeOverTime[yearIndex]);

  // 為替変動
  downsideIndex += safeNormalizeValue(downsideIndexGraph.exchangeRateFluctuationToPrice[yearIndex]);

  // 競合状態
  downsideIndex += safeNormalizeValue(downsideIndexGraph.competitorStatusToPrice[yearIndex]);

  // 数量ストレス指数を返却
  return downsideIndex;

}

/**
 * 原価ストレス指数を取得
 * 
 * @param {number} yearIndex 年数
 * @param downsideIndexGraph ダウンサイド指数
 * @returns {number} ストレス指数
 */
export const getDownsideIndexToCost = (yearIndex: number, downsideIndexGraph: DownsideIndex): number => {

  // 関数宣言
  const safeNormalizeValue = (value: number | null | undefined): number => {
    if (value === null || value === undefined || isNaN(value)) return 0;
    return new Decimal(-value).div(100).toNumber();
  };

  // 変数宣言
  let downsideIndex: number = 0;

  // 為替変動
  downsideIndex += safeNormalizeValue(downsideIndexGraph.exchangeRateFluctuationToCost[yearIndex]);

  // 材料高騰
  downsideIndex += safeNormalizeValue(downsideIndexGraph.materialCostIncrease[yearIndex]);

  // 数量ストレス指数を返却
  return downsideIndex;

}

/**
 * 期間ストレス指数を取得
 * 
 * @param downsideIndexGraph ダウンサイド指数
 * @returns {number} ストレス指数
 */
export const getDownsideIndexToPeriod = (downsideIndexGraph: DownsideIndex): number => {

  // 変数宣言
  let downsideIndex: number = 0;

  // 為替変動
  downsideIndex += new Decimal(-downsideIndexGraph.customerProductLifecycle).div(100).toNumber();

  // 数量ストレス指数を返却
  return downsideIndex;

}

/**
 * 数量ストレス指数を取得
 * 
 * @param {number} yearIndex 年数
 * @param downsideIndexGraph ダウンサイド指数
 * @returns {number} ストレス指数
 */
export const getDownsideIndexToQuantity = (yearIndex: number, downsideIndexGraph: DownsideIndex): number => {

  // 関数宣言
  const safeNormalizeValue = (value: number | null | undefined): number => {
    if (value === null || value === undefined || isNaN(value)) return 0;
    return new Decimal(-value).div(100).toNumber();
  };

  // 変数宣言
  let downsideIndex: number = 0;

  // 数量経時変化
  downsideIndex += safeNormalizeValue(downsideIndexGraph.quantityChangeOverTime[yearIndex]);

  // 数量上下変動
  downsideIndex += safeNormalizeValue(downsideIndexGraph.quantityFluctuation[yearIndex]);

  // 競合状態
  downsideIndex += safeNormalizeValue(downsideIndexGraph.competitorStatusToQuantity[yearIndex]);

  // 数量ストレス指数を返却
  return downsideIndex;

}

/**
 *
 * 指定したパーツNoの「価格と材料単価」データを返す。
 * partNoKey（実キー、No.435対応でデータセットに付与済み）があればそれで完全一致検索し、
 * 存在しない場合（partNoKey付与前に保存された旧データ）はlabel文字列のincludes検索にフォールバックする。
 * @param mergedChartData 「価格と材料単価」チャートデータ
 * @param partNo パーツNo（実キー）
 * @param dataType データタイプ
 * @returns データ配列
 */
export const getPriceData = (mergedChartData: any, partNo: string | undefined, dataType: "Price" | "Material"): any[] => {
  const priceDataset = mergedChartData.datasets.find(
    (ds: any) => (
      ds.partNoKey !== undefined
        ? ds.partNoKey === partNo
        : ds.label.includes(partNo)
    ) && ds.label.includes(dataType)
  );

  return priceDataset?.data;
}

/**
 * 指定したパーツNoの「数量（ダウンサイド）」データを返す。
 * partNoKey（実キー、No.435対応でデータセットに付与済み）があればそれで完全一致検索し、
 * 存在しない場合（partNoKey付与前に保存された旧データ）はlabel文字列のincludes検索にフォールバックする。
 * @param quantitiesData 「数量（ダウンサイド）」チャートデータ
 * @param partNo パーツNo（実キー）
 * @returns データ配列
 */
export const getQuantities = (quantitiesData: any, partNo: string | undefined): any[] => {
  const quantitiesDataset = quantitiesData.datasets.find(
    (dataset: any) => (
      dataset.partNoKey !== undefined
        ? dataset.partNoKey === partNo
        : dataset.label.includes(partNo)
    )
  );

  return quantitiesDataset?.data;
}

/**
 *
 * 月次材料費を算出する。
 * @param mergedChartData 価格と材料単価チャートデータ
 * @param quantitiesChartData 数量チャートデータ
 * @param partNoData パーツNo情報
 * @param labels 年月ラベル（オプション）
 * @param projectId プロジェクトID（オプション）
 * @returns 月次材料費
 */
export const calcMaterialCost = (mergedChartData: any, quantitiesChartData: any, partNoData: PartNo[], labels?: string[], projectId?: string, targetCurrencyName?: string, currencyData?: Currency[]) => {

  if (!mergedChartData || !quantitiesChartData) {
    return null;
  }

  const materialCost: Decimal[] = [];

  partNoData.forEach((partNoItem, _) => {
    const partNo = partNoItem.partNo;

    // 「価格と材料単価」から単位材料費の配列を取得
    const materialData = getPriceData(mergedChartData, partNo, "Material");

    // 「数量（ダウンサイド）」チャートデータのデータを取得
    const quantitiesData = getQuantities(quantitiesChartData, partNo);

    if (quantitiesData) {
      quantitiesData.map((quantities, idx) => {
        // 単位材料費
        const materialPrice: Decimal = new Decimal(materialData[idx] || 0);

        // 数量（月毎に入力した数量を使用）
        const quantity: Decimal = new Decimal(quantities || 0);

        if (!materialCost[idx]) {
          materialCost[idx] = new Decimal(0);
        }

        // 材料費
        materialCost[idx] = materialCost[idx].plus(materialPrice.mul(quantity));
      });
    }
  });

  return materialCost;

}

/**
 * 営業利益額の計算
 * @param sales 売上合計
 * @param materialCosts 材料費
 * @param labels 年月ラベル
 * @param isDownside ダウンサイドフラグ
 * @param targetEquipments 使用する設備情報
 * @param projectData プロジェクト情報
 * @param partNoData パーツNo情報
 * @param targetCurrencyName 対象通貨名
 * @param currencyData 通貨情報
 * @param downsideIndexGraph ダウンサイド指数
 * @param decimalCurrency 小数有無
 * @returns 営業利益額
 */
export const calcProfitByMonth = (
  sales: Decimal[],
  materialCosts: Decimal[],
  labels: string[] = [],
  isDownside: boolean = false,
  targetEquipments: EquipmentExtType[],
  projectData: ProjectWithRdsData,
  partNoData: PartNo[],
  targetCurrencyName: string,
  currencyData: Currency[],
  downsideIndexGraph: DownsideIndex | null,
  decimalCurrency: boolean,
  uniqueProcessData: UniqueProcessType[] = [],
  bomData: BomData[] = [],
  originalProjectStartDate?: string,
  pastEquipmentCostOverride?: { expenseData: MonthlyExpenseData[]; currencyName?: string },
  convertFn?: ConvertFn,
): { profit: Decimal[]; costBreakdown: CostBreakdownByMonth; equipmentDepreciationInputs: EquipmentDepreciationInputs; scrapCostInputs: ScrapCostInputs; residualDepreciationTotal: Decimal } => {

  // プロジェクト開始日
  const startDate = projectData?.projectStartDate || "";

  // 売上合計をフォーマット変換
  let currentYear = 1;
  const totalSalesByMonth = labels.map((label, index) => {
    // 年月ラベルから年/月を取得
    const [originalYear, month] = label.split("/").map(Number);
    // 4月の場合は年度をカウントアップ
    if (month === 4 && index !== 0) {
      currentYear += 1;
    }
    // オブジェクトを返す
    return {
      year: currentYear,
      month,
      sales: sales[index]
    };
  });

  // changedDepreciationStartMonthが設定されている場合はdepreciationStartMonthを上書き
  const equipmentsWithChangedDepreciation = targetEquipments.map(equip => ({
    ...equip,
    depreciationStartMonth: equip.changedDepreciationStartMonth ?? equip.depreciationStartMonth
  }));

  // 設備投資額を工場通貨へ変換
  const convertedCapexEquipment = convertEquipmentData(equipmentsWithChangedDepreciation, targetCurrencyName, currencyData, convertFn);

  // 総会計年度数の取得（PartNoのみ参照）
  const totalFiscalYearsByPartNo = getTotalFiscalYearsByPartNo(partNoData);

  // 年月リスト生成（年度）
  // （例: [{ year: 2023, month: 1 },{ year: 2023, month: 2 }...]）
  const yearMonthListByFiscalYear = generateYearMonthList(startDate, totalFiscalYearsByPartNo);

  // 年月リスト生成（年数）
  // （例: [{ year: 1, month: 1 },{ year: 1, month: 2 }...]）
  const yearMonthListByElapsedYears = generateYearMonthList(startDate, totalFiscalYearsByPartNo, true);

  // 材料費合計をフォーマット変換
  const totalMaterialCostByMonth = convertToYearMonthObject(yearMonthListByElapsedYears, materialCosts);

  // ------------------------------
  // 営業利益額
  // |-売上合計
  // | |-製品売上
  // | |-PJ単位金型・設備費回収
  // | └-PJ単位開発費回収
  // | ...
  // ------------------------------

  // ------------------------------
  // 年度別損益
  // | ...
  // |-売上原価
  // | |-材料費
  // | |-外注加工費
  // | |-生産工場労務費
  // | |-国内工場労務費
  // | └-生産工場経費
  // |   |-償却費以外の工場経費
  // |   └-減価償却費
  // |     |-投資対象設備
  // |     |-工場建物
  // |     └-その他の設備
  // | ...
  // ------------------------------

  // 月別の全PartNoのC/T合計（秒）を取得
  const totalCycleTimeByMonth: Decimal[] = calcTotalCycleTimeByMonth(uniqueProcessData, yearMonthListByFiscalYear);

  // 全PartNoの月別販売数量合計を取得（元データ）
  const quantitiesByPartNoMonthOriginal = getQuantitiesByPartNoMonth(partNoData, startDate);
  const totalSalesQuantityByMonthOriginal: Decimal[] = calcTotalQuantityByMonth(
    quantitiesByPartNoMonthOriginal,
    yearMonthListByElapsedYears.length
  );

  // ダウンサイドの場合、数量経時変化のストレス指数を適用
  // プロジェクト開始年度を取得
  const parseYearMonthForDownside = (label: string) => {
    const [y, m] = label.split("/");
    return { year: parseInt(y), month: parseInt(m) };
  };
  const { year: firstYear, month: firstMonth } = labels.length > 0 ? parseYearMonthForDownside(labels[0]) : { year: 0, month: 0 };
  const projectFiscalYearStart = firstMonth >= 4 ? firstYear : firstYear - 1;

  // 数量にストレス指数を適用（生産工場労務費、償却費以外の工場経費で使用）
  const totalSalesQuantityByMonth: Decimal[] = totalSalesQuantityByMonthOriginal.map((qty, index) => {
    if (!isDownside || !downsideIndexGraph || index >= labels.length) {
      return qty;
    }
    const { year, month } = parseYearMonthForDownside(labels[index]);
    const fyStart = month >= 4 ? year : year - 1;
    const fiscalYearIndex = fyStart - projectFiscalYearStart + 1;
    const downsideIdx = 1 - getDownsideIndexToQuantity(fiscalYearIndex - 1, downsideIndexGraph);
    return qty.times(downsideIdx).toDecimalPlaces(0, Decimal.ROUND_HALF_UP);
  });

  // quantitiesByPartNoMonthにもストレス指数を適用（仕損費計算用）
  const quantitiesByPartNoMonth: { partNo: string; year: number; quantities: Decimal[] }[] =
    quantitiesByPartNoMonthOriginal.map((item) => {
      const adjustedQuantities = item.quantities.map((qty: Decimal, index: number) => {
        if (!isDownside || !downsideIndexGraph || index >= labels.length) {
          return qty;
        }
        const { year, month } = parseYearMonthForDownside(labels[index]);
        const fyStart = month >= 4 ? year : year - 1;
        const fiscalYearIndex = fyStart - projectFiscalYearStart + 1;
        const downsideIdx = 1 - getDownsideIndexToQuantity(fiscalYearIndex - 1, downsideIndexGraph);
        return qty.times(downsideIdx).toDecimalPlaces(0, Decimal.ROUND_HALF_UP);
      });
      return { ...item, quantities: adjustedQuantities };
    });

  // 月別費用データをパース（6項目共通で使用）
  const monthlyExpenseData: MonthlyExpenseData[] = projectData.monthlyExpenseData
    ? JSON.parse(projectData.monthlyExpenseData)
    : [];

  // #region 外注加工費

  // 通貨IDから通貨名を取得
  const outsourcingCostCurrency = currencyData?.find(
    c => c.currencyId === projectData.outsourcingProcessingCostCurrencyId
  )?.currencyName;

  // 月別外注加工費を計算（monthlyExpenseDataから取得）
  const outsideCostByMonthArray = convertMonthlyExpenseData(
    monthlyExpenseData,
    "outsourcingProcessingCost",
    outsourcingCostCurrency,
    targetCurrencyName,
    currencyData,
    yearMonthListByFiscalYear,
    decimalCurrency,
    "convertOutsourcingCost",
    convertFn
  );

  const outsideCostByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, outsideCostByMonthArray);

  // ソース通貨値を抽出（costBreakdown用: source→display直接変換パスのため）
  const outsideCostByMonthSource = convertMonthlyExpenseData(
    monthlyExpenseData, "outsourcingProcessingCost",
    outsourcingCostCurrency, outsourcingCostCurrency, currencyData,
    yearMonthListByFiscalYear, hasDecimal(outsourcingCostCurrency ?? ""), "convertOutsourcingCost_source"
  );
  const outsideCostSourceByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, outsideCostByMonthSource);

  // #endregion 外注加工費

  // #region 生産工場労務費

  // 通貨IDから通貨名を取得
  const productionLaborCostCurrency = currencyData?.find(
    c => c.currencyId === projectData?.productionLaborCostCurrencyId
  )?.currencyName;

  // 生産工場労務費単価を取得（仕損費の計算に使用）
  // targetCurrencyNameに変換（仕損費計算で他の項目と通貨を合わせるため）
  const laborCostPerMinuteRaw = new Decimal(projectData?.productionFactoryLaborUnitPrice ?? 0);
  const laborCostPerMinute = productionLaborCostCurrency && targetCurrencyName && currencyData
    ? convertFn
      ? convertFn(laborCostPerMinuteRaw, productionLaborCostCurrency, targetCurrencyName)
      : convertCurrency(laborCostPerMinuteRaw, productionLaborCostCurrency, targetCurrencyName, currencyData)
    : laborCostPerMinuteRaw;

  // 月別生産工場労務費を計算（monthlyExpenseDataから取得）
  const laborCostByMonth = convertMonthlyExpenseData(
    monthlyExpenseData,
    "productionLaborCost",
    productionLaborCostCurrency,
    targetCurrencyName,
    currencyData,
    yearMonthListByFiscalYear,
    decimalCurrency,
    "calcProductionLaborCost",
    convertFn
  );

  // ベースアップ率はここでは適用しない（ベースアップ率は費用画面の自動算出時のみ適用し、monthlyExpenseDataの値をそのまま使う）

  // ダウンサイドONの場合、ダウンサイドシナリオの調整を適用
  applyDownsideLaborCostAdjustment(laborCostByMonth, labels, isDownside, downsideIndexGraph);

  const laborCostByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, laborCostByMonth);

  // ソース通貨値を抽出（costBreakdown用）
  const laborCostByMonthSource = convertMonthlyExpenseData(
    monthlyExpenseData, "productionLaborCost",
    productionLaborCostCurrency, productionLaborCostCurrency, currencyData,
    yearMonthListByFiscalYear, hasDecimal(productionLaborCostCurrency ?? ""), "calcProductionLaborCost_source"
  );
  // ソース通貨側もベースアップ率は適用しない（費用画面の自動算出時のみ適用）
  applyDownsideLaborCostAdjustment(laborCostByMonthSource, labels, isDownside, downsideIndexGraph);
  const laborCostSourceByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, laborCostByMonthSource);

  // #endregion 生産工場労務費

  // #region 国内工場労務費

  // 通貨IDから通貨名を取得
  const domesticLaborCostCurrency = currencyData?.find(
    c => c.currencyId === projectData.domesticLaborCostCurrencyId
  )?.currencyName;

  // 月別国内工場労務費を計算（monthlyExpenseDataから取得）
  const domesticLaborCostByMonth = convertMonthlyExpenseData(
    monthlyExpenseData,
    "domesticLaborCost",
    domesticLaborCostCurrency,
    targetCurrencyName,
    currencyData,
    yearMonthListByFiscalYear,
    decimalCurrency,
    "calcDomesticLaborCost",
    convertFn
  );

  // 国内工場労務費のベースアップ率はここでは適用しない（費用画面の自動算出時のみ適用）

  // ダウンサイドONの場合、ダウンサイドシナリオの調整を適用
  applyDownsideLaborCostAdjustment(domesticLaborCostByMonth, labels, isDownside, downsideIndexGraph);

  const domesticLaborCostByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, domesticLaborCostByMonth);

  // ソース通貨値を抽出（costBreakdown用）
  const domesticLaborCostByMonthSource = convertMonthlyExpenseData(
    monthlyExpenseData, "domesticLaborCost",
    domesticLaborCostCurrency, domesticLaborCostCurrency, currencyData,
    yearMonthListByFiscalYear, hasDecimal(domesticLaborCostCurrency ?? ""), "calcDomesticLaborCost_source"
  );
  // 国内工場労務費（ソース通貨側）もベースアップ率は適用しない（費用画面の自動算出時のみ適用）
  applyDownsideLaborCostAdjustment(domesticLaborCostByMonthSource, labels, isDownside, downsideIndexGraph);
  const domesticLaborCostSourceByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, domesticLaborCostByMonthSource);

  // #endregion 国内工場労務費

  // #region 投資対象設備

  // 投資対象設備
  // 計算方法: 設備投資額 / 償却年数（設備別に償却開始月から償却年数で割る）
  // No.411対応（B案）: 減価償却費は既取得資産の費用配分のため、ダウンサイドの生産期間短縮後も
  // 発生し続ける。そのため labels.length でのゼロ埋めは行わず、フル期間のまま保持する。
  const equipmentDepreciationByMonth = calcEquipmentDepreciationByYearMonth(totalFiscalYearsByPartNo, convertedCapexEquipment, decimalCurrency, startDate, false);

  // #endregion 投資対象設備

  // #region 工場建物

  // 通貨IDから通貨名を取得
  const factoryBuildingCostCurrency = currencyData?.find(
    c => c.currencyId === projectData.factoryBuildingDepreciationCurrencyId
  )?.currencyName;

  // 月別工場建物を計算（monthlyExpenseDataから取得）
  const factoryBuildingCostByMonthArray = convertMonthlyExpenseData(
    monthlyExpenseData,
    "factoryBuildingDepreciation",
    factoryBuildingCostCurrency,
    targetCurrencyName,
    currencyData,
    yearMonthListByFiscalYear,
    decimalCurrency,
    "convertFactoryBuildingCost",
    convertFn
  );

  const factoryCostByMonth = convertToYearMonthObject(yearMonthListByElapsedYears, factoryBuildingCostByMonthArray);

  // ソース通貨値を抽出（costBreakdown用）
  const factoryBuildingCostByMonthSource = convertMonthlyExpenseData(
    monthlyExpenseData, "factoryBuildingDepreciation",
    factoryBuildingCostCurrency, factoryBuildingCostCurrency, currencyData,
    yearMonthListByFiscalYear, hasDecimal(factoryBuildingCostCurrency ?? ""), "convertFactoryBuildingCost_source"
  );
  const factoryCostSourceByMonth = convertToYearMonthObject(yearMonthListByElapsedYears, factoryBuildingCostByMonthSource);

  // #endregion 工場建物

  // #region その他の設備

  // その他の設備
  let otherEquipmentCostByMonth: { year: number; month: number; value: Decimal }[] = [];
  let otherEquipmentCostSourceByMonth: { year: number; month: number; value: Decimal }[] = [];
  // 通貨IDから通貨名を取得（costBreakdown.sourceCurrencies用にブロック外で参照）
  const otherEquipmentCostCurrency = currencyData?.find(
    c => c.currencyId === projectData?.otherEquipmentCostCurrencyId
  )?.currencyName;
  if (projectData?.otherEquipmentCost) {

    // JSONパース（年月単位のデータ構造）
    const otherEquipmentCost: MonthValue[] = JSON.parse(projectData.otherEquipmentCost);

    // 月別その他の設備を計算（年月単位のデータを変換）
    const otherEquipmentCostByMonthNew = convertMonthlyValueData(
      otherEquipmentCost,
      otherEquipmentCostCurrency,
      targetCurrencyName,
      currencyData,
      yearMonthListByFiscalYear,
      decimalCurrency,
      "convertOtherEquipmentCost",
      convertFn
    );

    otherEquipmentCostByMonth = convertToYearMonthObject(yearMonthListByElapsedYears, otherEquipmentCostByMonthNew);

    // ソース通貨値を抽出（costBreakdown用）
    const otherEquipmentCostByMonthSource = convertMonthlyValueData(
      otherEquipmentCost,
      otherEquipmentCostCurrency, otherEquipmentCostCurrency, currencyData,
      yearMonthListByFiscalYear, hasDecimal(otherEquipmentCostCurrency ?? ""), "convertOtherEquipmentCost_source"
    );
    otherEquipmentCostSourceByMonth = convertToYearMonthObject(yearMonthListByElapsedYears, otherEquipmentCostByMonthSource);
  }

  // #endregion その他の設備

  // #region 過去PJの投資対象設備

  // 通貨IDから通貨名を取得（pastEquipmentCostOverrideが渡された場合はその通貨名を優先）
  const pastEquipmentCostCurrency = pastEquipmentCostOverride?.currencyName
    ?? currencyData?.find(c => c.currencyId === projectData?.pastEquipmentCostCurrencyId)?.currencyName;

  // pastEquipmentCostOverrideが渡された場合は、そのデータから過去PJ投資対象設備を取得
  // （当初投資・回収チャート等で、projectDataが過去PJの場合でも現在PJの費用画面データを使用するため）
  const pastEquipmentCostMonthlyData = pastEquipmentCostOverride?.expenseData || monthlyExpenseData;

  // ソース通貨値を抽出（costBreakdown用。表示通貨変換・Excel出力用）
  // ※pastEquipmentCostは費用画面で選択した通貨（pastEquipmentCostCurrencyId）で保存されている
  const pastEquipmentCostByMonthSource = convertMonthlyExpenseData(
    pastEquipmentCostMonthlyData, "pastEquipmentCost",
    pastEquipmentCostCurrency, pastEquipmentCostCurrency, currencyData,
    yearMonthListByFiscalYear, hasDecimal(pastEquipmentCostCurrency ?? ""), "convertPastEquipmentCost_source"
  );
  const pastEquipmentCostSourceByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, pastEquipmentCostByMonthSource);

  // 振り返りモード: 過去PJ設備の疑似検収月 = 最初に非ゼロ計上される月。
  // 検収月レート均等換算とするため、全月同じレートで換算する
  const pastEquipPseudoAcceptanceYm = pastEquipmentCostMonthlyData
    ?.filter(e => (e.pastEquipmentCost ?? 0) !== 0)
    .sort((a, b) => a.year !== b.year ? a.year - b.year : a.month - b.month)[0];
  const pastEquipConvertFn: ConvertFn | undefined = (convertFn && pastEquipPseudoAcceptanceYm)
    ? (amount, from, to) => convertFn(amount, from, to, `${pastEquipPseudoAcceptanceYm.year}-${String(pastEquipPseudoAcceptanceYm.month).padStart(2, "0")}`)
    : convertFn;

  // 費用画面の過去PJ投資対象設備をターゲット通貨に変換して減価償却費計算に使用
  let pastEquipmentCostByMonth: { year: number; month: number; value: Decimal }[] = [];
  if (pastEquipmentCostOverride) {
    const pastEquipmentCostByMonthArray = convertMonthlyExpenseData(
      pastEquipmentCostOverride.expenseData, "pastEquipmentCost",
      pastEquipmentCostCurrency, targetCurrencyName, currencyData,
      yearMonthListByFiscalYear, decimalCurrency, "convertPastEquipmentCost",
      pastEquipConvertFn
    );
    pastEquipmentCostByMonth = convertToYearMonthObject(yearMonthListByElapsedYears, pastEquipmentCostByMonthArray);
  }

  // #endregion 過去PJの投資対象設備

  // #region 減価償却費

  // 減価償却費
  // 計算方法: 投資対象設備 + 工場建物 + その他の設備
  // pastEquipmentCostOverrideが渡された場合: 費用画面の過去PJ投資対象設備（pastEquipmentCostByMonth）を加算
  // 渡されない場合: 過去PJ設備は設備マスタ経由でequipmentDepreciationByMonthに含まれるため別途加算しない
  const depreciationByMonth = mergeYearMonthValues(
    equipmentDepreciationByMonth,
    factoryCostByMonth,
    otherEquipmentCostByMonth,
    ...(pastEquipmentCostOverride ? [pastEquipmentCostByMonth] : [])
  );

  // #endregion 減価償却費

  // #region 償却費以外の工場経費

  // 通貨IDから通貨名を取得
  const overseasFactoryExpensesCurrency = currencyData?.find(
    c => c.currencyId === projectData?.overseasFactoryExpensesCurrencyId
  )?.currencyName;

  // 生産工場経費単価を取得（仕損費の計算に使用）
  // targetCurrencyNameに変換（仕損費計算で他の項目と通貨を合わせるため）
  const factoryExpensePerMinuteRaw = new Decimal(projectData?.productionFactoryExpenseUnitPrice ?? 0);
  const factoryExpensePerMinute = overseasFactoryExpensesCurrency && targetCurrencyName && currencyData
    ? convertFn
      ? convertFn(factoryExpensePerMinuteRaw, overseasFactoryExpensesCurrency, targetCurrencyName)
      : convertCurrency(factoryExpensePerMinuteRaw, overseasFactoryExpensesCurrency, targetCurrencyName, currencyData)
    : factoryExpensePerMinuteRaw;

  // 月別償却費以外の工場経費を計算（monthlyExpenseDataから取得）
  const overHeadCostByMonthArray = convertMonthlyExpenseData(
    monthlyExpenseData,
    "overseasFactoryExpenses",
    overseasFactoryExpensesCurrency,
    targetCurrencyName,
    currencyData,
    yearMonthListByFiscalYear,
    decimalCurrency,
    "calcFactoryOverheadCost",
    convertFn
  );

  const overHeadCostByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, overHeadCostByMonthArray);

  // ソース通貨値を抽出（costBreakdown用）
  const overHeadCostByMonthSource = convertMonthlyExpenseData(
    monthlyExpenseData, "overseasFactoryExpenses",
    overseasFactoryExpensesCurrency, overseasFactoryExpensesCurrency, currencyData,
    yearMonthListByFiscalYear, hasDecimal(overseasFactoryExpensesCurrency ?? ""), "calcFactoryOverheadCost_source"
  );
  const overHeadCostSourceByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, overHeadCostByMonthSource);

  // #endregion 償却費以外の工場経費

  // #region 生産工場経費

  // 計算方法: 償却費以外の工場経費 + 減価償却費
  // ※No.411対応（B案）により、償却費以外の工場経費は稼働連動費として短縮後の期間で打ち切るため、
  //   合算処理は売上原価の算出箇所（factoryExpensesTruncated）で行う

  // #endregion 生産工場経費

  // #region 国内工場経費

  // 国内工場経費-償却費以外の工場経費
  const domesticFactoryExpensesCurrency = currencyData?.find(
    c => c.currencyId === projectData?.domesticFactoryExpensesCurrencyId
  )?.currencyName;

  const domesticOverHeadCostByMonth = convertMonthlyExpenseData(
    monthlyExpenseData,
    "domesticFactoryExpenses",
    domesticFactoryExpensesCurrency,
    targetCurrencyName,
    currencyData,
    yearMonthListByFiscalYear,
    decimalCurrency,
    "convertDomesticOverHeadCost",
    convertFn
  );
  const domesticOverHeadCostByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, domesticOverHeadCostByMonth);

  // ソース通貨値を抽出（costBreakdown用）
  const domesticOverHeadCostByMonthSource = convertMonthlyExpenseData(
    monthlyExpenseData, "domesticFactoryExpenses",
    domesticFactoryExpensesCurrency, domesticFactoryExpensesCurrency, currencyData,
    yearMonthListByFiscalYear, hasDecimal(domesticFactoryExpensesCurrency ?? ""), "convertDomesticOverHeadCost_source"
  );
  const domesticOverHeadCostSourceByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, domesticOverHeadCostByMonthSource);

  // 国内工場経費-投資対象設備
  const domesticInvestmentDepreciationCurrency = currencyData?.find(
    c => c.currencyId === projectData?.domesticInvestmentDepreciationCurrencyId
  )?.currencyName;
  const domesticInvestmentDepreciationByMonth = convertMonthlyExpenseData(
    monthlyExpenseData,
    "domesticInvestmentDepreciation",
    domesticInvestmentDepreciationCurrency,
    targetCurrencyName,
    currencyData,
    yearMonthListByFiscalYear,
    decimalCurrency,
    "convertDomesticInvestmentDepreciation",
    convertFn
  );
  const domesticInvestmentDepreciationByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, domesticInvestmentDepreciationByMonth);
  // ソース通貨値を抽出（costBreakdown用）
  const domesticInvestmentDepreciationByMonthSource = convertMonthlyExpenseData(
    monthlyExpenseData, "domesticInvestmentDepreciation",
    domesticInvestmentDepreciationCurrency, domesticInvestmentDepreciationCurrency, currencyData,
    yearMonthListByFiscalYear, hasDecimal(domesticInvestmentDepreciationCurrency ?? ""), "convertDomesticInvestmentDepreciation_source"
  );
  const domesticInvestmentDepreciationSourceByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, domesticInvestmentDepreciationByMonthSource);

  // 国内工場経費-工場建物
  const domesticFactoryBuildingDepreciationCurrency = currencyData?.find(
    c => c.currencyId === projectData?.domesticFactoryBuildingDepreciationCurrencyId
  )?.currencyName;
  const domesticFactoryBuildingDepreciationByMonth = convertMonthlyExpenseData(
    monthlyExpenseData,
    "domesticFactoryBuildingDepreciation",
    domesticFactoryBuildingDepreciationCurrency,
    targetCurrencyName,
    currencyData,
    yearMonthListByFiscalYear,
    decimalCurrency,
    "convertDomesticFactoryBuildingDepreciation",
    convertFn
  );
  const domesticFactoryBuildingDepreciationByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, domesticFactoryBuildingDepreciationByMonth);
  // ソース通貨値を抽出（costBreakdown用）
  const domesticFactoryBuildingDepreciationByMonthSource = convertMonthlyExpenseData(
    monthlyExpenseData, "domesticFactoryBuildingDepreciation",
    domesticFactoryBuildingDepreciationCurrency, domesticFactoryBuildingDepreciationCurrency, currencyData,
    yearMonthListByFiscalYear, hasDecimal(domesticFactoryBuildingDepreciationCurrency ?? ""), "convertDomesticFactoryBuildingDepreciation_source"
  );
  const domesticFactoryBuildingDepreciationSourceByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, domesticFactoryBuildingDepreciationByMonthSource);

  // 国内工場経費-その他の設備
  const domesticOtherDepreciationCurrency = currencyData?.find(
    c => c.currencyId === projectData?.domesticOtherDepreciationCurrencyId
  )?.currencyName;
  const domesticOtherDepreciationByMonth = convertMonthlyExpenseData(
    monthlyExpenseData,
    "domesticOtherDepreciation",
    domesticOtherDepreciationCurrency,
    targetCurrencyName,
    currencyData,
    yearMonthListByFiscalYear,
    decimalCurrency,
    "convertDomesticOtherDepreciation",
    convertFn
  );
  const domesticOtherDepreciationByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, domesticOtherDepreciationByMonth);
  // ソース通貨値を抽出（costBreakdown用）
  const domesticOtherDepreciationByMonthSource = convertMonthlyExpenseData(
    monthlyExpenseData, "domesticOtherDepreciation",
    domesticOtherDepreciationCurrency, domesticOtherDepreciationCurrency, currencyData,
    yearMonthListByFiscalYear, hasDecimal(domesticOtherDepreciationCurrency ?? ""), "convertDomesticOtherDepreciation_source"
  );
  const domesticOtherDepreciationSourceByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, domesticOtherDepreciationByMonthSource);

  // 国内工場経費-減価償却費（投資対象設備 + 工場建物 + その他の設備）
  const domesticDepreciationByYearMonth = mergeYearMonthValues(
    domesticInvestmentDepreciationByYearMonth,
    domesticFactoryBuildingDepreciationByYearMonth,
    domesticOtherDepreciationByYearMonth
  );

  // 国内工場経費（合計）= 償却費以外 + 減価償却費
  // ※No.411対応（B案）により、償却費以外の国内工場経費は稼働連動費として短縮後の期間で打ち切るため、
  //   合算処理は売上原価の算出箇所（domesticFactoryExpensesTruncated）で行う

  // #endregion 国内工場経費

  // #region 仕損費

  // BOMデータをPartNo別にグループ化（ProcessBomRowForCalculation[]形式に変換）
  const bomDataByPartNo: Record<string, ProcessBomRowForCalculation[]> = {};

  // JSON文字列をRecord<string, string>形式にパース
  const parseMonthlyData = (jsonStr: string | null | undefined): Record<string, string> => {
    if (!jsonStr) return {};
    try {
      return JSON.parse(jsonStr);
    } catch {
      return {};
    }
  };

  for (const bomItem of bomData) {
    const partNo = bomItem.partNo ?? "";
    if (!bomDataByPartNo[partNo]) {
      bomDataByPartNo[partNo] = [];
    }

    bomDataByPartNo[partNo].push({
      seq: bomItem.seq ?? "",
      level: bomItem.level ?? "",
      isCustomerSpecifiedParts: bomItem.isCustomerSpecifiedParts,
      purchaseCurrencyId: bomItem.purchaseCurrencyId,
      amountMonthly: parseMonthlyData(bomItem.amountMonthly),
      cycleTimeMonthly: parseMonthlyData(bomItem.cycleTimeMonthly),
      yieldMonthly: parseMonthlyData(bomItem.yieldMonthly),
    });
  }

  // BOMデータの累積材料費（amountMonthly）を購入通貨からターゲット通貨に変換
  const bomDataConverted: Record<string, ProcessBomRowForCalculation[]> = {};
  for (const partNo of Object.keys(bomDataByPartNo)) {
    bomDataConverted[partNo] = bomDataByPartNo[partNo].map(row => {
      // 購入通貨を取得
      const purchaseCurrencyName = currencyData?.find(
        c => c.currencyId === row.purchaseCurrencyId
      )?.currencyName;
      // 購入通貨とターゲット通貨が同じ、または購入通貨が未設定の場合は変換不要
      if (!purchaseCurrencyName || purchaseCurrencyName === targetCurrencyName) {
        return row;
      }
      // amountMonthlyの値を購入通貨からターゲット通貨に変換
      const convertedAmountMonthly: Record<string, string> = {};
      for (const [yearMonth, amount] of Object.entries(row.amountMonthly || {})) {
        if (amount && amount !== "") {
          const convertedAmount = convertFn
            ? convertFn(new Decimal(amount), purchaseCurrencyName, targetCurrencyName, yearMonth)
            : convertCurrency(
              new Decimal(amount).toNumber(),
              purchaseCurrencyName,
              targetCurrencyName,
              currencyData
            );
          convertedAmountMonthly[yearMonth] = convertedAmount.toString();
        } else {
          convertedAmountMonthly[yearMonth] = amount;
        }
      }
      return {
        ...row,
        amountMonthly: convertedAmountMonthly,
      };
    });
  }

  // 月別仕損費を計算
  const scrapCostByMonth = calculateScrapCostByMonth(
    bomDataConverted,
    quantitiesByPartNoMonth,
    yearMonthListByFiscalYear,
    laborCostPerMinute,
    factoryExpensePerMinute
  );

  const scrapCostByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, scrapCostByMonth);

  // #endregion 仕損費

  // #region 売上原価

  // 計算方法: 材料費 + 外注加工費 + 生産工場労務費 + 国内工場労務費 + 生産工場経費 + 国内工場経費 + 仕損費

  // No.411対応（B案）: 稼働連動費（材料費・外注費・労務費・償却費以外の工場経費・仕損費）は
  // ダウンサイドの生産期間短縮後には発生しないため、短縮後の期間（labels.length）で打ち切る。
  // 減価償却費系（投資対象設備・工場建物・その他設備・国内設備等）はフル期間のまま残す。
  const activeMonthLength = labels.length;

  // 生産工場経費 = 償却費以外の工場経費（打ち切り） + 減価償却費（フル期間）
  const factoryExpensesTruncated = mergeYearMonthValues(
    truncateOperationalCostArray(overHeadCostByYearMonth, activeMonthLength),
    depreciationByMonth,
  );

  // 国内工場経費 = 償却費以外の工場経費（打ち切り） + 減価償却費（フル期間）
  const domesticFactoryExpensesTruncated = mergeYearMonthValues(
    truncateOperationalCostArray(domesticOverHeadCostByYearMonth, activeMonthLength),
    domesticDepreciationByYearMonth,
  );

  const salesCostByMonth = mergeYearMonthValues(
    truncateOperationalCostArray(totalMaterialCostByMonth, activeMonthLength),
    truncateOperationalCostArray(outsideCostByYearMonth, activeMonthLength),
    truncateOperationalCostArray(laborCostByYearMonth, activeMonthLength),
    truncateOperationalCostArray(domesticLaborCostByYearMonth, activeMonthLength),
    factoryExpensesTruncated,
    domesticFactoryExpensesTruncated,
    truncateOperationalCostArray(scrapCostByYearMonth, activeMonthLength),
  );

  // #endregion 売上原価

  // ------------------------------
  // 年度別損益
  // | ...
  // |-販売費及び一般管理費
  // | |-生産工場販管費
  // | |-国内工場販管費
  // | |-本社・販社販管費
  // | |-開発費
  // | └-ロイヤリティ
  // | ...
  // ------------------------------

  // #region 生産工場販管費

  // 通貨IDから通貨名を取得
  const productionSgaExpensesCurrency = currencyData?.find(
    c => c.currencyId === projectData?.productionSgaExpensesCurrencyId
  )?.currencyName;

  // 月別生産工場販管費を計算（monthlyExpenseDataから取得）
  const appliedLaborCostByMonthArray = convertMonthlyExpenseData(
    monthlyExpenseData,
    "productionSgaExpenses",
    productionSgaExpensesCurrency,
    targetCurrencyName,
    currencyData,
    yearMonthListByFiscalYear,
    decimalCurrency,
    "convertProductionSgaExpenses",
    convertFn
  );

  const appliedLaborCostByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, appliedLaborCostByMonthArray);

  // ソース通貨値を抽出（costBreakdown用）
  const appliedLaborCostByMonthSource = convertMonthlyExpenseData(
    monthlyExpenseData, "productionSgaExpenses",
    productionSgaExpensesCurrency, productionSgaExpensesCurrency, currencyData,
    yearMonthListByFiscalYear, hasDecimal(productionSgaExpensesCurrency ?? ""), "convertProductionSgaExpenses_source"
  );
  const appliedLaborCostSourceByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, appliedLaborCostByMonthSource);

  // #endregion 生産工場販管費

  // #region 国内工場販管費

  // 通貨IDから通貨名を取得
  const domesticSgaExpensesCurrency = currencyData?.find(
    c => c.currencyId === projectData?.domesticSgaExpensesCurrencyId
  )?.currencyName;

  // monthlyExpenseDataからdomesticSgaExpensesを取得
  const domesticSgaCostByMonth = convertMonthlyExpenseData(
    monthlyExpenseData,
    "domesticSgaExpenses",
    domesticSgaExpensesCurrency,
    targetCurrencyName,
    currencyData,
    yearMonthListByFiscalYear,
    decimalCurrency,
    "convertDomesticSgaExpenses",
    convertFn
  );
  const domesticSgaCostByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, domesticSgaCostByMonth);

  // ソース通貨値を抽出（costBreakdown用）
  const domesticSgaCostByMonthSource = convertMonthlyExpenseData(
    monthlyExpenseData, "domesticSgaExpenses",
    domesticSgaExpensesCurrency, domesticSgaExpensesCurrency, currencyData,
    yearMonthListByFiscalYear, hasDecimal(domesticSgaExpensesCurrency ?? ""), "convertDomesticSgaExpenses_source"
  );
  const domesticSgaCostSourceByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, domesticSgaCostByMonthSource);

  // #endregion 国内工場販管費

  // #region 本社・販社販管費

  // 通貨IDから通貨名を取得
  const salesCompanySgaCurrency = currencyData?.find(
    c => c.currencyId === projectData?.salesCompanySgaCurrencyId
  )?.currencyName;

  // 月別本社・販社販管費を計算（monthlyExpenseDataから取得）
  const salesManagementCostByMonthArray = convertMonthlyExpenseData(
    monthlyExpenseData,
    "salesCompanySga",
    salesCompanySgaCurrency,
    targetCurrencyName,
    currencyData,
    yearMonthListByFiscalYear,
    decimalCurrency,
    "convertSalesCompanySga",
    convertFn
  );

  const salesManagementCostByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, salesManagementCostByMonthArray);

  // ソース通貨値を抽出（costBreakdown用）
  const salesManagementCostByMonthSource = convertMonthlyExpenseData(
    monthlyExpenseData, "salesCompanySga",
    salesCompanySgaCurrency, salesCompanySgaCurrency, currencyData,
    yearMonthListByFiscalYear, hasDecimal(salesCompanySgaCurrency ?? ""), "convertSalesCompanySga_source"
  );
  const salesManagementCostSourceByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, salesManagementCostByMonthSource);

  // #endregion 本社・販社販管費

  // #region 開発費

  let devCostByMonth: { year: number; month: number; value: Decimal }[] = [];
  let devCostSourceByMonth: { year: number; month: number; value: Decimal }[] = [];
  // 通貨IDから通貨名を取得（costBreakdown.sourceCurrencies用にブロック外で参照）
  const devCostCurrency = currencyData?.find(
    c => c.currencyId === projectData?.devCostCurrencyId
  )?.currencyName;
  if (projectData?.devCost) {

    // JSONパース（年月単位のデータ構造）
    const devCost: MonthValue[] = JSON.parse(projectData.devCost);

    // 月別開発費を計算（年月単位のデータを変換）
    const devCostByMonthNew = convertMonthlyValueData(
      devCost,
      devCostCurrency,
      targetCurrencyName,
      currencyData,
      yearMonthListByFiscalYear,
      decimalCurrency,
      "convertDevCost",
      convertFn
    );
    devCostByMonth = convertToYearMonthObject(yearMonthListByElapsedYears, devCostByMonthNew);

    // ソース通貨値を抽出（costBreakdown用）
    const devCostByMonthSource = convertMonthlyValueData(
      devCost,
      devCostCurrency, devCostCurrency, currencyData,
      yearMonthListByFiscalYear, hasDecimal(devCostCurrency ?? ""), "convertDevCost_source"
    );
    devCostSourceByMonth = convertToYearMonthObject(yearMonthListByElapsedYears, devCostByMonthSource);
  }

  // #endregion 開発費

  // #region ロイヤリティ

  // 通貨IDから通貨名を取得
  const royaltyCurrency = currencyData?.find(
    c => c.currencyId === projectData?.royaltyCurrencyId
  )?.currencyName;

  // 月別ロイヤリティを計算（monthlyExpenseDataから取得）
  const royaltyByMonth = convertMonthlyExpenseData(
    monthlyExpenseData,
    "royalty",
    royaltyCurrency,
    targetCurrencyName,
    currencyData,
    yearMonthListByFiscalYear,
    decimalCurrency,
    "convertRoyalty",
    convertFn
  );

  const royaltyByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, royaltyByMonth);

  // ソース通貨値を抽出（costBreakdown用）
  const royaltyByMonthSource = convertMonthlyExpenseData(
    monthlyExpenseData, "royalty",
    royaltyCurrency, royaltyCurrency, currencyData,
    yearMonthListByFiscalYear, hasDecimal(royaltyCurrency ?? ""), "convertRoyalty_source"
  );
  const royaltySourceByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, royaltyByMonthSource);

  // #endregion ロイヤリティ

  // #region 販売費及び一般管理費

  // 計算方法: 生産工場販管費 + 国内工場販管費 + 本社・販社販管費 + 開発費 + ロイヤリティ
  // No.411対応（B案）: 販管費はすべて稼働連動費のため、短縮後の期間で打ち切る

  const sGAExpensesByMonth = mergeYearMonthValues(
    truncateOperationalCostArray(appliedLaborCostByYearMonth, activeMonthLength),
    truncateOperationalCostArray(domesticSgaCostByYearMonth, activeMonthLength),
    truncateOperationalCostArray(salesManagementCostByYearMonth, activeMonthLength),
    truncateOperationalCostArray(devCostByMonth, activeMonthLength),
    truncateOperationalCostArray(royaltyByYearMonth, activeMonthLength),
  );

  // #endregion 販売費及び一般管理費

  // ------------------------------
  // 年度別損益
  // | ...
  // |-その他の経費
  // |-営業利益
  // |-営業利益率
  // └-実質的営業利益率
  // ------------------------------

  // #region その他の経費

  let otherCostByMonth: { year: number; month: number; value: Decimal }[] = [];
  let otherCostSourceByMonth: { year: number; month: number; value: Decimal }[] = [];
  // 通貨IDから通貨名を取得（costBreakdown.sourceCurrencies用にブロック外で参照）
  const otherCostCurrency = currencyData?.find(
    c => c.currencyId === projectData?.otherCostCurrencyId
  )?.currencyName;
  if (projectData?.otherCost) {

    // JSONパース（年月単位のデータ構造）
    const otherCost: MonthValue[] = JSON.parse(projectData.otherCost);

    // 月別その他の経費を計算（年月単位のデータを変換）
    const otherCostByMonthNew = convertMonthlyValueData(
      otherCost,
      otherCostCurrency,
      targetCurrencyName,
      currencyData,
      yearMonthListByFiscalYear,
      decimalCurrency,
      "convertOtherCost",
      convertFn
    );

    otherCostByMonth = convertToYearMonthObject(yearMonthListByElapsedYears, otherCostByMonthNew);

    // ソース通貨値を抽出（costBreakdown用）
    const otherCostByMonthSource = convertMonthlyValueData(
      otherCost,
      otherCostCurrency, otherCostCurrency, currencyData,
      yearMonthListByFiscalYear, hasDecimal(otherCostCurrency ?? ""), "convertOtherCost_source"
    );
    otherCostSourceByMonth = convertToYearMonthObject(yearMonthListByElapsedYears, otherCostByMonthSource);
  }

  // #endregion その他の経費

  // #region 営業利益

  // 営業利益
  // 計算方法: 売上合計 - 売上原価 - 販売費及び一般管理費 - その他の経費
  // No.411対応（B案）: その他の経費も稼働連動費のため短縮後の期間で打ち切る
  const profitByMonth = subtractYearMonthValues(
    totalSalesByMonth.map(({ year, month, sales }) => ({ year, month, value: sales, })),
    salesCostByMonth,
    sGAExpensesByMonth,
    truncateOperationalCostArray(otherCostByMonth, activeMonthLength),
  );

  // 表示期間（labels.length）より後に残る減価償却費の合計を算出する。
  // チャートのX軸は短縮後の期間までしか描画できないため、profit配列には含められない。
  // 営業利益率・実質的営業利益率の分子（営業利益合計）へ反映するために別途返却する。
  const residualDepreciationTotal = profitByMonth
    .slice(activeMonthLength)
    .reduce((sum, entry) => sum.plus(entry.value), new Decimal(0));

  // #endregion 営業利益

  // 営業利益額と個別コスト内訳を返却
  // costBreakdownはソース通貨で保存（対象PJと同じsource→display直接変換パスにするため）
  // totalSales, totalMaterialCost, equipmentDepreciation, scrapCostはローカル通貨のまま
  return {
    profit: profitByMonth.slice(0, labels.length).map((entry) => entry.value),
    // 表示期間外（生産終了後）に残る減価償却費の合計（ローカル通貨）。
    // 営業利益率・実質的営業利益率の分子へ反映するために使用する（No.411対応）
    residualDepreciationTotal,
    // 設備償却を表示通貨で再計算するための入力データ（対象PJと同じ変換パスにするため）
    equipmentDepreciationInputs: {
      equipments: equipmentsWithChangedDepreciation,
      originalEquipments: targetEquipments,
      maxYears: totalFiscalYearsByPartNo,
      startDate,
      labelsLength: labels.length,
    },
    // 仕損費を表示通貨で再計算するための入力データ（対象PJと同じ変換パスにするため）
    scrapCostInputs: {
      bomDataByPartNo,
      quantitiesByPartNoMonth: quantitiesByPartNoMonth.map(q => ({
        ...q,
        quantities: q.quantities.map(d => d.toNumber()),
      })),
      yearMonthListByFiscalYear,
      yearMonthListByElapsedYears,
      laborCostPerMinuteRaw: laborCostPerMinuteRaw.toString(),
      laborCostCurrency: productionLaborCostCurrency,
      factoryExpensePerMinuteRaw: factoryExpensePerMinuteRaw.toString(),
      factoryExpenseCurrency: overseasFactoryExpensesCurrency,
    },
    costBreakdown: {
      totalSales: totalSalesByMonth.map(e => e.sales.toString()),
      totalMaterialCost: totalMaterialCostByMonth.map(e => e.value.toString()),
      outsideCost: outsideCostSourceByYearMonth.map(e => e.value.toString()),
      laborCost: laborCostSourceByYearMonth.map(e => e.value.toString()),
      domesticLaborCost: domesticLaborCostSourceByYearMonth.map(e => e.value.toString()),
      overHeadCost: overHeadCostSourceByYearMonth.map(e => e.value.toString()),
      equipmentDepreciation: equipmentDepreciationByMonth.map(e => e.value.toDecimalPlaces(decimalCurrency ? 3 : 0).toString()),
      pastEquipmentCost: pastEquipmentCostSourceByYearMonth.map(e => e.value.toString()),
      pastEquipmentCostSeparate: !!pastEquipmentCostOverride,
      factoryCost: factoryCostSourceByMonth.map(e => e.value.toString()),
      otherEquipmentCost: otherEquipmentCostSourceByMonth.map(e => e.value.toString()),
      domesticOverHeadCost: domesticOverHeadCostSourceByYearMonth.map(e => e.value.toString()),
      domesticInvestmentDepreciation: domesticInvestmentDepreciationSourceByYearMonth.map(e => e.value.toString()),
      domesticFactoryBuildingDepreciation: domesticFactoryBuildingDepreciationSourceByYearMonth.map(e => e.value.toString()),
      domesticOtherDepreciation: domesticOtherDepreciationSourceByYearMonth.map(e => e.value.toString()),
      scrapCost: scrapCostByYearMonth.map(e => e.value.toString()),
      appliedLaborCost: appliedLaborCostSourceByYearMonth.map(e => e.value.toString()),
      domesticSgaCost: domesticSgaCostSourceByYearMonth.map(e => e.value.toString()),
      salesManagementCost: salesManagementCostSourceByYearMonth.map(e => e.value.toString()),
      devCost: devCostSourceByMonth.map(e => e.value.toString()),
      royalty: royaltySourceByYearMonth.map(e => e.value.toString()),
      otherCost: otherCostSourceByMonth.map(e => e.value.toString()),
      sourceCurrencies: {
        outsideCost: outsourcingCostCurrency,
        laborCost: productionLaborCostCurrency,
        domesticLaborCost: domesticLaborCostCurrency,
        overHeadCost: overseasFactoryExpensesCurrency,
        factoryCost: factoryBuildingCostCurrency,
        pastEquipmentCost: pastEquipmentCostCurrency,
        otherEquipmentCost: otherEquipmentCostCurrency,
        domesticOverHeadCost: domesticFactoryExpensesCurrency,
        domesticInvestmentDepreciation: domesticInvestmentDepreciationCurrency,
        domesticFactoryBuildingDepreciation: domesticFactoryBuildingDepreciationCurrency,
        domesticOtherDepreciation: domesticOtherDepreciationCurrency,
        appliedLaborCost: productionSgaExpensesCurrency,
        domesticSgaCost: domesticSgaExpensesCurrency,
        salesManagementCost: salesCompanySgaCurrency,
        devCost: devCostCurrency,
        royalty: royaltyCurrency,
        otherCost: otherCostCurrency,
      },
    },
  };

}

/**
 * 回収額の計算
 * @param profitByMonth 営業利益
 * @param selectedEquipmentAmountsByMonth 設備投資額（選択状態のみ）
 * @param selectedEquipments 選択済み設備情報
 * @returns 回収額
 */
/** calcRecoverySimulationForNewProduct の戻り値型 */
export interface RecoverySimulationResult {
  /** 回収額（累積） */
  monthlyRecovery: Decimal[];
  /** 営業利益（入力値） */
  profitByMonth: Decimal[];
  /** 投資対象設備の減価償却費 */
  equipmentDepreciationByMonth: Decimal[];
  /** 工場建物・その他設備の減価償却費 */
  factoryAndOtherCostByMonth: Decimal[];
  /** 回収原資 */
  recoverySourcesByMonth: Decimal[];
  /** 設備投資額（月別集約） */
  capexByMonth: Decimal[];
}

export const calcRecoverySimulationForNewProduct = (
  profitByMonth: Decimal[],
  selectedEquipmentAmountsByMonth: {
    equipmentId: string,
    year: number,
    month: number,
    capexAmount: Decimal,
  }[][],
  selectedEquipments: EquipmentExtType[],
  projectData: ProjectWithRdsData,
  capexPartNo: PartNo[],
  decimalCurrency: boolean,
): RecoverySimulationResult => {

  // コメント: 工場建物、その他の設備は、回収原資の計算時点で不要になるが、今後の要望対応などを考慮して記載しておく

  const functionName = "calcRecoverySimulationForNewProduct";
  try {

    // 年度数
    const totalFiscalYears = getTotalFiscalYearsByPartNo(capexPartNo);

    // プロジェクト開始日（投資設備検収日）
    const startDate = projectData?.projectStartDate || "";

    // 投資対象設備の計算（償却開始月から償却年数で計算）
    const equipmentDepreciationByMonthRaw = calcEquipmentDepreciationByYearMonth(totalFiscalYears, selectedEquipments, decimalCurrency, startDate, false);
    // EOL予定日以降（profitByMonth.length以降）は0にする
    const equipmentDepreciationByMonth = equipmentDepreciationByMonthRaw.map((entry, index) =>
      index >= profitByMonth.length ? { ...entry, value: new Decimal(0) } : entry
    );

    // 工場建物の計算（TODO: 仮の固定値）
    const factoryCostByMonth: Decimal[] = Array.from({ length: profitByMonth.length }, () => new Decimal(0));

    // その他の設備
    let otherEquipmentCostByMonth: Decimal[] = [];
    if (projectData?.otherEquipmentCost) {
      // JSON ⇒ Object（月単位のデータ構造）
      const otherEquipmentCost: MonthValue[] = JSON.parse(projectData.otherEquipmentCost);
      // profitByMonthの長さに合わせてDecimal配列を生成
      otherEquipmentCostByMonth = Array.from({ length: profitByMonth.length }, (_, index) => {
        // プロジェクト開始日から年月を計算
        const startDateObj = dayjs(startDate);
        const currentDate = startDateObj.add(index, 'month');
        const year = currentDate.year();
        const month = currentDate.month() + 1;
        // 該当する月のデータを検索
        const monthData = otherEquipmentCost.find(item => item.year === year && item.month === month);
        return monthData ? new Decimal(monthData.value) : new Decimal(0);
      });
    } else {
      // データがない場合は固定値0を設定
      otherEquipmentCostByMonth = Array.from({ length: profitByMonth.length }, () => new Decimal(0));
    }

    // 減価償却費の計算（投資対象設備 + 工場建物 + その他の設備）
    // ※過去PJの投資対象設備はselectedEquipmentsに含まれるため、equipmentDepreciationByMonthに既に含まれている
    const depreciationByMonth = sumDecimalArray(equipmentDepreciationByMonth.map((entry) => entry.value), factoryCostByMonth, otherEquipmentCostByMonth);

    // 工場建物・その他設備の減価償却費（工場建物 + その他の設備）
    const factoryAndOtherEquipmentCostByYear = sumDecimalArray(factoryCostByMonth, otherEquipmentCostByMonth);

    // 回収資源の計算（営業利益 + 減価償却費 - 工場建物・その他設備の減価償却費）
    const totalProfitByMonth = sumDecimalArray(profitByMonth, depreciationByMonth);
    const recoverySourcesByMonth = subtractDecimalArray(totalProfitByMonth, factoryAndOtherEquipmentCostByYear);

    // 設備投資額の計算（検収月で計上）
    let capexEquipmentByMonth = calcAggregateCapexByMonth(selectedEquipmentAmountsByMonth).map((entry) => entry.capexAmount);

    // 設備がない場合、空配列になるため0で補完する
    if (capexEquipmentByMonth.length === 0) {
      capexEquipmentByMonth = Array.from({ length: recoverySourcesByMonth.length }, () => new Decimal(0));
    }

    // 回収額の計算（前年度未回収額 + 今年度設備投資額 - 今年度回収原資）
    const monthlyRecovery = calcRecoverySimulation(recoverySourcesByMonth, capexEquipmentByMonth, decimalCurrency, false);

    // 計算結果を返却
    return {
      monthlyRecovery,
      profitByMonth,
      equipmentDepreciationByMonth: equipmentDepreciationByMonth.map((entry) => entry.value),
      factoryAndOtherCostByMonth: factoryAndOtherEquipmentCostByYear,
      recoverySourcesByMonth,
      capexByMonth: capexEquipmentByMonth,
    };

  } catch (error) {
    console.error(`${functionName} ERROR: `, error);
    return {
      monthlyRecovery: [],
      profitByMonth: [],
      equipmentDepreciationByMonth: [],
      factoryAndOtherCostByMonth: [],
      recoverySourcesByMonth: [],
      capexByMonth: [],
    };
  }

}

/**
 * チャートのY軸スケール（yMin, yMax）を計算する。
 * データが空または全て0の場合は { yMin: 0, yMax: 0 } を返す。
 *
 * @param allData   Y軸の全データポイント（null除外済み）
 * @param isMinZero true: yMinを常に0にする（数量チャート等）
 *                  false: データの最小値からyMinを算出（金額・生産能力チャート等）
 */
export const calcYAxisRange = (allData: number[], isMinZero: boolean = true): { yMin: number; yMax: number } => {
  if (allData.length === 0 || allData.every(v => Number(v) === 0)) {
    return { yMin: 0, yMax: 0 };
  }

  const actualMax = Math.max(...allData);
  const actualMin = Math.min(...allData);
  const margin = Math.max(Math.abs(actualMax), Math.abs(actualMin)) * 0.1;
  const rawMin = actualMin < 0 ? actualMin - margin : actualMin;
  const rawMax = actualMax + margin;
  const maxAbsValue = Math.max(Math.abs(rawMin), Math.abs(rawMax));
  const magnitude = maxAbsValue > 0 ? Math.pow(10, Math.floor(Math.log10(maxAbsValue))) : 1;

  let yMin = 0;
  if (!isMinZero) {
    yMin = Math.round(Math.floor(rawMin / magnitude * 10) / 10 * magnitude);
    if (yMin > 0) yMin = 0;
  }

  let yMax = Math.round(Math.ceil(rawMax / magnitude * 10) / 10 * magnitude);
  if (yMax < 0) yMax = 0;

  return { yMin, yMax };
};

/**
 * スケール算出
 * @param data1 当初計画データ
 * @param data2 ダウンサイドデータ
 * @param isMinZero true：最小値を0とする。false：data1とdata2の最小値
 * @returns
 */
export const computeCommonScaleSalesChart = (data1: any, data2: any, isMinZero: boolean) => {
  const allData = [
    ...data1.datasets.flatMap((ds: any) => ds.data.filter((v: any) => v !== null)),
    ...data2.datasets.flatMap((ds: any) => ds.data.filter((v: any) => v !== null))
  ];
  const commonLabels = data1.labels.length >= data2.labels.length ? data1.labels : data2.labels;
  const { yMin, yMax } = calcYAxisRange(allData, isMinZero);
  return { yMin, yMax, commonLabels };
};

/**
 * 保存された設備設定をEquipmentExtType配列に復元する
 * @param savedEquipments 保存された設備設定
 * @param baseEquipments ベースとなる設備情報
 * @returns 復元されたEquipmentExtType配列
 */
export const restoreEquipmentAmounts = (savedEquipments: SavedEquipmentConfig[], baseEquipments: EquipmentExtType[]): EquipmentExtType[] => {
  return baseEquipments
    .map(equipment => {
      const savedConfig = savedEquipments.find(e => e.equipmentId === equipment.equipmentId);
      if (savedConfig) {
        // changeInQuantityが変更されていない場合は元のamountを保持（unitPriceの丸め誤差による微差を防止）
        const newAmount = savedConfig.changeInQuantity === equipment.changeInQuantity
          ? equipment.amount
          : new Decimal(equipment.unitPrice || 0).mul(savedConfig.changeInQuantity).toString();
        return {
          ...equipment,
          selected: true,
          changeInQuantity: savedConfig.changeInQuantity,
          changedAcceptanceMonth: savedConfig.changedAcceptanceMonth,
          changedDepreciationStartMonth: savedConfig.changedDepreciationStartMonth,
          amount: newAmount
        } as EquipmentExtType;
      }
      return null;
    })
    .filter((e): e is EquipmentExtType => e !== null) as EquipmentExtType[];
};

/**
 * 営業利益合計と営業利益率を計算
 *
 * No.411対応（B案）: ダウンサイドの生産期間短縮時、生産終了後も発生し続ける減価償却費は
 * チャートのX軸（短縮後の期間）に描画できないため profit データセットには含まれない。
 * チャートデータに `residualDepreciationTotal`（表示期間外の残存減価償却費合計）が
 * 付与されている場合は、営業利益合計・営業利益率の分子へ加算し、「営業利益合計」表示
 * （calcYearlyRoundedProfitTotal 経由）と同一の区分で算出する。
 *
 * @param salesChartData 売上チャートデータ
 * @returns 営業利益合計と営業利益率
 */
export const calcTotalProfitAndRate = (salesChartData: any): {
  totalProfit: Decimal,
  profitRate: string,
} => {
  // 売上合計を算出
  const totalSales = salesChartData?.datasets
    ?.filter((ds: { id: string }) => ds.id === "sales")
    ?.flatMap((ds: { data: any }) => ds.data)
    ?.reduce((sum: Decimal, value: number) => sum.plus(value != null ? new Decimal(value) : new Decimal(0)), new Decimal(0)) || new Decimal(0);

  // 営業利益合計を算出（チャート表示期間内の合計）
  const chartProfit = salesChartData?.datasets
    ?.filter((ds: { id: string }) => ds.id === "profit")
    ?.flatMap((ds: { data: any }) => ds.data)
    ?.reduce((sum: Decimal, value: number) => sum.plus(value != null ? new Decimal(value) : new Decimal(0)), new Decimal(0)) || new Decimal(0);

  // 表示期間外に残る減価償却費（マイナス値）を加算し、「営業利益合計」表示と区分を揃える
  const residual = salesChartData?.residualDepreciationTotal
    ? new Decimal(salesChartData.residualDepreciationTotal)
    : new Decimal(0);
  const totalProfit = chartProfit.plus(residual);

  // 営業利益率を算出
  const profitRate = (calcProfitRate(totalSales, totalProfit))?.toFixed(1).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  return {
    totalProfit: totalProfit,
    profitRate: profitRate,
  };
}

/**
 * 増分キャッシュフローExcel出力用: 対象PJと同じ変換パスで月別営業利益・減価償却費を算出する。
 *
 * calcProfitByMonth() はローカル通貨で営業利益を計算するが、対象PJは各コスト項目を
 * ソース通貨→表示通貨に直接変換してから営業利益を算出する。
 * この関数は costBreakdown（ソース通貨の月別値）から、対象PJと同じ変換パスで
 * 月別の営業利益・投資対象設備減価償却費・工場建物等減価償却費を表示通貨で算出する。
 *
 * @param costBreakdown calcProfitByMonth() から取得した個別コスト内訳（ソース通貨）
 * @param localCurrencyName ローカル通貨名
 * @param displayCurrencyName 表示通貨名
 * @param currencyData 通貨マスタ
 * @param equipmentDepreciationInputs 設備償却再計算用の生データ
 * @param scrapCostInputs 仕損費再計算用の生データ
 * @param convertFn 振り返りモード用の月別レート変換関数
 * @param labels 振り返りモード用のラベル
 * @param extendedLength Excel出力用の年月列延長長さ（No.411対応・8章）。
 *   ダウンサイドの生産期間短縮後の長さ（costBreakdown.totalSales.length）より大きい場合、
 *   延長部分は稼働連動費0・減価償却費系のみ計算続行した値で埋めて配列を延長する。
 *   未指定の場合は短縮後の長さのまま返す（従来通り）。
 * @returns 月別の営業利益・投資対象設備減価償却費・工場建物等減価償却費（表示通貨、Decimal[]）
 */
export const calcMonthlyProfitInDisplayCurrency = (
  costBreakdown: CostBreakdownByMonth,
  localCurrencyName: string,
  displayCurrencyName: string,
  currencyData: Currency[],
  equipmentDepreciationInputs?: EquipmentDepreciationInputs,
  scrapCostInputs?: ScrapCostInputs,
  convertFn?: ConvertFn,
  labels?: string[],
  extendedLength?: number,
): {
  profitByMonth: Decimal[];
  equipmentDepreciationByMonth: Decimal[];
  factoryAndOtherCostByMonth: Decimal[];
} => {
  const displayDecimal = hasDecimal(displayCurrencyName);
  const cb = costBreakdown;

  // string[] → Decimal[] 変換ヘルパー
  const toDecimalArray = (strArr: string[]): Decimal[] => strArr.map(v => new Decimal(v || 0));

  // === 振り返りモード: costBreakdown の通貨状態に応じた完全分離パス ===
  // calcProfitByMonth 内で convertFn により既に表示通貨に変換済みの項目はそのまま使用
  // ソース通貨で保持されている項目のみ convertFn + labels で月別レート変換
  if (convertFn && labels) {
    // ソース通貨 → 表示通貨の月別レート変換ヘルパー
    const convertMonthlyReview = (monthly: string[], sourceCurrencyName?: string): Decimal[] => {
      const decimals = toDecimalArray(monthly);
      const fromCurrency = sourceCurrencyName || localCurrencyName;
      if (fromCurrency === displayCurrencyName) return decimals;
      return decimals.map((v, i) =>
        new Decimal(convertFn(v, fromCurrency, displayCurrencyName, labels[i]?.replace("/", "-")))
          .toDecimalPlaces(displayDecimal ? 2 : 0, Decimal.ROUND_HALF_UP));
    };

    // --- 変換済み項目（calcProfitByMonth 内で既に月別レートで表示通貨に変換済み）---
    const monthlySales = toDecimalArray(cb.totalSales);
    const monthlyMaterialCost = toDecimalArray(cb.totalMaterialCost);
    const monthlyScrapCost = toDecimalArray(cb.scrapCost);

    // --- ソース通貨項目（月別レートで変換） ---
    const monthlyOutsideCost = convertMonthlyReview(cb.outsideCost, cb.sourceCurrencies?.outsideCost);
    const monthlyLaborCost = convertMonthlyReview(cb.laborCost, cb.sourceCurrencies?.laborCost);
    const monthlyDomesticLaborCost = convertMonthlyReview(cb.domesticLaborCost, cb.sourceCurrencies?.domesticLaborCost);
    const monthlyOverHeadCost = convertMonthlyReview(cb.overHeadCost, cb.sourceCurrencies?.overHeadCost);

    // 投資対象設備の減価償却費（既に表示通貨で変換済み）
    let monthlyEquipmentDepreciation = toDecimalArray(cb.equipmentDepreciation);
    // pastEquipmentCostはソース通貨で保持 → 疑似検収月（最初に非ゼロ計上される月）のレートで固定換算
    if (cb.pastEquipmentCostSeparate) {
      const pastDecimals = toDecimalArray(cb.pastEquipmentCost);
      const pseudoAcceptanceIdx = pastDecimals.findIndex(v => !v.isZero());
      const pastFromCurrency = cb.sourceCurrencies?.pastEquipmentCost || localCurrencyName;
      const monthlyPastEquipmentCost = (pseudoAcceptanceIdx >= 0 && pastFromCurrency !== displayCurrencyName)
        ? pastDecimals.map(v => new Decimal(convertFn(v, pastFromCurrency, displayCurrencyName, labels[pseudoAcceptanceIdx]?.replace("/", "-")))
          .toDecimalPlaces(displayDecimal ? 2 : 0, Decimal.ROUND_HALF_UP))
        : convertMonthlyReview(cb.pastEquipmentCost, cb.sourceCurrencies?.pastEquipmentCost);
      monthlyEquipmentDepreciation = sumDecimalArray(monthlyEquipmentDepreciation, monthlyPastEquipmentCost);
    }

    const monthlyFactoryCost = convertMonthlyReview(cb.factoryCost, cb.sourceCurrencies?.factoryCost);
    const monthlyOtherEquipmentCost = convertMonthlyReview(cb.otherEquipmentCost, cb.sourceCurrencies?.otherEquipmentCost);
    const monthlyFactoryAndOtherCost = sumDecimalArray(monthlyFactoryCost, monthlyOtherEquipmentCost);
    const monthlyDepreciation = sumDecimalArray(monthlyEquipmentDepreciation, monthlyFactoryCost, monthlyOtherEquipmentCost);
    const monthlyFactoryExpenses = sumDecimalArray(monthlyOverHeadCost, monthlyDepreciation);

    // 国内工場経費（ソース通貨 → 月別レート変換）
    const monthlyDomesticOverHeadCost = convertMonthlyReview(cb.domesticOverHeadCost, cb.sourceCurrencies?.domesticOverHeadCost);
    const monthlyDomesticInvestmentDepreciation = convertMonthlyReview(cb.domesticInvestmentDepreciation, cb.sourceCurrencies?.domesticInvestmentDepreciation);
    const monthlyDomesticFactoryBuildingDepreciation = convertMonthlyReview(cb.domesticFactoryBuildingDepreciation, cb.sourceCurrencies?.domesticFactoryBuildingDepreciation);
    const monthlyDomesticOtherDepreciation = convertMonthlyReview(cb.domesticOtherDepreciation, cb.sourceCurrencies?.domesticOtherDepreciation);
    const monthlyDomesticDepreciation = sumDecimalArray(monthlyDomesticInvestmentDepreciation, monthlyDomesticFactoryBuildingDepreciation, monthlyDomesticOtherDepreciation);
    const monthlyDomesticFactoryExpenses = sumDecimalArray(monthlyDomesticOverHeadCost, monthlyDomesticDepreciation);

    // 売上原価
    const monthlySalesCost = sumDecimalArray(
      monthlyMaterialCost, monthlyOutsideCost, monthlyLaborCost, monthlyDomesticLaborCost,
      monthlyFactoryExpenses, monthlyDomesticFactoryExpenses, monthlyScrapCost,
    );

    // 販管費（ソース通貨 → 月別レート変換）
    const monthlyAppliedLaborCost = convertMonthlyReview(cb.appliedLaborCost, cb.sourceCurrencies?.appliedLaborCost);
    const monthlyDomesticSgaCost = convertMonthlyReview(cb.domesticSgaCost, cb.sourceCurrencies?.domesticSgaCost);
    const monthlySalesManagementCost = convertMonthlyReview(cb.salesManagementCost, cb.sourceCurrencies?.salesManagementCost);
    const monthlyDevCost = convertMonthlyReview(cb.devCost, cb.sourceCurrencies?.devCost);
    const monthlyRoyalty = convertMonthlyReview(cb.royalty, cb.sourceCurrencies?.royalty);
    const monthlySGAExpenses = sumDecimalArray(
      monthlyAppliedLaborCost, monthlyDomesticSgaCost, monthlySalesManagementCost, monthlyDevCost, monthlyRoyalty,
    );

    // その他の経費（ソース通貨 → 月別レート変換）
    const monthlyOtherCost = convertMonthlyReview(cb.otherCost, cb.sourceCurrencies?.otherCost);

    // 営業利益 = 売上 - 売上原価 - 販管費 - その他
    const profitByMonth = subtractDecimalArray(monthlySales, monthlySalesCost, monthlySGAExpenses, monthlyOtherCost);

    return {
      profitByMonth,
      equipmentDepreciationByMonth: monthlyEquipmentDepreciation,
      factoryAndOtherCostByMonth: monthlyFactoryAndOtherCost,
    };
  }

  // === 通常モード: 既存パス ===
  // No.411対応（B案）: 画面の「営業利益合計」と一致させるため、コスト区分別の期間扱いを適用する。
  // 減価償却費系はフル期間、稼働連動費系は短縮後の期間（activeMonthLength）で打ち切る。

  // ソース通貨 → 表示通貨に変換するヘルパー（月別）
  const convertMonthly = (monthly: string[], sourceCurrencyName?: string): Decimal[] => {
    const decimals = toDecimalArray(monthly);
    const fromCurrency = sourceCurrencyName || localCurrencyName;
    if (fromCurrency === displayCurrencyName) return decimals;
    return decimals.map(v =>
      convertCurrency(v, fromCurrency, displayCurrencyName, currencyData)
        .toDecimalPlaces(displayDecimal ? 2 : 0, Decimal.ROUND_HALF_UP));
  };

  // 稼働連動費の打ち切り位置（＝ダウンサイド短縮後の月数）
  const activeMonthLength = equipmentDepreciationInputs?.labelsLength ?? cb.totalSales.length;

  // 稼働連動費用の変換ヘルパー（変換後に打ち切り位置以降を0にする）
  const convertMonthlyOperational = (monthly: string[], sourceCurrencyName?: string): Decimal[] =>
    truncateOperationalDecimals(convertMonthly(monthly, sourceCurrencyName), activeMonthLength);

  // --- 売上合計（ローカル通貨→表示通貨） ---
  // No.411対応（8章）: extendedLength指定時、cb.totalSalesは短縮後の長さのため、
  // 延長部分（生産終了後）は売上0として0埋めする
  const monthlySalesRaw = convertMonthly(cb.totalSales);
  const monthlySales = extendedLength && extendedLength > monthlySalesRaw.length
    ? [...monthlySalesRaw, ...Array.from({ length: extendedLength - monthlySalesRaw.length }, () => new Decimal(0))]
    : monthlySalesRaw;

  // --- 材料費（ローカル通貨→表示通貨、稼働連動費のため打ち切り） ---
  const monthlyMaterialCost = convertMonthlyOperational(cb.totalMaterialCost);

  // --- 売上原価のサブカテゴリ（ソース通貨→表示通貨で直接変換、稼働連動費のため打ち切り） ---
  const monthlyOutsideCost = convertMonthlyOperational(cb.outsideCost, cb.sourceCurrencies?.outsideCost);
  const monthlyLaborCost = convertMonthlyOperational(cb.laborCost, cb.sourceCurrencies?.laborCost);
  const monthlyDomesticLaborCost = convertMonthlyOperational(cb.domesticLaborCost, cb.sourceCurrencies?.domesticLaborCost);
  const monthlyOverHeadCost = convertMonthlyOperational(cb.overHeadCost, cb.sourceCurrencies?.overHeadCost);

  // 投資対象設備の減価償却費
  // No.411対応（B案）: 減価償却費は生産終了後も発生し続けるため、labelsLengthでのゼロ埋めは行わない
  let monthlyEquipmentDepreciation: Decimal[];
  if (equipmentDepreciationInputs) {
    // 対象PJと同じパス: 設備金額を表示通貨に変換→表示通貨で償却計算
    const displayEquipment = convertEquipmentData(
      equipmentDepreciationInputs.equipments, displayCurrencyName, currencyData
    );
    const depByMonthRaw = calcEquipmentDepreciationByYearMonth(
      equipmentDepreciationInputs.maxYears,
      displayEquipment, displayDecimal, equipmentDepreciationInputs.startDate, false
    );
    monthlyEquipmentDepreciation = depByMonthRaw.map(entry => entry.value);
  } else {
    monthlyEquipmentDepreciation = convertMonthly(cb.equipmentDepreciation);
  }

  // pastEquipmentCostSeparate=trueの場合、過去PJの投資対象設備（費用画面の値）を
  // 投資対象設備の減価償却費に合算（equipmentDepreciationに含まれていないため）
  if (cb.pastEquipmentCostSeparate) {
    const monthlyPastEquipmentCost = convertMonthly(cb.pastEquipmentCost, cb.sourceCurrencies?.pastEquipmentCost);
    monthlyEquipmentDepreciation = sumDecimalArray(monthlyEquipmentDepreciation, monthlyPastEquipmentCost);
  }

  const monthlyFactoryCost = convertMonthly(cb.factoryCost, cb.sourceCurrencies?.factoryCost);
  const monthlyOtherEquipmentCost = convertMonthly(cb.otherEquipmentCost, cb.sourceCurrencies?.otherEquipmentCost);

  // 工場建物・その他設備の減価償却費（合算）
  const monthlyFactoryAndOtherCost = sumDecimalArray(monthlyFactoryCost, monthlyOtherEquipmentCost);

  // 減価償却費合計（投資対象設備 + 工場建物 + その他の設備）
  const monthlyDepreciation = sumDecimalArray(monthlyEquipmentDepreciation, monthlyFactoryCost, monthlyOtherEquipmentCost);
  // 生産工場経費 = 償却費以外 + 減価償却費
  const monthlyFactoryExpenses = sumDecimalArray(monthlyOverHeadCost, monthlyDepreciation);

  // 国内工場経費（償却費以外は稼働連動費のため打ち切り、減価償却費はフル期間）
  const monthlyDomesticOverHeadCost = convertMonthlyOperational(cb.domesticOverHeadCost, cb.sourceCurrencies?.domesticOverHeadCost);
  const monthlyDomesticInvestmentDepreciation = convertMonthly(cb.domesticInvestmentDepreciation, cb.sourceCurrencies?.domesticInvestmentDepreciation);
  const monthlyDomesticFactoryBuildingDepreciation = convertMonthly(cb.domesticFactoryBuildingDepreciation, cb.sourceCurrencies?.domesticFactoryBuildingDepreciation);
  const monthlyDomesticOtherDepreciation = convertMonthly(cb.domesticOtherDepreciation, cb.sourceCurrencies?.domesticOtherDepreciation);
  const monthlyDomesticDepreciation = sumDecimalArray(monthlyDomesticInvestmentDepreciation, monthlyDomesticFactoryBuildingDepreciation, monthlyDomesticOtherDepreciation);
  const monthlyDomesticFactoryExpenses = sumDecimalArray(monthlyDomesticOverHeadCost, monthlyDomesticDepreciation);

  // 仕損費
  let monthlyScrapCost: Decimal[];
  if (scrapCostInputs) {
    // BOMデータの累積材料費を購入通貨→表示通貨に変換
    const bomDataDisplayConverted: Record<string, ProcessBomRowForCalculation[]> = {};
    for (const partNo of Object.keys(scrapCostInputs.bomDataByPartNo)) {
      bomDataDisplayConverted[partNo] = scrapCostInputs.bomDataByPartNo[partNo].map(row => {
        const purchaseCurrencyName = currencyData?.find(
          c => c.currencyId === row.purchaseCurrencyId
        )?.currencyName;
        if (!purchaseCurrencyName || purchaseCurrencyName === displayCurrencyName) {
          return row;
        }
        const convertedAmountMonthly: Record<string, string> = {};
        for (const [yearMonth, amount] of Object.entries(row.amountMonthly || {})) {
          if (amount && amount !== "") {
            const convertedAmount = convertCurrency(
              new Decimal(amount).toNumber(),
              purchaseCurrencyName,
              displayCurrencyName,
              currencyData
            );
            convertedAmountMonthly[yearMonth] = convertedAmount.toString();
          } else {
            convertedAmountMonthly[yearMonth] = amount;
          }
        }
        return { ...row, amountMonthly: convertedAmountMonthly };
      });
    }
    const laborCostDisplay = scrapCostInputs.laborCostCurrency && displayCurrencyName && currencyData
      ? convertCurrency(new Decimal(scrapCostInputs.laborCostPerMinuteRaw), scrapCostInputs.laborCostCurrency, displayCurrencyName, currencyData)
      : new Decimal(scrapCostInputs.laborCostPerMinuteRaw);
    const factoryExpenseDisplay = scrapCostInputs.factoryExpenseCurrency && displayCurrencyName && currencyData
      ? convertCurrency(new Decimal(scrapCostInputs.factoryExpensePerMinuteRaw), scrapCostInputs.factoryExpenseCurrency, displayCurrencyName, currencyData)
      : new Decimal(scrapCostInputs.factoryExpensePerMinuteRaw);
    const scrapCostByMonthDisplay = calculateScrapCostByMonth(
      bomDataDisplayConverted,
      scrapCostInputs.quantitiesByPartNoMonth.map(q => ({
        ...q,
        quantities: q.quantities.map(n => new Decimal(n)),
      })),
      scrapCostInputs.yearMonthListByFiscalYear,
      laborCostDisplay,
      factoryExpenseDisplay
    );
    const scrapCostByYearMonthDisplay = convertToYearMonthObject(
      scrapCostInputs.yearMonthListByElapsedYears, scrapCostByMonthDisplay
    );
    // No.411対応（B案）: 仕損費は稼働連動費のため短縮後の期間で打ち切る
    monthlyScrapCost = truncateOperationalDecimals(scrapCostByYearMonthDisplay.map(e => e.value), activeMonthLength);
  } else {
    monthlyScrapCost = convertMonthlyOperational(cb.scrapCost);
  }

  // 売上原価 = 各サブカテゴリの合算
  const monthlySalesCost = sumDecimalArray(
    monthlyMaterialCost, monthlyOutsideCost, monthlyLaborCost, monthlyDomesticLaborCost,
    monthlyFactoryExpenses, monthlyDomesticFactoryExpenses, monthlyScrapCost,
  );

  // --- 販管費のサブカテゴリ（すべて稼働連動費のため打ち切り） ---
  const monthlyAppliedLaborCost = convertMonthlyOperational(cb.appliedLaborCost, cb.sourceCurrencies?.appliedLaborCost);
  const monthlyDomesticSgaCost = convertMonthlyOperational(cb.domesticSgaCost, cb.sourceCurrencies?.domesticSgaCost);
  const monthlySalesManagementCost = convertMonthlyOperational(cb.salesManagementCost, cb.sourceCurrencies?.salesManagementCost);
  const monthlyDevCost = convertMonthlyOperational(cb.devCost, cb.sourceCurrencies?.devCost);
  const monthlyRoyalty = convertMonthlyOperational(cb.royalty, cb.sourceCurrencies?.royalty);
  const monthlySGAExpenses = sumDecimalArray(
    monthlyAppliedLaborCost, monthlyDomesticSgaCost, monthlySalesManagementCost, monthlyDevCost, monthlyRoyalty,
  );

  // --- その他の経費（稼働連動費のため打ち切り） ---
  const monthlyOtherCost = convertMonthlyOperational(cb.otherCost, cb.sourceCurrencies?.otherCost);

  // --- 営業利益 = 売上 - 売上原価 - 販管費 - その他 ---
  const profitByMonth = subtractDecimalArray(monthlySales, monthlySalesCost, monthlySGAExpenses, monthlyOtherCost);

  return {
    profitByMonth,
    equipmentDepreciationByMonth: monthlyEquipmentDepreciation,
    factoryAndOtherCostByMonth: monthlyFactoryAndOtherCost,
  };
};

/**
 * 対象PJと同じ年度丸め方式で営業利益合計を計算する。
 * 各コストカテゴリを個別に convertMonthlyToYearly() で年度集計（toFixed丸め付き）してから
 * 年度利益を算出し合計することで、対象PJの年度別損益と完全に一致する値を得る。
 *
 * 特殊処理:
 * - totalMaterialCost: 対象PJの getTotalMaterialCostByYear と同様に丸めなしで年度集計後、通貨変換
 * - その他のコスト: 月別値を通貨変換後、convertMonthlyToYearly で年度集計（toFixed丸め付き）
 *
 * @param costBreakdown calcProfitByMonth() から取得した個別コスト内訳（ローカル通貨）
 * @param localCurrencyName ローカル通貨名
 * @param displayCurrencyName 表示通貨名
 * @param currencyData 通貨マスタ
 * @returns 表示通貨での営業利益合計（Decimal）
 */
export const calcYearlyRoundedProfitTotal = (
  costBreakdown: CostBreakdownByMonth,
  localCurrencyName: string,
  displayCurrencyName: string,
  currencyData: Currency[],
  equipmentDepreciationInputs?: EquipmentDepreciationInputs,
  scrapCostInputs?: ScrapCostInputs,
): Decimal => {
  const displayDecimal = hasDecimal(displayCurrencyName);
  const cb = costBreakdown;

  // string[] → Decimal[] 変換ヘルパー
  const toDecimalArray = (strArr: string[]): Decimal[] => strArr.map(v => new Decimal(v || 0));

  // ソース通貨 → 表示通貨に変換するヘルパー
  // sourceCurrencyNameが未設定の場合はローカル通貨からの変換にフォールバック（後方互換性）
  const convertMonthly = (monthly: string[], sourceCurrencyName?: string): Decimal[] => {
    const decimals = toDecimalArray(monthly);
    const fromCurrency = sourceCurrencyName || localCurrencyName;
    if (fromCurrency === displayCurrencyName) return decimals;
    return decimals.map(v => convertCurrency(v, fromCurrency, displayCurrencyName, currencyData));
  };

  // No.411対応（B案）: 稼働連動費の打ち切り位置（＝ダウンサイド短縮後の月数）。
  // costBreakdown.totalSales は短縮後のlabelsから生成されるため、その長さが稼働期間となる。
  // equipmentDepreciationInputs.labelsLength があればそちらを優先する（同値だが明示的な値のため）。
  const activeMonthLength = equipmentDepreciationInputs?.labelsLength ?? cb.totalSales.length;

  // 年度集計（convertMonthlyToYearly: toFixed丸め付き）
  // ※減価償却費系（フル期間で計上する項目）に使用する
  const toYearly = (monthly: string[], sourceCurrencyName?: string): Decimal[] => {
    return convertMonthlyToYearly(convertMonthly(monthly, sourceCurrencyName), displayDecimal);
  };

  // 年度集計（丸めなし）→ 年度値を通貨変換（対象PJの売上・材料費と同等のパターン）
  // totalSales, totalMaterialCostはローカル通貨で保存されているため、localCurrencyNameから変換
  const toYearlyNoRoundFromDecimals = (decimals: Decimal[]): Decimal[] => {
    const yearlyLocal: Decimal[] = [];
    const firstEnd = decimals.length % 12 || 12;
    let total = new Decimal(0);
    decimals.forEach((value, idx) => {
      total = total.plus(value);
      if (idx === firstEnd - 1 || (idx > firstEnd - 1 && (idx - (firstEnd - 1)) % 12 === 0)) {
        yearlyLocal.push(total);
        total = new Decimal(0);
      }
    });
    if (localCurrencyName === displayCurrencyName) return yearlyLocal;
    return yearlyLocal.map(v => convertCurrency(v, localCurrencyName, displayCurrencyName, currencyData));
  };

  const toYearlyNoRound = (monthly: string[]): Decimal[] => toYearlyNoRoundFromDecimals(toDecimalArray(monthly));

  // 年度集計（稼働連動費用）。短縮後の期間で打ち切ってから年度集計する。
  // ※配列長は維持したまま打ち切り位置以降を0にする（convertMonthlyToYearlyの年度境界判定を壊さないため）
  const toYearlyOperational = (monthly: string[], sourceCurrencyName?: string): Decimal[] => {
    const converted = convertMonthly(monthly, sourceCurrencyName);
    return convertMonthlyToYearly(truncateOperationalDecimals(converted, activeMonthLength), displayDecimal);
  };

  // 年度集計（丸めなし・稼働連動費用）。材料費はローカル通貨で保存されているため専用処理とする。
  const toYearlyNoRoundOperational = (monthly: string[]): Decimal[] => {
    const truncated = truncateOperationalDecimals(toDecimalArray(monthly), activeMonthLength);
    return toYearlyNoRoundFromDecimals(truncated);
  };

  // --- 売上合計（対象PJと同様: 丸めなし → 通貨変換） ---
  const yearlySales = toYearlyNoRound(cb.totalSales);

  // --- 材料費（対象PJの getTotalMaterialCostByYear と同等: 丸めなし → 通貨変換） ---
  // No.411対応（B案）: 材料費は稼働連動費のため短縮後の期間で打ち切る
  const yearlyMaterialCost = toYearlyNoRoundOperational(cb.totalMaterialCost);

  // --- 売上原価のサブカテゴリ（個別に年度丸め、ソース通貨→表示通貨で直接変換） ---
  // No.411対応（B案）: 外注費・労務費・償却費以外の工場経費は稼働連動費のため短縮後の期間で打ち切る
  const yearlyOutsideCost = toYearlyOperational(cb.outsideCost, cb.sourceCurrencies?.outsideCost);
  const yearlyLaborCost = toYearlyOperational(cb.laborCost, cb.sourceCurrencies?.laborCost);
  const yearlyDomesticLaborCost = toYearlyOperational(cb.domesticLaborCost, cb.sourceCurrencies?.domesticLaborCost);
  // 生産工場経費 = 償却費以外 + 減価償却費（個別丸め後に合算）
  const yearlyOverHeadCost = toYearlyOperational(cb.overHeadCost, cb.sourceCurrencies?.overHeadCost);
  // 投資対象設備: 生データがある場合は対象PJと同じパス（設備金額を表示通貨に変換→表示通貨で償却計算）
  // No.411対応（B案）: 減価償却費は生産終了後も発生し続けるため、labelsLengthでのゼロ埋めは行わない
  let yearlyEquipmentDepreciation: Decimal[];
  if (equipmentDepreciationInputs) {
    const displayEquipment = convertEquipmentData(
      equipmentDepreciationInputs.equipments, displayCurrencyName, currencyData
    );
    const depByMonthRaw = calcEquipmentDepreciationByYearMonth(
      equipmentDepreciationInputs.maxYears,
      displayEquipment, displayDecimal, equipmentDepreciationInputs.startDate, false
    );
    yearlyEquipmentDepreciation = convertMonthlyToYearly(depByMonthRaw.map(entry => entry.value), displayDecimal);
  } else {
    // フォールバック: costBreakdownのローカル通貨値を変換（後方互換性）
    yearlyEquipmentDepreciation = toYearly(cb.equipmentDepreciation);
  }

  // pastEquipmentCostSeparate=trueの場合、過去PJの投資対象設備（費用画面の値）を
  // 投資対象設備の減価償却費に合算（equipmentDepreciationに含まれていないため）
  if (cb.pastEquipmentCostSeparate) {
    const yearlyPastEquipmentCost = toYearly(cb.pastEquipmentCost, cb.sourceCurrencies?.pastEquipmentCost);
    yearlyEquipmentDepreciation = sumDecimalArray(yearlyEquipmentDepreciation, yearlyPastEquipmentCost);
  }

  const yearlyFactoryCost = toYearly(cb.factoryCost, cb.sourceCurrencies?.factoryCost);
  const yearlyOtherEquipmentCost = toYearly(cb.otherEquipmentCost, cb.sourceCurrencies?.otherEquipmentCost);
  const yearlyDepreciation = sumDecimalArray(yearlyEquipmentDepreciation, yearlyFactoryCost, yearlyOtherEquipmentCost);
  const yearlyFactoryExpenses = sumDecimalArray(yearlyOverHeadCost, yearlyDepreciation);
  // 国内工場経費 = 償却費以外 + 減価償却費（個別丸め後に合算）
  // No.411対応（B案）: 償却費以外の国内工場経費は稼働連動費のため短縮後の期間で打ち切る
  const yearlyDomesticOverHeadCost = toYearlyOperational(cb.domesticOverHeadCost, cb.sourceCurrencies?.domesticOverHeadCost);
  const yearlyDomesticInvestmentDepreciation = toYearly(cb.domesticInvestmentDepreciation, cb.sourceCurrencies?.domesticInvestmentDepreciation);
  const yearlyDomesticFactoryBuildingDepreciation = toYearly(cb.domesticFactoryBuildingDepreciation, cb.sourceCurrencies?.domesticFactoryBuildingDepreciation);
  const yearlyDomesticOtherDepreciation = toYearly(cb.domesticOtherDepreciation, cb.sourceCurrencies?.domesticOtherDepreciation);
  const yearlyDomesticDepreciation = sumDecimalArray(yearlyDomesticInvestmentDepreciation, yearlyDomesticFactoryBuildingDepreciation, yearlyDomesticOtherDepreciation);
  const yearlyDomesticFactoryExpenses = sumDecimalArray(yearlyDomesticOverHeadCost, yearlyDomesticDepreciation);
  // 仕損費: 生データがある場合は対象PJと同じパス（各入力を表示通貨に変換→表示通貨で仕損費計算）
  let yearlyScrapCost: Decimal[];
  if (scrapCostInputs) {
    // BOMデータの累積材料費を購入通貨から表示通貨に変換
    const bomDataDisplayConverted: Record<string, ProcessBomRowForCalculation[]> = {};
    for (const partNo of Object.keys(scrapCostInputs.bomDataByPartNo)) {
      bomDataDisplayConverted[partNo] = scrapCostInputs.bomDataByPartNo[partNo].map(row => {
        const purchaseCurrencyName = currencyData?.find(
          c => c.currencyId === row.purchaseCurrencyId
        )?.currencyName;
        if (!purchaseCurrencyName || purchaseCurrencyName === displayCurrencyName) {
          return row;
        }
        const convertedAmountMonthly: Record<string, string> = {};
        for (const [yearMonth, amount] of Object.entries(row.amountMonthly || {})) {
          if (amount && amount !== "") {
            const convertedAmount = convertCurrency(
              new Decimal(amount).toNumber(),
              purchaseCurrencyName,
              displayCurrencyName,
              currencyData
            );
            convertedAmountMonthly[yearMonth] = convertedAmount.toString();
          } else {
            convertedAmountMonthly[yearMonth] = amount;
          }
        }
        return { ...row, amountMonthly: convertedAmountMonthly };
      });
    }
    // 労務費・工場経費単価を表示通貨に変換
    const laborCostDisplay = scrapCostInputs.laborCostCurrency && displayCurrencyName && currencyData
      ? convertCurrency(new Decimal(scrapCostInputs.laborCostPerMinuteRaw), scrapCostInputs.laborCostCurrency, displayCurrencyName, currencyData)
      : new Decimal(scrapCostInputs.laborCostPerMinuteRaw);
    const factoryExpenseDisplay = scrapCostInputs.factoryExpenseCurrency && displayCurrencyName && currencyData
      ? convertCurrency(new Decimal(scrapCostInputs.factoryExpensePerMinuteRaw), scrapCostInputs.factoryExpenseCurrency, displayCurrencyName, currencyData)
      : new Decimal(scrapCostInputs.factoryExpensePerMinuteRaw);
    // 表示通貨ベースで仕損費を計算
    const scrapCostByMonthDisplay = calculateScrapCostByMonth(
      bomDataDisplayConverted,
      scrapCostInputs.quantitiesByPartNoMonth.map(q => ({
        ...q,
        quantities: q.quantities.map(n => new Decimal(n)),
      })),
      scrapCostInputs.yearMonthListByFiscalYear,
      laborCostDisplay,
      factoryExpenseDisplay
    );
    const scrapCostByYearMonthDisplay = convertToYearMonthObject(
      scrapCostInputs.yearMonthListByElapsedYears, scrapCostByMonthDisplay
    );
    // No.411対応（B案）: 仕損費は稼働連動費のため、年度集計前に短縮後の期間で打ち切る
    yearlyScrapCost = convertMonthlyToYearly(
      truncateOperationalDecimals(scrapCostByYearMonthDisplay.map(e => e.value), activeMonthLength),
      displayDecimal
    );
  } else {
    // フォールバック: costBreakdownのローカル通貨値を変換（後方互換性）
    yearlyScrapCost = toYearlyOperational(cb.scrapCost);
  }

  // 売上原価 = 各サブカテゴリの合算
  const yearlySalesCost = sumDecimalArray(
    yearlyMaterialCost, yearlyOutsideCost, yearlyLaborCost, yearlyDomesticLaborCost,
    yearlyFactoryExpenses, yearlyDomesticFactoryExpenses, yearlyScrapCost,
  );

  // --- 販管費のサブカテゴリ（個別に年度丸め） ---
  // No.411対応（B案）: 販管費はすべて稼働連動費のため短縮後の期間で打ち切る
  const yearlyAppliedLaborCost = toYearlyOperational(cb.appliedLaborCost, cb.sourceCurrencies?.appliedLaborCost);
  const yearlyDomesticSgaCost = toYearlyOperational(cb.domesticSgaCost, cb.sourceCurrencies?.domesticSgaCost);
  const yearlySalesManagementCost = toYearlyOperational(cb.salesManagementCost, cb.sourceCurrencies?.salesManagementCost);
  const yearlyDevCost = toYearlyOperational(cb.devCost, cb.sourceCurrencies?.devCost);
  const yearlyRoyalty = toYearlyOperational(cb.royalty, cb.sourceCurrencies?.royalty);

  const yearlySGAExpenses = sumDecimalArray(
    yearlyAppliedLaborCost, yearlyDomesticSgaCost, yearlySalesManagementCost, yearlyDevCost, yearlyRoyalty,
  );

  // --- その他の経費 ---
  // No.411対応（B案）: その他の経費も稼働連動費のため短縮後の期間で打ち切る
  const yearlyOtherCost = toYearlyOperational(cb.otherCost, cb.sourceCurrencies?.otherCost);

  // --- 営業利益 = 売上 - 売上原価 - 販管費 - その他 ---
  const yearlyProfit = subtractDecimalArray(yearlySales, yearlySalesCost, yearlySGAExpenses, yearlyOtherCost);

  // 合計
  return yearlyProfit.reduce((sum, v) => sum.plus(v), new Decimal(0));
}

/**
 * 実質的営業利益率を計算（合計）
 * 実質的営業利益率 = 営業利益 / (売上合計 - 客先指定部品材料費) × 100
 * @param totalSales 売上合計
 * @param totalProfit 営業利益合計
 * @param totalCustomerSpecifiedPartsCost 客先指定部品材料費合計
 * @returns 実質的営業利益率（文字列、小数点1桁）
 */
export const calcRealProfitRate = (
  totalSales: Decimal,
  totalProfit: Decimal,
  totalCustomerSpecifiedPartsCost: Decimal
): string => {
  const realSales = totalSales.minus(totalCustomerSpecifiedPartsCost);
  if (realSales.isZero() || realSales.isNegative()) {
    return "0.0";
  }
  return totalProfit.dividedBy(realSales).times(100).toFixed(1).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

/**
 * 月別の実質的営業利益率を計算
 * @param salesData 月別売上データ配列
 * @param profitData 月別営業利益データ配列
 * @param customerSpecifiedPartsCostData 月別客先指定部品材料費データ配列
 * @returns 月別の実質的営業利益率配列（数値）
 */
export const calcRealProfitRateByMonth = (
  salesData: number[],
  profitData: number[],
  customerSpecifiedPartsCostData: Decimal[]
): number[] => {
  return salesData.map((sales, index) => {
    const profit = profitData[index] || 0;
    const customerCost = customerSpecifiedPartsCostData[index] || new Decimal(0);
    const realSales = new Decimal(sales).minus(customerCost);
    if (realSales.isZero() || realSales.isNegative()) {
      return 0;
    }
    return Number(new Decimal(profit).dividedBy(realSales).times(100).toFixed(1));
  });
};

/** calcRealProfitRateCommon の引数 */
export type CalcRealProfitRateCommonParams = {
  salesData: Decimal[];
  mergedData: any;
  quantitiesData: any;
  labels: string[];
  capexPartNo: PartNo[];
  equipments: EquipmentExtType[];
  projectData: ProjectWithRdsData;
  isDownside: boolean;
  localCurrencyName: string;
  currencyData: Currency[];
  /** 費用側の変換先通貨。通常はlocalCurrencyName、振り返りモードでは呼び出し側でselectedCurrency.nameを渡す */
  targetCurrencyName: string;
  downsideIndexGraph: DownsideIndex | null;
  decimalCurrency: boolean;
  processCodeData?: UniqueProcessType[];
  bomData?: BomData[];
  moldSales?: string | null;
  developmentSales?: string | null;
  equipmentSales?: string | null;
  /** 振り返りモード用の月別レート変換関数。呼び出し側でreviewDate判定済みの値を渡す */
  convertFn?: ConvertFn;
  /** 指定時、客先指定部品材料費合計をこの位置以降でsliceする（投資分回収セクション向け） */
  investmentStartPos?: number;
  /**
   * 指定時、売上合計・営業利益合計はmonthlyDataからのreduceではなくこの値を使う（投資分回収セクション向け）。
   * residualDepreciationTotalは「営業利益率」側（calcTotalProfitAndRate）が使うチャートデータ自身が持つ値
   * （投資開始以降のlabelsを基準に算出）を渡すこと。本関数内部で計算するmonthlyData.residualDepreciationTotal
   * はlabels引数（通常PJ全期間）基準のため、totalsOverride使用時に代用すると基準がずれて過大計上になる。
   */
  totalsOverride?: { totalSales: Decimal; totalProfit: Decimal; residualDepreciationTotal?: Decimal };
};

export type CalcRealProfitRateCommonResult = {
  realProfitRate: string;
  realProfitRateByMonth: number[];
};

/**
 * 実質的営業利益率を計算する共通処理。
 * calcMaterialCost → calcMonthlyProfitAndLossDetails → 集計 → calcRealProfitRate/calcRealProfitRateByMonth の
 * 5ステップをまとめたもの（SimulationProductivityImprovement.tsx内で重複していた7箇所を集約）。
 */
export const calcRealProfitRateCommon = (
  params: CalcRealProfitRateCommonParams
): CalcRealProfitRateCommonResult => {
  const {
    salesData, mergedData, quantitiesData, labels, capexPartNo,
    equipments, projectData, isDownside, localCurrencyName, currencyData,
    targetCurrencyName, downsideIndexGraph, decimalCurrency,
    processCodeData, bomData, moldSales, developmentSales, equipmentSales,
    convertFn, investmentStartPos, totalsOverride,
  } = params;

  const materialCosts = calcMaterialCost(
    mergedData, quantitiesData, capexPartNo, labels,
    projectData?.projectId!, localCurrencyName, currencyData
  );

  const monthlyData = calcMonthlyProfitAndLossDetails(
    salesData, materialCosts || [], labels, isDownside, equipments,
    projectData, capexPartNo, targetCurrencyName, currencyData,
    downsideIndexGraph, decimalCurrency, processCodeData, bomData,
    moldSales, developmentSales, equipmentSales, undefined, convertFn,
  );

  if (!monthlyData) {
    return { realProfitRate: "", realProfitRateByMonth: [] };
  }

  let customerCosts = monthlyData.customerSpecifiedPartsCost;
  if (investmentStartPos !== undefined && investmentStartPos >= 0) {
    customerCosts = customerCosts.slice(investmentStartPos);
  }
  const totalCustomerCost = customerCosts.reduce((sum, v) => sum.plus(v || new Decimal(0)), new Decimal(0));

  const totalSalesVal = totalsOverride
    ? totalsOverride.totalSales
    : monthlyData.totalSales.reduce((sum, v) => sum.plus(v || new Decimal(0)), new Decimal(0));
  // No.411対応（B案）: 表示期間外（生産終了後）に残る減価償却費を分子に加算し、
  // 「営業利益合計」表示と同一の区分（減価償却費フル期間・稼働連動費短縮後）に揃える。
  // 期間短縮がない場合 residualDepreciationTotal は0のため、従来と結果は変わらない。
  // No.411追加対応: totalsOverride指定時（投資分回収セクション向け）は、labels引数（通常PJ全期間）基準で
  // 算出したmonthlyData.residualDepreciationTotalではなく、totalsOverride.residualDepreciationTotal
  // （呼び出し側で「営業利益率」側と同じ基準＝投資開始以降のlabelsで算出した値）を使う。
  const totalProfitVal = totalsOverride
    ? totalsOverride.totalProfit.plus(totalsOverride.residualDepreciationTotal ?? new Decimal(0))
    : monthlyData.profit.reduce((sum, v) => sum.plus(v || new Decimal(0)), new Decimal(0))
      .plus(monthlyData.residualDepreciationTotal ?? new Decimal(0));

  return {
    realProfitRate: calcRealProfitRate(totalSalesVal, totalProfitVal, totalCustomerCost),
    realProfitRateByMonth: calcRealProfitRateByMonth(
      monthlyData.totalSales.map(v => v.toNumber()),
      monthlyData.profit.map(v => v.toNumber()),
      monthlyData.customerSpecifiedPartsCost
    ),
  };
};

export const changeChartData = (setFunc: React.Dispatch<React.SetStateAction<any>>, propertyName: string, value: any) => {
  setFunc((prev: any) => ({
    ...prev,
    [propertyName]: value,
  }));
}

/** アノテーション位置 */
const positions: LabelPosition[] = ["start", "center", "end"];

/**
 * 「回収○ヶ月目」ラベル表示のアノテーション生成
 * @param monthOfRecovery 回収時期ｎヶ月目（数値配列、カンマ区切り文字列のどちらも対応）
 * @param labels 投資回収チャートデータのラベル情報
 * @returns 「回収○ヶ月目」ラベル表示のアノテーション
 */
export const generateRecoveryPointAnnotationOptions = (monthOfRecovery: number[] | string | null | undefined, labels: string[]): Record<string, AnnotationOptions> => {
  const annotations = {} as Record<string, AnnotationOptions>;

  let monthOfRecoveryLocal: number[] = [];

  if (typeof monthOfRecovery === "string" || !monthOfRecovery) {
    monthOfRecoveryLocal = monthOfRecovery
      ?.split(",")
      .map((str) => Number(str.trim()))
      .filter((num) => !isNaN(num) && num > 0) || [];
  } else {
    // number[]の場合
    monthOfRecoveryLocal = monthOfRecovery;
  }

  monthOfRecoveryLocal.forEach((index, i) => {
    const currentPosition = positions[i % positions.length];

    annotations[`recoveryPoint${i}`] = {
      type: "line",
      xMin: labels[index - 1],
      xMax: labels[index - 1],
      borderColor: "orange",
      borderWidth: 1,
      display: true,
      label: {
        content: i18n.t('common.chart.recoveryPointLabel', { period: formatRecoveryMonths(index) }),
        display: true,
        position: currentPosition,
        backgroundColor: "rgba(0, 0, 0, 0.6)",
      },
    };
  });

  return annotations;
}
/**
 * 投資量産開始年月を基にアノテーションを生成して返す。
 * purposeに応じてラベル文言を切り替える。
 *
 * @param investmentStartYearMonth 投資量産開始年月
 * @param purpose 投資目的コード（PurposeType.IncreasedProduction.code or PurposeType.UpdateAndOther.code or PurposeType.Rationalization.code）
 * @param offsetIndex オフセット
 * @returns 投資量産開始アノテーション
 */
export const generateAnnotationStarLabels = (
  investmentStartYearMonth: string,
  purpose?: string | null,
  offsetIndex?: number
): Record<string, AnnotationOptions> | null => {
  if (!investmentStartYearMonth) {
    return null;
  }

  // purposeに応じてラベル文言を決定
  let labelText: string;
  if (purpose === PurposeType.NewProduct.code) {
    labelText = i18n.t('common.chart.newProductProductionStart');
  } else if (purpose === PurposeType.UpdateAndOther.code) {
    labelText = i18n.t('common.chart.updateInvestmentProductionStart');
  } else if (purpose === PurposeType.Rationalization.code) {
    labelText = i18n.t('common.chart.rationalizationProductionStart');
  } else {
    labelText = i18n.t('common.chart.increaseProductionProductionStart');
  }

  const currentPosition = positions[(0 + (offsetIndex || 0)) % positions.length];
  const annotationStarLabels: Record<string, AnnotationOptions> = {
    starLabel0: {
      type: "line",
      xMin: investmentStartYearMonth,
      xMax: investmentStartYearMonth,
      borderColor: "rgba(0, 162, 232, 0.6)",
      borderWidth: 2,
      display: true,
      label: {
        content: labelText,
        display: true,
        position: currentPosition,
        font: { size: 12 },
        backgroundColor: "rgba(0, 33, 0, 0.6)",
      },
    },
  };

  return annotationStarLabels;
}

/* ----------------------------------------------------------------------------------------------------------------------------- */
/* チャートデータ作成 */
/* ----------------------------------------------------------------------------------------------------------------------------- */

/**
 * 【数量 (当初計画 / ダウンサイド)】チャートデータ作成
 * @param originalProfitAndLossByMonth オリジナル月別(当初損益月別
 * @param downsideIndexGraph ストレス指数（グラフ用）
 * @param capexPartNo PartNo一覧（displayPartNo解決用、No.435対応）
 * @returns 【数量 (当初計画 / ダウンサイド)】チャートデータ
 */
export const calcQuantitiesChartDataSet = (
  originalProfitAndLossByMonth: OriginalProfitAndLossByMonthTable,
  downsideIndexGraph: DownsideIndex | null,
  capexPartNo: PartNo[] = []

) => {

  // パーツNo別の月別/数量
  const monthlyQuantities = originalProfitAndLossByMonth.quantityByPartNo;
  // 年月ラベル
  const labels = [...originalProfitAndLossByMonth.labels];

  // プロジェクトに紐づくパーツNo
  const parts = Object.keys(monthlyQuantities);

  // ▼ 当初計画（オリジナルデータセット）
  // パーツNo毎に作成
  const datasetsOriginal = parts.map((part, idx) => ({
    label: `Part:${resolveDisplayPartNoByKey(part, capexPartNo)} (Original)`,
    partNoKey: part,   // フィルタ処理用の実キー（3.4節）
    data: monthlyQuantities[part],
    borderColor: idx % 2 === 0 ? "red" : "orange",
    backgroundColor: idx % 2 === 0 ? "red" : "orange",
    fill: false,
    tension: 0.4,
  }));

  // #region 数量（ダウンサイド）データ作成

  // 関数宣言
  const parseYearMonth = (label: string | undefined) => {
    if (!label) {
      return { year: 0, month: 0 };
    }
    const [y, m] = label.split("/");
    return { year: parseInt(y) || 0, month: parseInt(m) || 0 };
  };

  // 変数宣言
  let labelsModified = [...labels];
  let monthlyQuantitiesByPartModified: { [p: string]: (string | null)[] } = {};

  // プロジェクト開始年度を取得
  const { year: firstYear, month: firstMonth } = parseYearMonth(labelsModified[0]);
  const projectFiscalYearStart = firstMonth >= 4 ? firstYear : firstYear - 1;

  parts.forEach((part) => {

    const originalQtyArray = monthlyQuantities[part];
    // labelsの長さに合わせてデータを処理（配列の長さが異なる場合の防御）
    const maxLength = Math.min(originalQtyArray.length, labelsModified.length);
    const modifiedData = originalQtyArray.slice(0, maxLength).map((qty: string, index: number) => {

      // 年月を取得 （例）{ 2025, 4 }
      const { year, month } = parseYearMonth(labelsModified[index]);
      // 年度を取得 （例）2025
      const fyStart = month >= 4 ? year : year - 1;
      // 年数を取得 （例）1
      const fiscalYearIndex = fyStart - projectFiscalYearStart + 1;

      let downsindeIndex = 1;
      if (downsideIndexGraph) {
        // 数量をストレス指数分減少（月度単利）- 常に適用
        downsindeIndex = (1 - getDownsideIndexToQuantity(fiscalYearIndex - 1, downsideIndexGraph));
      }
      const newQty = new Decimal(qty || 0).times(downsindeIndex).toDecimalPlaces(0, Decimal.ROUND_HALF_UP);
      return newQty.toString();

    });

    monthlyQuantitiesByPartModified[part] = modifiedData;

  });

  // #endregion

  let newLength = labelsModified.length;
  if (downsideIndexGraph) {
    // ストレス指数分短縮（常に適用）
    const downsideIndex = getDownsideIndexToPeriod(downsideIndexGraph);
    newLength = Math.floor(labelsModified.length * (1 - downsideIndex));
  }

  // データのみを短縮（ラベルは変更しない）
  parts.forEach((part) => {
    monthlyQuantitiesByPartModified[part] = monthlyQuantitiesByPartModified[part].slice(0, newLength);
  });

  // 数量（ダウンサイド）データセットを作成
  const datasetsModified = parts.map((part, idx) => ({
    label: `Part:${resolveDisplayPartNoByKey(part, capexPartNo)} (Modified)`,
    partNoKey: part,
    data: monthlyQuantitiesByPartModified[part],
    borderColor: idx % 2 === 0 ? "blue" : "green",
    backgroundColor: idx % 2 === 0 ? "blue" : "green",
    fill: false,
    tension: 0.4,
  }));

  return {
    quantitiesChartDataOriginal: {
      labels: labels,
      datasets: datasetsOriginal,
    },
    quantitiesChartDataModified: {
      labels: labelsModified,
      datasets: datasetsModified,
    }
  };
}

/**
 * 【価格と材料単価 (当初計画 / ダウンサイド)】チャートデータ作成
 * @param originalProfitAndLossByMonth オリジナル月別(当初損益月別）（「対象プロジェクトの利益試算」で登録）
 * @param capexPartNo パーツNo情報
 * @param partNoToMonthlyMaterialCost 月別材料費単価（パーツNo別、キー: YYYY-MM形式）
 * @param downsideIndexGraph ストレス指数（グラフ用）
 * @param targetCurrencyName 対象通貨名
 * @param currencyData 通貨情報
 * @param convertFn 振り返りモード用の月別レート変換関数（未指定時は通貨マスタのレートで変換）
 * @returns 【価格と材料単価 (当初計画 / ダウンサイド)】チャートデータ
 */
export const calcMergedChartDataSet = (
  originalProfitAndLossByMonth: OriginalProfitAndLossByMonthTable,
  capexPartNo: PartNo[],
  partNoToMonthlyMaterialCost: Map<string, Record<string, Decimal>> | null,
  downsideIndexGraph: DownsideIndex,
  targetCurrencyName: string,
  currencyData: Currency[],
  convertFn?: ConvertFn,
) => {
  const mergedChartDataByPartNo: { [key: string]: MergedChartData } = {};
  // capexPartNoItem自体を保持し、後段でdisplayPartNo解決に使う
  const partNoItemByKey: { [key: string]: PartNo } = {};

  capexPartNo.forEach((capexPartNoItem: PartNo) => {
    const materialCostByMonth = partNoToMonthlyMaterialCost?.get(capexPartNoItem.partNo || "") ?? {};
    const result = calcMergedChartData(capexPartNoItem, originalProfitAndLossByMonth, materialCostByMonth,
      targetCurrencyName, currencyData, downsideIndexGraph, convertFn);
    mergedChartDataByPartNo[capexPartNoItem.partNo || ""] = result;
    partNoItemByKey[capexPartNoItem.partNo || ""] = capexPartNoItem;
  });

  // ラベル（yyyy/MMをデータ毎に保持）
  let labelsOriginal: string[] = [...originalProfitAndLossByMonth.labels];
  let labelsModified: string[] = [...originalProfitAndLossByMonth.labels];

  const originalDatasets: any[] = [];
  const modifiedDatasets: any[] = [];

  // ▼ 2本の線(Price / Material)をまとめて1つのチャートへ
  Object.keys(mergedChartDataByPartNo).forEach((partNo, idx) => {
    const mergedChartData = mergedChartDataByPartNo[partNo];
    const displayLabel = resolveDisplayPartNo(partNoItemByKey[partNo]);

    originalDatasets.push({
      label: `Part:${displayLabel} Sales Price (Original)`,
      partNoKey: partNo,
      data: mergedChartData.monthlyPriceArrayOriginal,
      borderColor: idx % 2 === 0 ? "red" : "orange",
      backgroundColor: idx % 2 === 0 ? "red" : "orange",
      fill: false,
    });

    originalDatasets.push({
      label: `Part:${displayLabel} Material (Original)`,
      partNoKey: partNo,
      data: mergedChartData.monthlyMaterialArrayOriginal,
      borderColor: idx % 2 === 0 ? "brown" : "gold",
      backgroundColor: idx % 2 === 0 ? "brown" : "gold",
      fill: false,
    });

    modifiedDatasets.push({
      label: `Part:${displayLabel} Sales Price (Modified)`,
      partNoKey: partNo,
      data: mergedChartData.monthlyPriceArrayModified,
      borderColor: idx % 2 === 0 ? "blue" : "green",
      backgroundColor: idx % 2 === 0 ? "blue" : "green",
      fill: false,
    });

    modifiedDatasets.push({
      label: `Part:${displayLabel} Material (Modified)`,
      partNoKey: partNo,
      data: mergedChartData.monthlyMaterialArrayModified,
      borderColor: idx % 2 === 0 ? "orange" : "rgb(75,192,192)",
      backgroundColor: idx % 2 === 0 ? "orange" : "rgb(75,192,192)",
      fill: false,
    });
  });

  return {
    mergedOriginal: {
      labels: labelsOriginal,
      datasets: originalDatasets, tension: 0.4,
    },

    mergedModified: {
      labels: labelsModified,
      datasets: modifiedDatasets, tension: 0.4,
    }
  }
}

/**
 * 【売上（当初計画）】チャートデータ作成
 * @param originalProfitAndLossByMonth 
 * @param quantitiesChartDataOriginal 【数量 (当初計画 / ダウンサイド)】チャートデータ
 * @param mergedChartDataOriginal 【価格と材料単価 (当初計画 / ダウンサイド)】チャートデータ
 * @param capexPartNo パーツNo情報
 * @param projectData プロジェクト情報
 * @param currencyData 通貨情報
 * @param originalEquipmentAmounts 初期表示の設備情報（当初計画・ダウンサイド用）
 * @param originalImprovedWorkingDaysData 稼働時間情報
 * @param originalImprovedNonWorkingHoursData 非稼働時間情報
 * @param downsideIndexGraph ストレス指数（グラフ用）
 * @param targetCurrencyName 対象通貨名
 * @param decimalCurrency 小数有無
 * @returns 【売上（当初計画）】チャートデータ
 */
export const calcOriginalSalesChartDataSet = (
  originalProfitAndLossByMonth: OriginalProfitAndLossByMonthTable,
  quantitiesChartDataOriginal: any,
  mergedChartDataOriginal: any,
  capexPartNo: PartNo[],
  projectData: ProjectWithRdsData | null | undefined,
  originalEquipmentAmounts: EquipmentExtType[],
  originalImprovedWorkingDaysData: { fiscalYear: string, workingDaysData: WorkingDaysType }[],
  originalImprovedNonWorkingHoursData: { fiscalYear: string, nonWorkingHoursByMonth: NonWorkingHoursType[] }[],
  downsideIndexGraph: DownsideIndex,
  targetCurrencyName: string,
  currencyData: Currency[],
  decimalCurrency: boolean,
  uniqueProcessData: UniqueProcessType[] = [],
  bomData: BomData[] = [],
  pastEquipmentCostOverride?: { expenseData: MonthlyExpenseData[]; currencyName?: string },
  convertFn?: ConvertFn,
  localCurrencyName?: string,
  overrideSales?: number[],
) => {
  // 年月ラベル
  const labels = originalProfitAndLossByMonth.labels;

  // 売上（DBはローカル通貨で保存）
  // overrideSales が渡された場合（振り返り×JPY かつ 販売通貨=JPY）: その値をそのまま使用し変換をスキップ。
  // rawSales はローカル通貨（工場通貨）のため、販売通貨=表示通貨のケースで月別レート変換すると二重変換になる。
  const rawSales = overrideSales?.length
    ? overrideSales.map(v => new Decimal(v))
    : originalProfitAndLossByMonth.totalSales.map(totalSales => new Decimal(totalSales!));
  // 振り返りモード: convertFn + localCurrencyName が渡された場合、月別レートでローカル通貨→表示通貨に変換。
  // 通常モード: rawSales をそのまま使用（targetCurrencyName === localCurrencyName）。
  // overrideSales 指定時: 既に表示通貨のためスキップ。
  const sales = (overrideSales?.length || !(convertFn && localCurrencyName && localCurrencyName !== targetCurrencyName))
    ? rawSales
    : rawSales.map((v, i) => new Decimal(convertFn!(v, localCurrencyName!, targetCurrencyName, labels[i]?.replace("/", "-"))));
  // 材料費（mergedChartDataOriginal はローカル通貨で保存されているため、返り値もローカル通貨）
  const materialCostRaw = calcMaterialCost(mergedChartDataOriginal, quantitiesChartDataOriginal, capexPartNo, labels, projectData?.projectId!, targetCurrencyName, currencyData);

  // 振り返りモード: materialCostRaw（ローカル通貨）を月別レートで表示通貨に変換
  // 通常モード: targetCurrencyName === localCurrencyName のためそのまま使用
  const materialCost = (convertFn && localCurrencyName && localCurrencyName !== targetCurrencyName && materialCostRaw)
    ? materialCostRaw.map((v, i) => new Decimal(convertFn(v, localCurrencyName, targetCurrencyName, labels[i]?.replace("/", "-"))))
    : materialCostRaw;

  // EBITDA算出（当初計画は初期値を使用）
  const profitResult = calcProfitByMonth(sales, materialCost!, labels, false, originalEquipmentAmounts,
    projectData!, capexPartNo, targetCurrencyName, currencyData, downsideIndexGraph!, decimalCurrency, uniqueProcessData, bomData,
    undefined, pastEquipmentCostOverride, convertFn);
  const profitByMonth = profitResult.profit;

  // 営業利益率を算出
  const profitRateArray = calcProfitRateArray(sales, profitByMonth);

  // 売上（当初計画）データ設定
  return {
    profitByMonth: profitByMonth,
    charData: {
      costBreakdown: profitResult.costBreakdown,
      equipmentDepreciationInputs: profitResult.equipmentDepreciationInputs,
      scrapCostInputs: profitResult.scrapCostInputs,
      // 表示期間外の残存減価償却費（当初計画では期間短縮がないため通常0）。営業利益率の分子に加算される
      residualDepreciationTotal: profitResult.residualDepreciationTotal?.toString(),
      labels: originalProfitAndLossByMonth.labels,
      datasets: [
        {
          label: i18n.t('common.chart.salesTotal', { suffix: '(Original)' }),
          id: "sales",
          yAxisID: "sales",
          data: sales.map(v => Number(v ?? 0)),
          borderColor: "blue",
          backgroundColor: "rgba(0,0,255,0.1)",
          fill: true,
          tension: 0.4,
        },
        {
          label: i18n.t('common.chart.operatingProfit', { suffix: '(Original)' }),
          id: "profit",
          yAxisID: "sales",
          data: profitByMonth.map((v) => Number(v ?? 0)),
          borderColor: "green",
          backgroundColor: "rgba(0,255,0,0.1)",
          fill: true,
          tension: 0.4,
        },
        {
          label: i18n.t('common.chart.operatingProfitRate', { suffix: '(Original)' }),
          id: "profitRate",
          yAxisID: "profitRate",
          data: profitRateArray.map(v => Number(v ?? 0).toFixed(1)),
          borderColor: "gold",
          backgroundColor: "gold",
          fill: false,
          tension: 0.4,
        },
      ],
    }
  };
}

/**
 * 【投資回収（当初計画）】チャートデータ作成
 * @param profitByMonth 営業利益額(EBITDA)
 * @param capexPartNo パーツNo情報
 * @param projectData プロジェクト情報
 * @param originalEquipmentAmounts 初期表示の設備情報（当初計画・ダウンサイド用）
 * @param labels ラベル情報
 * @param decimalCurrency 小数有無
 * @returns 【投資回収（当初計画）】チャートデータ
 */
export const calcOriginalCombinedChartDataSet = (
  profitByMonth: Decimal[],
  capexPartNo: PartNo[],
  projectData: ProjectWithRdsData | null | undefined,
  originalEquipmentAmounts: EquipmentExtType[],
  labels: string[],
  decimalCurrency: boolean,
  targetCurrencyName: string,
  currencyData: Currency[],
  convertFn?: ConvertFn,
) => {
  // 最大年数
  const totalFiscalYears = getTotalFiscalYearsByPartNo(capexPartNo);

  // プロジェクト開始年月
  const startDate = projectData?.projectStartDate || "";

  // 設備投資額をターゲット通貨へ変換（設備ごとの通貨が表示通貨と異なる場合のみ変換）
  const convertedEquipmentAmounts = convertEquipmentData(originalEquipmentAmounts, targetCurrencyName, currencyData, convertFn) as EquipmentExtType[];

  // 設備投資額の取得（selected=trueの設備のみ）
  const capexAmountByMonthArray: { equipmentId: string, year: number, month: number, capexAmount: Decimal }[][] = [];
  const equipmentAmountsByMonth = convertedEquipmentAmounts
    .filter((equip) => equip.selected !== false)  // selected=falseの設備を除外
    .map((equip) => {
      const capexAmountByMonth = calcCapexAmountByMonth(equip, totalFiscalYears, startDate, false);
      capexAmountByMonthArray.push(capexAmountByMonth);
      return {
        equipment: equip,
        capexAmountByMonth: capexAmountByMonth
      };
    });

  // 選択済みの設備（当初計画用）
  const originalSelectedEquipmentAmounts = convertedEquipmentAmounts.filter(e => e.selected);

  // 選択済みの設備の設備投資額のみに絞る
  const selectedEquipmentAmountsByMonth = capexAmountByMonthArray.map(monthlyData =>
    monthlyData.filter(capex =>
      originalSelectedEquipmentAmounts.some(equipment => equipment.equipmentId === capex.equipmentId)
    )
  ).filter(monthlyData => monthlyData.length > 0);

  // #region 【回収額の計算】

  // 回収額の計算
  const recoveryResult = calcRecoverySimulationForNewProduct(profitByMonth, selectedEquipmentAmountsByMonth, originalSelectedEquipmentAmounts,
    projectData!, capexPartNo, decimalCurrency);
  const monthlyRecovery = recoveryResult.monthlyRecovery;

  // #endregion

  // Y >=0、かつ前月はY < 0 のindexを探す関数
  const nonNegativeIndexes = monthlyRecovery
    .map((val, idx) => ((val?.toNumber() >= 0) && (monthlyRecovery[idx - 1]?.toNumber() < 0) ? idx : null))
    .filter((val) => val !== null);

  let monthOfRecoveryOriginal: number[] = [];

  if (nonNegativeIndexes.length > 0) {
    monthOfRecoveryOriginal = nonNegativeIndexes.map((idx) => idx + 1);
  }

  const recoveryDataset = {
    type: "line",
    label: i18n.t('common.chart.recoveryChart'),
    data: monthlyRecovery,
    borderColor: "green",
    backgroundColor: "rgba(0,255,0,0.1)",
    fill: false,
    tension: 0.4,
  };

  // 各設備投資を Bar で表示
  const capexDatasets = equipmentAmountsByMonth.map((eqData, eqDataIdx) => {
    const data = new Array(labels.length).fill(0);
    let idx = 0;
    eqData.capexAmountByMonth.forEach((amountData) => {
      data[idx] = amountData.capexAmount ? Number(amountData.capexAmount) : 0;
      idx++
    });
    return {
      type: "bar",
      label: eqData.equipment.equipmentName,  // 設備名
      data,
      backgroundColor: [
        "rgba(255,99,132,0.5)",
        "rgba(54,162,235,0.5)",
        "rgba(255,206,86,0.5)",
        "rgba(75,192,192,0.5)",
        "rgba(153,102,255,0.5)",
        "rgba(255,159,64,0.5)",
      ][eqDataIdx % 6],
      stack: "capex",
    };
  });

  return {
    monthOfRecoveryOriginal: monthOfRecoveryOriginal,
    chartData: {
      labels,
      datasets: [recoveryDataset, ...capexDatasets],
    },
    incrementalCashFlowData: {
      profitByMonth: recoveryResult.profitByMonth,
      equipmentDepreciationByMonth: recoveryResult.equipmentDepreciationByMonth,
      factoryAndOtherCostByMonth: recoveryResult.factoryAndOtherCostByMonth,
      recoverySourcesByMonth: recoveryResult.recoverySourcesByMonth,
      capexByMonth: recoveryResult.capexByMonth,
      recoveryByMonth: recoveryResult.monthlyRecovery,
    },
  };
}

/**
 * 【売上(ダウンサイド)】チャートデータ作成
 * @param quantitiesChartDataModified 【数量 (当初計画 / ダウンサイド)】チャートデータ
 * @param mergedChartDataModified 【価格と材料単価 (当初計画 / ダウンサイド)】チャートデータ
 * @param capexPartNo パーツ情報
 * @param projectData プロジェクト情報
 * @param equipmentAmounts 初期表示の設備情報（当初計画・ダウンサイド用）
 * @param workingDaysData 稼働時間情報
 * @param nonWorkingHoursData 非稼働時間情報
 * @param tempConfigForGraphGeneration グラフ生成専用：一時的な保存済み設定データ
 * @param generatingChartDataForId グラフデータ作成中の設定ID
 * @param downsideIndexGraph ストレス指数（グラフ用）
 * @param salesCurrencyName 販売通貨名
 * @param targetCurrencyName 対象通貨名
 * @param currencyData 通貨情報
 * @param labels ラベル情報
 * @param decimalCurrency 小数有無
 * @returns 【売上(ダウンサイド)】チャートデータ
 */
export const calcDownsideSalesChartDataSet = (
  quantitiesChartDataModified: any,
  mergedChartDataModified: any,
  capexPartNo: PartNo[],
  projectData: ProjectWithRdsData | null | undefined,
  equipmentAmounts: EquipmentExtType[],
  workingDaysData: { fiscalYear: string, workingDaysData: WorkingDaysType }[],
  nonWorkingHoursData: { fiscalYear: string, nonWorkingHoursByMonth: NonWorkingHoursType[] }[],
  tempConfigForGraphGeneration: SavedSimulationConfig | null,
  generatingChartDataForId: string | null,
  downsideIndexGraph: DownsideIndex | null,
  salesCurrencyName: string,
  targetCurrencyName: string,
  currencyData: Currency[],
  labels: string[],
  decimalCurrency: boolean,
  uniqueProcessData: UniqueProcessType[] = [],
  bomData: BomData[] = [],
  originalProjectStartDate?: string,
  isDownside: boolean = true,
  pastEquipmentCostOverride?: { expenseData: MonthlyExpenseData[]; currencyName?: string },
  convertFn?: ConvertFn,
  localCurrencyName?: string,
) => {
  // 年月ラベルを取得（元の長さ）
  let labelsModified: string[] = [...labels];

  // 変数宣言
  let monthlySalesModified: Decimal[] = Array(labelsModified.length).fill(new Decimal(0));
  const tempMonthlySalesModifiedByPartNo: SalesByPartNo = {};

  // PartNo金型/開発費のカレンダーベースマッピング用
  // projectStartDateから年度開始年を算出（4月始まり）
  const projectStartDateStr = projectData?.projectStartDate || "";
  let fiscalStartCalYear = 0;
  if (projectStartDateStr) {
    const [pStartYear, pStartMonth] = projectStartDateStr.split(/[-\/]/).map(Number);
    fiscalStartCalYear = pStartMonth >= 4 ? pStartYear : pStartYear - 1;
  }

  // PartNo金型/開発の年月データをカレンダー日付ベースのMapに変換するヘルパー
  // 旧convertDecimalArrayのポジションベースではなく、年度→カレンダー月の正しいマッピングを使用
  function convertPartNoDataToCalendarMap(
    data: any,
    partNoCurrency: string,
    targetCurrency: string,
    currencies: Currency[],
    convertFnArg?: ConvertFn,
  ): Map<string, Decimal> {
    const map = new Map<string, Decimal>();
    Object.keys(data).forEach(yearKey => {
      const year = Number(yearKey);
      const months = data[yearKey].months;
      Object.keys(months).forEach(monthKey => {
        const fiscalMonth = Number(monthKey);
        const price = months[monthKey].price;
        if (!price || Number(price) === 0) return;

        // 年度内月 → カレンダー月 (月1=4月, 月2=5月, ..., 月9=12月, 月10=1月, 月11=2月, 月12=3月)
        const calMonth = ((fiscalMonth + 2) % 12) + 1;
        // カレンダー年 (4月以降=年度開始年, 1-3月=翌年)
        const calYear = fiscalStartCalYear + (year - 1) + (calMonth < 4 ? 1 : 0);
        const label = `${calYear}/${String(calMonth).padStart(2, "0")}`;
        // 月別レート用の "YYYY-MM" 形式
        const yearMonth = `${calYear}-${String(calMonth).padStart(2, "0")}`;

        const converted = convertFnArg
          ? new Decimal(convertFnArg(new Decimal(price), partNoCurrency, targetCurrency, yearMonth))
          : convertCurrency(new Decimal(price), partNoCurrency, targetCurrency, currencies) || new Decimal(0);

        const existing = map.get(label) || new Decimal(0);
        map.set(label, existing.plus(converted));
      });
    });
    return map;
  }

  // PartNo毎の売上を計算
  capexPartNo.forEach((partNoItem) => {

    // PartNo別の基準通貨を取得（currencyAndRatioから比率が最大のものを取得）
    let partNoCurrencyName = salesCurrencyName; // デフォルトは販売通貨
    if (partNoItem?.quantityAndPrice) {
      try {
        const quantityAndPriceData = JSON.parse(partNoItem.quantityAndPrice);
        if (quantityAndPriceData?.currencyAndRatio) {
          const currencyAndRatio = typeof quantityAndPriceData.currencyAndRatio === 'string'
            ? JSON.parse(quantityAndPriceData.currencyAndRatio)
            : quantityAndPriceData.currencyAndRatio;

          if (Array.isArray(currencyAndRatio) && currencyAndRatio.length > 0) {
            // 比率が最大のcurrencyIdを取得（同じ場合は最初に見つかったもの）
            const maxRatio = Math.max(...currencyAndRatio.map((c: any) => c.ratio || 0));
            const maxRatioCurrency = currencyAndRatio.find((c: any) => c.ratio === maxRatio);

            if (maxRatioCurrency?.id) {
              const currency = currencyData.find(c => c.currencyId === maxRatioCurrency.id);
              if (currency) {
                partNoCurrencyName = currency.currencyName!;
              }
            }
          }
        }
      } catch (error) {
        console.error("Failed to parse quantityAndPrice or currencyAndRatio:", error);
      }
    }

    // 金型費(PartNo別)が存在する場合は金型費を取得（カレンダーベースマッピング）
    let moldSales: Decimal[] | null = null;
    if (partNoItem?.moldSalesByPartNo) {
      const moldSalesByPartNo = JSON.parse(partNoItem.moldSalesByPartNo);
      if (Array.isArray(moldSalesByPartNo)) {
        const moldSalesMap = new Map<string, Decimal>();
        moldSalesByPartNo.forEach((item: any) => {
          if (item?.data) {
            const itemMap = convertPartNoDataToCalendarMap(item.data, partNoCurrencyName, targetCurrencyName, currencyData, convertFn);
            itemMap.forEach((value, label) => {
              const existing = moldSalesMap.get(label) || new Decimal(0);
              moldSalesMap.set(label, existing.plus(value));
            });
          }
        });
        if (moldSalesMap.size > 0) {
          moldSales = labelsModified.map(label => moldSalesMap.get(label) || new Decimal(0));
        }
      }
    }

    // 開発費(PartNo別)が存在する場合は開発費を取得（カレンダーベースマッピング）
    let developmentSales: Decimal[] | null = null;
    if (partNoItem?.developmentSalesByPartNo) {
      const developmentSalesByPartNo = JSON.parse(partNoItem.developmentSalesByPartNo);
      if (Array.isArray(developmentSalesByPartNo)) {
        const devSalesMap = new Map<string, Decimal>();
        developmentSalesByPartNo.forEach((item: any) => {
          if (item?.data) {
            const itemMap = convertPartNoDataToCalendarMap(item.data, partNoCurrencyName, targetCurrencyName, currencyData, convertFn);
            itemMap.forEach((value, label) => {
              const existing = devSalesMap.get(label) || new Decimal(0);
              devSalesMap.set(label, existing.plus(value));
            });
          }
        });
        if (devSalesMap.size > 0) {
          developmentSales = labelsModified.map(label => devSalesMap.get(label) || new Decimal(0));
        }
      }
    }

    // PartNoを取得
    const partNo = partNoItem.partNo;

    // 「価格と材料単価(ダウンサイド)」チャートデータの単価を取得 
    const priceModifiedData = getPriceData(mergedChartDataModified, partNo, "Price");
    if (!priceModifiedData) {
      console.warn(`generate chartDataModified not found priceModifiedData(Price).PartNo : ${partNo}`);
      return;
    }

    // 「数量(ダウンサイド)」チャートデータの数量を取得
    const quantitiesModifiedData = getQuantities(quantitiesChartDataModified, partNo);
    if (!quantitiesModifiedData) {
      console.warn(`generate chartDataModified not found quantitiesModifiedData.PartNo : ${partNo}`);
      return;
    }

    const tempMonthlySalesModified: Decimal[] = Array(labelsModified.length).fill(new Decimal(0));

    // 年月ラベル数分、繰り返す。
    labelsModified.forEach((_: any, index: number) => {
      const priceRaw = new Decimal(priceModifiedData[index] || 0);
      // 振り返りモード: price（ローカル通貨）を月別レートで表示通貨に変換
      // 通常モード: targetCurrencyName === localCurrencyName のためそのまま使用
      const price = (convertFn && localCurrencyName && localCurrencyName !== targetCurrencyName)
        ? new Decimal(convertFn(priceRaw, localCurrencyName, targetCurrencyName, labelsModified[index]?.replace("/", "-")))
        : priceRaw;
      const quantity = new Decimal(quantitiesModifiedData[index] || 0);
      const mold = moldSales ? moldSales[index] : 0;
      const development = developmentSales ? developmentSales[index] : 0;

      // 売上算出：複数パーツの売上を年月毎に管理
      const sales = price.times(quantity).add(mold).add(development);
      tempMonthlySalesModified[index] = sales;
      monthlySalesModified[index] = monthlySalesModified[index].plus(sales);
    });

    tempMonthlySalesModifiedByPartNo[partNo ?? ""] = tempMonthlySalesModified;
  });

  // #region PJ単位回収（金型・開発費・設備費）- getProjectSalesByMonthで正しくデータ取得
  // projectStartDateがオーバーライドされている場合、元のprojectStartDateを使用
  const startDateForPJ = originalProjectStartDate || projectData?.projectStartDate || "";

  const projectMoldSalesByMonth = getProjectSalesByMonth(
    startDateForPJ, projectData?.moldSalesByProject || null, targetCurrencyName, currencyData, convertFn,
  );
  const projectDevSalesByMonth = getProjectSalesByMonth(
    startDateForPJ, projectData?.developmentSalesByProject || null, targetCurrencyName, currencyData, convertFn,
  );
  const projectEquipmentSalesByMonth = getProjectSalesByMonth(
    startDateForPJ, projectData?.equipmentSalesByProject || null, targetCurrencyName, currencyData, convertFn,
  );

  // ラベル→(経過年, カレンダー月)マッピング用リスト生成
  // labelsModifiedの最終月までカバーするように年数を算出（スライス後capexPartNoだと不足する場合がある）
  let totalFiscalYearsForPJ = getTotalFiscalYearsByPartNo(capexPartNo);
  if (labelsModified.length > 0 && startDateForPJ) {
    const lastLabel = labelsModified[labelsModified.length - 1];
    const [lastLabelYear, lastLabelMonth] = lastLabel.split("/").map(Number);
    const [pjStartYear, pjStartMonth] = startDateForPJ.split(/[-\/]/).map(Number);
    const pjStartFiscalYear = pjStartMonth >= 4 ? pjStartYear : pjStartYear - 1;
    const lastLabelFiscalYear = lastLabelMonth >= 4 ? lastLabelYear : lastLabelYear - 1;
    const neededFiscalYears = lastLabelFiscalYear - pjStartFiscalYear + 1;
    if (neededFiscalYears > totalFiscalYearsForPJ) {
      totalFiscalYearsForPJ = neededFiscalYears;
    }
  }
  const yearMonthListElapsed = generateYearMonthList(startDateForPJ, totalFiscalYearsForPJ, true);
  const yearMonthListCalendar = generateYearMonthList(startDateForPJ, totalFiscalYearsForPJ, false);

  // "YYYY/MM" → PJ回収値のMapを構築
  const buildPJSalesMap = (
    pjSales: { year: number; month: number; sales: Decimal }[]
  ): Map<string, Decimal> => {
    const map = new Map<string, Decimal>();
    yearMonthListElapsed.forEach(({ year: elapsedYear, month: calMonth }, idx) => {
      const calEntry = yearMonthListCalendar[idx];
      if (!calEntry) return;
      const label = `${calEntry.year}/${String(calEntry.month).padStart(2, "0")}`;
      const found = pjSales.find(e => e.year === elapsedYear && e.month === calMonth);
      if (found) {
        const existing = map.get(label) || new Decimal(0);
        map.set(label, existing.plus(found.sales));
      }
    });
    return map;
  };

  const moldSalesMap = buildPJSalesMap(projectMoldSalesByMonth);
  const devSalesMap = buildPJSalesMap(projectDevSalesByMonth);
  const equipmentSalesMap = buildPJSalesMap(projectEquipmentSalesByMonth);

  let moldSales: Decimal[] = labelsModified.map(label => moldSalesMap.get(label) || new Decimal(0));
  let developmentSales: Decimal[] = labelsModified.map(label => devSalesMap.get(label) || new Decimal(0));
  let equipmentSales: Decimal[] = labelsModified.map(label => equipmentSalesMap.get(label) || new Decimal(0));
  // #endregion

  // 月別売上に金型費、開発費、設備費を加算（既に通貨変換済み）
  let monthlySalesModifiedProcessed = monthlySalesModified.map((sales: Decimal, index: number) => {
    return sales.add(moldSales[index]).add(developmentSales[index]).add(equipmentSales[index]).toDecimalPlaces(decimalCurrency ? 3 : 0);
  });

  // ストレス指数分短縮（isDownsideがtrueの場合のみ適用）
  let newLength: number = labelsModified.length;
  if (isDownside && downsideIndexGraph) {
    const downsideIndex = getDownsideIndexToPeriod(downsideIndexGraph);
    newLength = Math.floor(labelsModified.length * (1 - downsideIndex));
  }

  // データのみを短縮（ラベルは変更しない）
  monthlySalesModifiedProcessed = monthlySalesModifiedProcessed.slice(0, newLength);

  // 変数宣言
  const sales = monthlySalesModifiedProcessed;
  const lebals = labelsModified.slice(0, newLength); // 計算用に短縮したラベル

  // 材料費を算出（mergedChartDataModified はローカル通貨で保存されているため、返り値もローカル通貨）
  const materialRaw = calcMaterialCost(mergedChartDataModified, quantitiesChartDataModified, capexPartNo, labelsModified, projectData?.projectId!, targetCurrencyName, currencyData);

  // 振り返りモード: materialRaw（ローカル通貨）を月別レートで表示通貨に変換
  // 通常モード: targetCurrencyName === localCurrencyName のためそのまま使用
  const material = (convertFn && localCurrencyName && localCurrencyName !== targetCurrencyName && materialRaw)
    ? materialRaw.map((v, i) => new Decimal(convertFn(v, localCurrencyName, targetCurrencyName, labelsModified[i]?.replace("/", "-"))))
    : materialRaw;

  // 営業利益を算出（保存済みグラフ生成中は一時保存データ、それ以外は初期値を使用）
  const targetEquipmentsForSales = (generatingChartDataForId && tempConfigForGraphGeneration)
    ? restoreEquipmentAmounts(tempConfigForGraphGeneration.equipmentAmounts, equipmentAmounts)
    : equipmentAmounts;
  const profitResult = calcProfitByMonth(sales, material!, lebals, isDownside, targetEquipmentsForSales,
    projectData!, capexPartNo, targetCurrencyName, currencyData, downsideIndexGraph!, decimalCurrency, uniqueProcessData, bomData,
    originalProjectStartDate, pastEquipmentCostOverride, convertFn);
  const profit = profitResult.profit;

  // 営業利益率を算出
  const profitRateArray = calcProfitRateArray(sales, profit);

  // チャートデータオブジェクトを作成
  const chartData = {
    labels: labelsModified,
    datasets: [
      {
        label: i18n.t('common.chart.salesTotal', { suffix: '(Modified)' }),
        id: "sales",
        yAxisID: "sales",
        data: monthlySalesModifiedProcessed.map((v) => Number(v ?? 0)),
        borderColor: "red",
        backgroundColor: "rgba(255,0,0,0.1)",
        fill: true,
        tension: 0.4,
      },
      {
        label: i18n.t('common.chart.operatingProfit', { suffix: '(Modified)' }),
        id: "profit",
        yAxisID: "sales",
        data: profit.map((v) => Number(v ?? 0)),
        borderColor: "green",
        backgroundColor: "rgba(0,255,0,0.1)",
        fill: true,
        tension: 0.4,
      },
      {
        label: i18n.t('common.chart.operatingProfitRate', { suffix: '(Modified)' }),
        id: "profitRate",
        yAxisID: "profitRate",
        data: profitRateArray.map(v => Number(v ?? 0).toFixed(1)),
        borderColor: "gold",
        backgroundColor: "gold",
        fill: false,
        tension: 0.4,
      },
    ],
  };

  // チャートデータにcostBreakdown・設備償却・仕損費の入力データを付与
  (chartData as any).costBreakdown = profitResult.costBreakdown;
  (chartData as any).equipmentDepreciationInputs = profitResult.equipmentDepreciationInputs;
  (chartData as any).scrapCostInputs = profitResult.scrapCostInputs;
  // 表示期間外（生産終了後）の残存減価償却費。営業利益率の分子（営業利益合計）へ加算するために付与（No.411対応）
  (chartData as any).residualDepreciationTotal = profitResult.residualDepreciationTotal?.toString();

  return {
    // パーツNo毎の月次売上（ダウンサイド）を保持
    monthlySalesByPartNo: tempMonthlySalesModifiedByPartNo,
    chartData: chartData,
  };


}
/**
 * 【投資回収（ダウンサイド）】チャートデータ作成
 * @param quantitiesChartDataModified 【数量 (当初計画 / ダウンサイド)】チャートデータ
 * @param mergedChartDataModified 【価格と材料単価 (当初計画 / ダウンサイド)】チャートデータ
 * @param capexPartNo パーツ情報
 * @param projectData プロジェクト情報
 * @param equipmentAmounts 初期表示の設備情報（当初計画・ダウンサイド用）
 * @param workingDaysData 稼働時間情報
 * @param nonWorkingHoursData 非稼働時間情報
 * @param tempConfigForGraphGeneration グラフ生成専用：一時的な保存済み設定データ
 * @param generatingChartDataForId グラフデータ作成中の設定ID
 * @param salesChartDataForSavedConfig 売上（保存済み設定用）配列形式
 * @param salesChartDataModified 売上（ダウンサイド）
 * @param monthlySalesModifiedByPartNo 売上（ダウンサイド）パーツNo別
 * @param downsideIndexGraph ストレス指数（グラフ用）
 * @param targetCurrencyName 対象通貨名
 * @param currencyData 通貨情報
 * @param decimalCurrency 小数有無
 * @returns 
 */
export const calcDownsideCombinedChartDataSet = (
  quantitiesChartDataModified: any,
  mergedChartDataModified: any,
  capexPartNo: PartNo[],
  projectData: ProjectWithRdsData | null | undefined,
  equipmentAmounts: EquipmentExtType[],
  workingDaysData: { fiscalYear: string, workingDaysData: WorkingDaysType }[],
  nonWorkingHoursData: { fiscalYear: string, nonWorkingHoursByMonth: NonWorkingHoursType[] }[],
  tempConfigForGraphGeneration: SavedSimulationConfig | null,
  generatingChartDataForId: string | null,
  salesChartDataForSavedConfig: { configId: string; data: any }[],
  salesChartDataModified: any,
  monthlySalesModifiedByPartNo: SalesByPartNo,
  downsideIndexGraph: DownsideIndex | null,
  targetCurrencyName: string,
  currencyData: Currency[],
  decimalCurrency: boolean,
  uniqueProcessData: UniqueProcessType[] = [],
  bomData: BomData[] = [],
  originalProjectStartDate?: string,
  isDownside: boolean = true,
  pastEquipmentCostOverride?: { expenseData: MonthlyExpenseData[]; currencyName?: string },
  convertFn?: ConvertFn,
  localCurrencyName?: string,
) => {
  // 保存済み設定グラフ生成中の場合と通常の場合で使用するデータを切り替え
  const targetSalesChartData = generatingChartDataForId
    ? salesChartDataForSavedConfig.find(item => item.configId === generatingChartDataForId)?.data
    : salesChartDataModified;

  // 「売上（ダウンサイド）」「数量（ダウンサイド）」「価格と材料単価（ダウンサイド）」がそろっていない場合は終了
  if (!monthlySalesModifiedByPartNo || !quantitiesChartDataModified || !mergedChartDataModified || !capexPartNo || !targetSalesChartData) {
    return;
  }

  // 材料費（mergedChartDataModified はローカル通貨で保存されているため、返り値もローカル通貨）
  const materialCostRaw = calcMaterialCost(mergedChartDataModified, quantitiesChartDataModified, capexPartNo, targetSalesChartData.labels, projectData?.projectId!, targetCurrencyName, currencyData);

  if (!materialCostRaw) {
    // 材料費がない場合
    return null;
  }

  // 振り返りモード: materialCostRaw（ローカル通貨）を月別レートで表示通貨に変換
  // 通常モード: targetCurrencyName === localCurrencyName のためそのまま使用
  const materialCost = (convertFn && localCurrencyName && localCurrencyName !== targetCurrencyName)
    ? materialCostRaw.map((v, i) => new Decimal(convertFn(v, localCurrencyName, targetCurrencyName, targetSalesChartData.labels[i]?.replace("/", "-"))))
    : materialCostRaw;

  // 売上データ
  let sales = targetSalesChartData.datasets[0].data;
  // Decimal型に変換
  sales = sales.map((value: any) => new Decimal(value));

  // ラベル（データの長さに合わせる）
  // 保存済み設定グラフ生成中の場合はtargetSalesChartDataから、それ以外はsalesChartDataModifiedから取得
  let lebals = targetSalesChartData.labels.slice(0, sales.length);

  // 保存済みグラフ生成中の場合は一時保存データ、それ以外は初期値を使用
  const targetEquipmentAmounts = (generatingChartDataForId && tempConfigForGraphGeneration)
    ? restoreEquipmentAmounts(tempConfigForGraphGeneration.equipmentAmounts, equipmentAmounts)
    : equipmentAmounts;

  // EBITDA算出（条件分岐で選択した設備を使用）
  // pastEquipmentCostOverrideが渡された場合、過去PJ設備の減価償却費は費用画面の値を使用するため
  // calcProfitByMonthには空配列を渡す（CAPEXバー表示にはtargetEquipmentAmountsを引き続き使用）
  const equipmentsForProfit = pastEquipmentCostOverride ? [] as EquipmentExtType[] : targetEquipmentAmounts;
  const profitResult = calcProfitByMonth(sales, materialCost!, lebals, isDownside, equipmentsForProfit,
    projectData!, capexPartNo, targetCurrencyName, currencyData, downsideIndexGraph!, decimalCurrency, uniqueProcessData, bomData,
    originalProjectStartDate, pastEquipmentCostOverride, convertFn);
  const monthlyEbitdaModified = profitResult.profit;

  // 最大年数
  const maxNumberOfYear = getTotalFiscalYearsByPartNo(capexPartNo);

  // プロジェクト開始年月
  const startDate = targetSalesChartData.labels[0] + "/01";

  // 設備投資額をターゲット通貨へ変換（設備ごとの通貨が表示通貨と異なる場合のみ変換）
  const convertedTargetEquipmentAmounts = convertEquipmentData(targetEquipmentAmounts, targetCurrencyName, currencyData, convertFn) as EquipmentExtType[];

  // 設備投資額の取得（selected=trueの設備のみ）
  const capexAmountByMonthArray: { equipmentId: string, year: number, month: number, capexAmount: Decimal }[][] = [];
  const equipmentAmountsByMonth = convertedTargetEquipmentAmounts
    .filter((equip) => equip.selected !== false)  // selected=falseの設備を除外
    .map((equip) => {
      const capexAmountByMonth = calcCapexAmountByMonth(equip, maxNumberOfYear, startDate, false);
      capexAmountByMonthArray.push(capexAmountByMonth);
      return {
        equipment: equip,
        capexAmountByMonth: capexAmountByMonth
      };
    });

  // 選択済みの設備（ダウンサイド用）
  const selectedEquipmentsForDownside = convertedTargetEquipmentAmounts.filter(e => e.selected);

  // 選択済みの設備の設備投資額のみに絞る
  const selectedEquipmentAmountsByMonth = capexAmountByMonthArray.map(monthlyData =>
    monthlyData.filter(capex =>
      selectedEquipmentsForDownside.some(equipment => equipment.equipmentId === capex.equipmentId)
    )
  ).filter(monthlyData => monthlyData.length > 0);

  // #region 【回収額の計算】

  // 回収額の計算
  const recoveryResult = calcRecoverySimulationForNewProduct(monthlyEbitdaModified, selectedEquipmentAmountsByMonth, selectedEquipmentsForDownside,
    projectData!, capexPartNo, decimalCurrency);
  const monthlyRecoveryModified = recoveryResult.monthlyRecovery;

  // #endregion

  // ----------------------------
  // 新たに混合チャートデータを作成して保存
  // ----------------------------
  const labels = targetSalesChartData.labels; // Modified 用ラベル

  // 投資回収（ダウンサイド）データセット
  const recoveryDataset = {
    type: "line",
    label: i18n.t('common.chart.recoveryChartModified'),
    data: monthlyRecoveryModified,
    borderColor: "green",
    backgroundColor: "rgba(0,255,0,0.1)",
    fill: false,
    tension: 0.4,
  };

  // 設備数分、繰り返す。
  const capexDatasets = equipmentAmountsByMonth.map((eqData, eqDataIdx) => {
    const data = new Array(labels.length).fill(0);
    let idx = 0;
    eqData.capexAmountByMonth.forEach((amountData) => {
      data[idx] = amountData.capexAmount ? Number(amountData.capexAmount) : 0;
      idx++
    });
    return {
      type: "bar",
      label: eqData.equipment.equipmentName,
      data,
      backgroundColor: [
        "rgba(255,99,132,0.5)",
        "rgba(54,162,235,0.5)",
        "rgba(255,206,86,0.5)",
        "rgba(75,192,192,0.5)",
        "rgba(153,102,255,0.5)",
        "rgba(255,159,64,0.5)",
      ][eqDataIdx % 6],
      stack: "capex",
    };
  });

  // Y >=0、かつ前月はY < 0 のindexを探す関数
  const nonNegativeIndexesMod = monthlyRecoveryModified
    .map((val, idx) => ((val?.toNumber() >= 0) && (monthlyRecoveryModified[idx - 1]?.toNumber() < 0) ? idx : null))
    .filter((val) => val !== null);

  const recoveryMonths = nonNegativeIndexesMod.length > 0
    ? nonNegativeIndexesMod.map((idx) => idx + 1)
    : [];

  return {
    generatingChartDataForId: generatingChartDataForId,
    recoveryMonths: recoveryMonths,
    labels: labels,
    recoveryDataset: recoveryDataset,
    capexDatasets: capexDatasets,
    incrementalCashFlowData: {
      profitByMonth: recoveryResult.profitByMonth,
      equipmentDepreciationByMonth: recoveryResult.equipmentDepreciationByMonth,
      factoryAndOtherCostByMonth: recoveryResult.factoryAndOtherCostByMonth,
      recoverySourcesByMonth: recoveryResult.recoverySourcesByMonth,
      capexByMonth: recoveryResult.capexByMonth,
      recoveryByMonth: recoveryResult.monthlyRecovery,
      costBreakdown: profitResult.costBreakdown,
      equipmentDepreciationInputs: profitResult.equipmentDepreciationInputs,
      scrapCostInputs: profitResult.scrapCostInputs,
    },
  };

}

/**
 * 【月次詳細損益データ】計算
 * Simulation画面のExcel出力用に、月別の詳細損益データを計算して返す
 * @param sales 売上配列
 * @param materialCosts 材料費配列
 * @param labels ラベル配列（YYYY/MM形式）
 * @param isDownside ダウンサイドONかどうか
 * @param targetEquipments 設備情報
 * @param projectData プロジェクトデータ
 * @param partNoData PartNoデータ
 * @param targetCurrencyName ターゲット通貨名
 * @param currencyData 通貨データ
 * @param downsideIndexGraph ダウンサイドシナリオ指数
 * @param decimalCurrency 小数有無
 * @param uniqueProcessData 工程データ
 * @param bomData BOMデータ
 * @param moldSalesByProject PJ単位金型回収
 * @param developmentSalesByProject PJ単位開発費回収
 * @param equipmentSalesByProject PJ単位設備費回収
 * @param extendedLabels No.411対応（9章）: 出力専用の延長後ラベル配列（任意）。指定時、戻り値の各配列を
 *   このラベル長まで延長する（稼働連動費・売上系は0埋め、減価償却費系はフル期間で既に保持済みの値を継続）。
 *   数量調整等の中間計算に使う`labels`引数自体には影響しない
 * @returns 月別詳細損益データ
 */
export const calcMonthlyProfitAndLossDetails = (
  sales: Decimal[],
  materialCosts: Decimal[],
  labels: string[] = [],
  isDownside: boolean = false,
  targetEquipments: EquipmentExtType[],
  projectData: ProjectWithRdsData,
  partNoData: PartNo[],
  targetCurrencyName: string,
  currencyData: Currency[],
  downsideIndexGraph: DownsideIndex | null,
  decimalCurrency: boolean,
  uniqueProcessData: UniqueProcessType[] = [],
  bomData: BomData[] = [],
  moldSalesByProject?: string | null,
  developmentSalesByProject?: string | null,
  equipmentSalesByProject?: string | null,
  originalProjectStartDate?: string,
  convertFn?: ConvertFn,
  extendedLabels?: string[],
): MonthlyProfitAndLossData | null => {
  if (!labels || labels.length === 0 || !projectData) {
    return null;
  }

  // プロジェクト開始日
  const startDate = projectData?.projectStartDate || "";
  // PJ回収用の開始日（overriddenProjectDataの場合は元の開始日を使用）
  const startDateForPJ = originalProjectStartDate || startDate;

  // 売上合計をフォーマット変換
  let currentYear = 1;
  const totalSalesByMonth = labels.map((label, index) => {
    const [, month] = label.split("/").map(Number);
    if (month === 4 && index !== 0) {
      currentYear += 1;
    }
    return {
      year: currentYear,
      month,
      sales: sales[index] || new Decimal(0)
    };
  });

  // changedDepreciationStartMonthが設定されている場合はdepreciationStartMonthを上書き
  const equipmentsWithChangedDepreciation = targetEquipments.map(equip => ({
    ...equip,
    depreciationStartMonth: equip.changedDepreciationStartMonth ?? equip.depreciationStartMonth
  }));

  // 設備投資額を工場通貨へ変換
  const convertedCapexEquipment = convertEquipmentData(equipmentsWithChangedDepreciation, targetCurrencyName, currencyData, convertFn);

  // 総会計年度数の取得（PartNoのみ参照）
  const totalFiscalYearsByPartNo = getTotalFiscalYearsByPartNo(partNoData);

  // 年月リスト生成（年度）
  const yearMonthListByFiscalYear = generateYearMonthList(startDate, totalFiscalYearsByPartNo);

  // 年月リスト生成（年数）
  const yearMonthListByElapsedYears = generateYearMonthList(startDate, totalFiscalYearsByPartNo, true);

  // 振り返りモード: materialCosts（ローカル通貨）を月別レートで表示通貨に変換
  const localCurrencyIdForMaterial = getLocalCurrencyId(projectData?.companyPlantSnapshot);
  const localCurrencyNameForMaterial = currencyData?.find(c => c.currencyId === localCurrencyIdForMaterial)?.currencyName || "";
  const convertedMaterialCosts = (convertFn && localCurrencyNameForMaterial && localCurrencyNameForMaterial !== targetCurrencyName)
    ? materialCosts.map((v, i) => convertFn(v, localCurrencyNameForMaterial, targetCurrencyName, labels[i]?.replace("/", "-")))
    : materialCosts;

  // 材料費合計をフォーマット変換
  const totalMaterialCostByMonth = convertToYearMonthObject(yearMonthListByElapsedYears, convertedMaterialCosts);

  // 月別の全PartNoのC/T合計（秒）を取得
  const totalCycleTimeByMonth: Decimal[] = calcTotalCycleTimeByMonth(uniqueProcessData, yearMonthListByFiscalYear);

  // 全PartNoの月別販売数量合計を取得（元データ）
  const quantitiesByPartNoMonthOriginal = getQuantitiesByPartNoMonth(partNoData, startDate);
  const totalSalesQuantityByMonthOriginal: Decimal[] = calcTotalQuantityByMonth(
    quantitiesByPartNoMonthOriginal,
    yearMonthListByElapsedYears.length
  );

  // ダウンサイドの場合、数量経時変化のストレス指数を適用
  // プロジェクト開始年度を取得
  const parseYearMonthForDownside = (label: string) => {
    const [y, m] = label.split("/");
    return { year: parseInt(y), month: parseInt(m) };
  };
  const { year: firstYear, month: firstMonth } = labels.length > 0 ? parseYearMonthForDownside(labels[0]) : { year: 0, month: 0 };
  const projectFiscalYearStart = firstMonth >= 4 ? firstYear : firstYear - 1;

  // 数量にストレス指数を適用（生産工場労務費、償却費以外の工場経費で使用）
  const totalSalesQuantityByMonth: Decimal[] = totalSalesQuantityByMonthOriginal.map((qty, index) => {
    if (!isDownside || !downsideIndexGraph || index >= labels.length) {
      return qty;
    }
    const { year, month } = parseYearMonthForDownside(labels[index]);
    const fyStart = month >= 4 ? year : year - 1;
    const fiscalYearIndex = fyStart - projectFiscalYearStart + 1;
    const downsideIdx = 1 - getDownsideIndexToQuantity(fiscalYearIndex - 1, downsideIndexGraph);
    return qty.times(downsideIdx).toDecimalPlaces(0, Decimal.ROUND_HALF_UP);
  });

  // quantitiesByPartNoMonthにもストレス指数を適用（仕損費計算用）
  const quantitiesByPartNoMonth: { partNo: string; year: number; quantities: Decimal[] }[] =
    quantitiesByPartNoMonthOriginal.map((item) => {
      const adjustedQuantities = item.quantities.map((qty: Decimal, index: number) => {
        if (!isDownside || !downsideIndexGraph || index >= labels.length) {
          return qty;
        }
        const { year, month } = parseYearMonthForDownside(labels[index]);
        const fyStart = month >= 4 ? year : year - 1;
        const fiscalYearIndex = fyStart - projectFiscalYearStart + 1;
        const downsideIdx = 1 - getDownsideIndexToQuantity(fiscalYearIndex - 1, downsideIndexGraph);
        return qty.times(downsideIdx).toDecimalPlaces(0, Decimal.ROUND_HALF_UP);
      });
      return { ...item, quantities: adjustedQuantities };
    });

  // 月別費用データをパース
  const monthlyExpenseData: MonthlyExpenseData[] = projectData.monthlyExpenseData
    ? JSON.parse(projectData.monthlyExpenseData)
    : [];

  // #region 外注加工費
  const outsourcingCostCurrency = currencyData?.find(
    c => c.currencyId === projectData.outsourcingProcessingCostCurrencyId
  )?.currencyName;

  const outsideCostByMonthArray = convertMonthlyExpenseData(
    monthlyExpenseData,
    "outsourcingProcessingCost",
    outsourcingCostCurrency,
    targetCurrencyName,
    currencyData,
    yearMonthListByFiscalYear,
    decimalCurrency,
    "convertOutsourcingCost",
    convertFn
  );
  const outsideCostByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, outsideCostByMonthArray);
  // #endregion

  // #region 生産工場労務費
  // 通貨IDから通貨名を取得
  const productionLaborCostCurrency = currencyData?.find(
    c => c.currencyId === projectData?.productionLaborCostCurrencyId
  )?.currencyName;

  // 生産工場労務費単価を取得（仕損費の計算に使用）
  // targetCurrencyNameに変換（仕損費計算で他の項目と通貨を合わせるため）
  const laborCostPerMinuteRaw = new Decimal(projectData?.productionFactoryLaborUnitPrice ?? 0);
  const laborCostPerMinute = productionLaborCostCurrency && targetCurrencyName && currencyData
    ? convertFn
      ? convertFn(laborCostPerMinuteRaw, productionLaborCostCurrency, targetCurrencyName)
      : convertCurrency(laborCostPerMinuteRaw, productionLaborCostCurrency, targetCurrencyName, currencyData)
    : laborCostPerMinuteRaw;

  // 月別生産工場労務費を計算（monthlyExpenseDataから取得）
  const laborCostByMonth = convertMonthlyExpenseData(
    monthlyExpenseData,
    "productionLaborCost",
    productionLaborCostCurrency,
    targetCurrencyName,
    currencyData,
    yearMonthListByFiscalYear,
    decimalCurrency,
    "calcProductionLaborCost",
    convertFn
  );
  // 生産工場労務費のベースアップ率はここでは適用しない（費用画面の自動算出時のみ適用）
  applyDownsideLaborCostAdjustment(laborCostByMonth, labels, isDownside, downsideIndexGraph);
  const laborCostByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, laborCostByMonth);
  // #endregion

  // #region 国内工場労務費
  const domesticLaborCostCurrency = currencyData?.find(
    c => c.currencyId === projectData.domesticLaborCostCurrencyId
  )?.currencyName;

  const domesticLaborCostByMonth = convertMonthlyExpenseData(
    monthlyExpenseData,
    "domesticLaborCost",
    domesticLaborCostCurrency,
    targetCurrencyName,
    currencyData,
    yearMonthListByFiscalYear,
    decimalCurrency,
    "calcDomesticLaborCost",
    convertFn
  );
  // 国内工場労務費のベースアップ率はここでは適用しない（費用画面の自動算出時のみ適用）
  applyDownsideLaborCostAdjustment(domesticLaborCostByMonth, labels, isDownside, downsideIndexGraph);
  const domesticLaborCostByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, domesticLaborCostByMonth);
  // #endregion

  // #region 投資対象設備
  // No.411対応（B案）: 減価償却費は既取得資産の費用配分のため、ダウンサイドの生産期間短縮後も
  // 発生し続ける。そのため labels.length でのゼロ埋めは行わず、フル期間のまま保持する。
  const equipmentDepreciationByMonth = calcEquipmentDepreciationByYearMonth(totalFiscalYearsByPartNo, convertedCapexEquipment, decimalCurrency, startDate, false);
  // #endregion

  // #region 工場建物
  const factoryBuildingCostCurrency = currencyData?.find(
    c => c.currencyId === projectData.factoryBuildingDepreciationCurrencyId
  )?.currencyName;

  const factoryBuildingCostByMonthArray = convertMonthlyExpenseData(
    monthlyExpenseData,
    "factoryBuildingDepreciation",
    factoryBuildingCostCurrency,
    targetCurrencyName,
    currencyData,
    yearMonthListByFiscalYear,
    decimalCurrency,
    "convertFactoryBuildingCost",
    convertFn
  );
  const factoryCostByMonth = convertToYearMonthObject(yearMonthListByElapsedYears, factoryBuildingCostByMonthArray);
  // #endregion

  // #region その他の設備
  let otherEquipmentCostByMonth: { year: number; month: number; value: Decimal }[] = [];
  if (projectData?.otherEquipmentCost) {
    const otherEquipmentCost: MonthValue[] = JSON.parse(projectData.otherEquipmentCost);
    const otherEquipmentCostCurrency = currencyData?.find(
      c => c.currencyId === projectData.otherEquipmentCostCurrencyId
    )?.currencyName;

    const otherEquipmentCostByMonthNew = convertMonthlyValueData(
      otherEquipmentCost,
      otherEquipmentCostCurrency,
      targetCurrencyName,
      currencyData,
      yearMonthListByFiscalYear,
      decimalCurrency,
      "convertOtherEquipmentCost",
      convertFn
    );
    otherEquipmentCostByMonth = convertToYearMonthObject(yearMonthListByElapsedYears, otherEquipmentCostByMonthNew);
  }
  // #endregion

  // #region 過去PJの投資対象設備
  const pastEquipmentCostCurrency = currencyData?.find(
    c => c.currencyId === projectData?.pastEquipmentCostCurrencyId
  )?.currencyName;

  // 振り返りモード: 過去PJ設備の疑似検収月 = 最初に非ゼロ計上される月。全月同じレートで換算する
  const pastEquipPseudoAcceptanceYm = monthlyExpenseData
    ?.filter(e => (e.pastEquipmentCost ?? 0) !== 0)
    .sort((a, b) => a.year !== b.year ? a.year - b.year : a.month - b.month)[0];
  const pastEquipConvertFn: ConvertFn | undefined = (convertFn && pastEquipPseudoAcceptanceYm)
    ? (amount, from, to) => convertFn(amount, from, to, `${pastEquipPseudoAcceptanceYm.year}-${String(pastEquipPseudoAcceptanceYm.month).padStart(2, "0")}`)
    : convertFn;

  const pastEquipmentCostByMonthArray = convertMonthlyExpenseData(
    monthlyExpenseData,
    "pastEquipmentCost",
    pastEquipmentCostCurrency,
    targetCurrencyName,
    currencyData,
    yearMonthListByFiscalYear,
    decimalCurrency,
    "convertPastEquipmentCost",
    pastEquipConvertFn
  );
  const pastEquipmentCostByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, pastEquipmentCostByMonthArray);
  // #endregion

  // #region 減価償却費
  // ※過去PJの投資対象設備は設備マスタ(mergeEquipmentAmounts)経由でequipmentDepreciationByMonthに含まれるため別途加算しない
  // pastEquipmentCostByYearMonthは返却値に含める（Excel出力の表示用）
  const depreciationByMonth = mergeYearMonthValues(
    equipmentDepreciationByMonth,
    factoryCostByMonth,
    otherEquipmentCostByMonth
  );
  // #endregion

  // #region 償却費以外の工場経費
  // 通貨IDから通貨名を取得
  const overseasFactoryExpensesCurrency = currencyData?.find(
    c => c.currencyId === projectData?.overseasFactoryExpensesCurrencyId
  )?.currencyName;

  // 生産工場経費単価を取得（仕損費の計算に使用）
  // targetCurrencyNameに変換（仕損費計算で他の項目と通貨を合わせるため）
  const factoryExpensePerMinuteRaw = new Decimal(projectData?.productionFactoryExpenseUnitPrice ?? 0);
  const factoryExpensePerMinute = overseasFactoryExpensesCurrency && targetCurrencyName && currencyData
    ? convertFn
      ? convertFn(factoryExpensePerMinuteRaw, overseasFactoryExpensesCurrency, targetCurrencyName)
      : convertCurrency(factoryExpensePerMinuteRaw, overseasFactoryExpensesCurrency, targetCurrencyName, currencyData)
    : factoryExpensePerMinuteRaw;

  // 月別償却費以外の工場経費を計算（monthlyExpenseDataから取得）
  const overHeadCostByMonthArray = convertMonthlyExpenseData(
    monthlyExpenseData,
    "overseasFactoryExpenses",
    overseasFactoryExpensesCurrency,
    targetCurrencyName,
    currencyData,
    yearMonthListByFiscalYear,
    decimalCurrency,
    "calcFactoryOverheadCost",
    convertFn
  );
  const overHeadCostByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, overHeadCostByMonthArray);
  // #endregion

  // #region 生産工場経費
  const factoryExpensesByMonth = mergeYearMonthValues(
    overHeadCostByYearMonth,
    depreciationByMonth,
  );
  // #endregion

  // #region 国内工場経費
  const domesticFactoryExpensesCurrency = currencyData?.find(
    c => c.currencyId === projectData?.domesticFactoryExpensesCurrencyId
  )?.currencyName;

  const domesticFactoryExpensesByMonth = convertMonthlyExpenseData(
    monthlyExpenseData,
    "domesticFactoryExpenses",
    domesticFactoryExpensesCurrency,
    targetCurrencyName,
    currencyData,
    yearMonthListByFiscalYear,
    decimalCurrency,
    "convertDomesticFactoryExpenses",
    convertFn
  );
  // 国内工場経費-償却費以外の工場経費
  const domesticOverHeadCostByMonth = domesticFactoryExpensesByMonth;
  const domesticOverHeadCostByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, domesticOverHeadCostByMonth);

  // 国内工場経費-投資対象設備
  const domesticInvestmentDepreciationCurrency = currencyData?.find(
    c => c.currencyId === projectData?.domesticInvestmentDepreciationCurrencyId
  )?.currencyName;
  const domesticInvestmentDepreciationByMonth = convertMonthlyExpenseData(
    monthlyExpenseData,
    "domesticInvestmentDepreciation",
    domesticInvestmentDepreciationCurrency,
    targetCurrencyName,
    currencyData,
    yearMonthListByFiscalYear,
    decimalCurrency,
    "convertDomesticInvestmentDepreciation",
    convertFn
  );
  const domesticInvestmentDepreciationByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, domesticInvestmentDepreciationByMonth);

  // 国内工場経費-工場建物
  const domesticFactoryBuildingDepreciationCurrency = currencyData?.find(
    c => c.currencyId === projectData?.domesticFactoryBuildingDepreciationCurrencyId
  )?.currencyName;
  const domesticFactoryBuildingDepreciationByMonth = convertMonthlyExpenseData(
    monthlyExpenseData,
    "domesticFactoryBuildingDepreciation",
    domesticFactoryBuildingDepreciationCurrency,
    targetCurrencyName,
    currencyData,
    yearMonthListByFiscalYear,
    decimalCurrency,
    "convertDomesticFactoryBuildingDepreciation",
    convertFn
  );
  const domesticFactoryBuildingDepreciationByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, domesticFactoryBuildingDepreciationByMonth);

  // 国内工場経費-その他の設備
  const domesticOtherDepreciationCurrency = currencyData?.find(
    c => c.currencyId === projectData?.domesticOtherDepreciationCurrencyId
  )?.currencyName;
  const domesticOtherDepreciationByMonth = convertMonthlyExpenseData(
    monthlyExpenseData,
    "domesticOtherDepreciation",
    domesticOtherDepreciationCurrency,
    targetCurrencyName,
    currencyData,
    yearMonthListByFiscalYear,
    decimalCurrency,
    "convertDomesticOtherDepreciation",
    convertFn
  );
  const domesticOtherDepreciationByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, domesticOtherDepreciationByMonth);

  // 国内工場経費-減価償却費（投資対象設備 + 工場建物 + その他の設備）
  const domesticDepreciationByYearMonth = mergeYearMonthValues(
    domesticInvestmentDepreciationByYearMonth,
    domesticFactoryBuildingDepreciationByYearMonth,
    domesticOtherDepreciationByYearMonth
  );

  // 国内工場経費（合計）= 償却費以外 + 減価償却費
  const domesticFactoryExpensesByYearMonth = mergeYearMonthValues(
    domesticOverHeadCostByYearMonth,
    domesticDepreciationByYearMonth
  );
  // #endregion

  // #region 仕損費
  const bomDataByPartNo: Record<string, ProcessBomRowForCalculation[]> = {};
  const parseMonthlyData = (jsonStr: string | null | undefined): Record<string, string> => {
    if (!jsonStr) return {};
    try {
      return JSON.parse(jsonStr);
    } catch {
      return {};
    }
  };

  for (const bomItem of bomData) {
    const partNo = bomItem.partNo ?? "";
    if (!bomDataByPartNo[partNo]) {
      bomDataByPartNo[partNo] = [];
    }
    bomDataByPartNo[partNo].push({
      seq: bomItem.seq ?? "",
      level: bomItem.level ?? "",
      isCustomerSpecifiedParts: bomItem.isCustomerSpecifiedParts,
      purchaseCurrencyId: bomItem.purchaseCurrencyId,
      amountMonthly: parseMonthlyData(bomItem.amountMonthly),
      cycleTimeMonthly: parseMonthlyData(bomItem.cycleTimeMonthly),
      yieldMonthly: parseMonthlyData(bomItem.yieldMonthly),
    });
  }

  // BOMデータの累積材料費（amountMonthly）を購入通貨からターゲット通貨に変換
  const bomDataConverted: Record<string, ProcessBomRowForCalculation[]> = {};
  for (const partNo of Object.keys(bomDataByPartNo)) {
    bomDataConverted[partNo] = bomDataByPartNo[partNo].map(row => {
      // 購入通貨を取得
      const purchaseCurrencyName = currencyData?.find(
        c => c.currencyId === row.purchaseCurrencyId
      )?.currencyName;
      // 購入通貨とターゲット通貨が同じ、または購入通貨が未設定の場合は変換不要
      if (!purchaseCurrencyName || purchaseCurrencyName === targetCurrencyName) {
        return row;
      }
      // amountMonthlyの値を購入通貨からターゲット通貨に変換
      const convertedAmountMonthly: Record<string, string> = {};
      for (const [yearMonth, amount] of Object.entries(row.amountMonthly || {})) {
        if (amount && amount !== "") {
          const convertedAmount = convertFn
            ? convertFn(new Decimal(amount), purchaseCurrencyName, targetCurrencyName, yearMonth)
            : convertCurrency(
              new Decimal(amount).toNumber(),
              purchaseCurrencyName,
              targetCurrencyName,
              currencyData
            );
          convertedAmountMonthly[yearMonth] = convertedAmount.toString();
        } else {
          convertedAmountMonthly[yearMonth] = amount;
        }
      }
      return {
        ...row,
        amountMonthly: convertedAmountMonthly,
      };
    });
  }

  const scrapCostByMonth = calculateScrapCostByMonth(
    bomDataConverted,
    quantitiesByPartNoMonth,
    yearMonthListByFiscalYear,
    laborCostPerMinute,
    factoryExpensePerMinute
  );
  const scrapCostByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, scrapCostByMonth);
  // #endregion

  // #region 客先指定部品費（実質的営業利益率用）
  // 客先指定部品のseqリストをPartNo別に作成
  const customerSpecifiedSeqsByPartNo: Record<string, string[]> = {};
  for (const partNo of Object.keys(bomDataByPartNo)) {
    const bomRows = bomDataByPartNo[partNo];
    const customerSpecifiedSeqs = bomRows
      .filter(row => {
        const isCustomerSpecified: boolean = row.isCustomerSpecifiedParts
          ? JSON.parse(row.isCustomerSpecifiedParts)
          : false;
        return isCustomerSpecified;
      })
      .map(row => row.seq ?? "")
      .filter(seq => seq !== "");
    if (customerSpecifiedSeqs.length > 0) {
      customerSpecifiedSeqsByPartNo[partNo] = customerSpecifiedSeqs;
    }
  }

  // PartNo別のmaterialCostWithInhouseProfitデータを作成
  const materialCostByPartNo: { partNo: string, materialCostData: CBomDataWithMonthly[] }[] = partNoData
    .filter(part => part.partNo && part.materialCostWithInhouseProfit)
    .map(part => {
      try {
        const materialCostData: CBomDataWithMonthly[] = JSON.parse(part.materialCostWithInhouseProfit!);
        return { partNo: part.partNo!, materialCostData };
      } catch {
        return { partNo: part.partNo!, materialCostData: [] };
      }
    });

  // 客先指定部品材料費（月別）を計算
  // materialCostWithInhouseProfitは既にローカル通貨で保存されている
  const customerSpecifiedPartsCostByMonthLocal = calculateCustomerSpecifiedPartsCostByMonth(
    customerSpecifiedSeqsByPartNo,
    materialCostByPartNo,
    quantitiesByPartNoMonth,
    yearMonthListByFiscalYear
  );

  // ローカル通貨からターゲット通貨に変換
  const localCurrencyIdForCustomerSpecified = getLocalCurrencyId(projectData?.companyPlantSnapshot);
  const localCurrencyNameForCustomerSpecified = currencyData?.find(c => c.currencyId === localCurrencyIdForCustomerSpecified)?.currencyName || "";
  const customerSpecifiedPartsCostByMonthCalc = customerSpecifiedPartsCostByMonthLocal.map((value, i) => {
    if (!localCurrencyNameForCustomerSpecified || localCurrencyNameForCustomerSpecified === targetCurrencyName) {
      return value;
    }
    if (convertFn) {
      return convertFn(value, localCurrencyNameForCustomerSpecified, targetCurrencyName, labels[i]?.replace("/", "-"));
    }
    return convertCurrency(value, localCurrencyNameForCustomerSpecified, targetCurrencyName, currencyData);
  });
  // #endregion

  // #region 売上原価
  const salesCostByMonth = mergeYearMonthValues(
    totalMaterialCostByMonth,
    outsideCostByYearMonth,
    laborCostByYearMonth,
    domesticLaborCostByYearMonth,
    factoryExpensesByMonth,
    domesticFactoryExpensesByYearMonth,
    scrapCostByYearMonth,
  );
  // #endregion

  // #region 生産工場販管費
  const productionSgaExpensesCurrency = currencyData?.find(
    c => c.currencyId === projectData?.productionSgaExpensesCurrencyId
  )?.currencyName;

  const appliedLaborCostByMonthArray = convertMonthlyExpenseData(
    monthlyExpenseData,
    "productionSgaExpenses",
    productionSgaExpensesCurrency,
    targetCurrencyName,
    currencyData,
    yearMonthListByFiscalYear,
    decimalCurrency,
    "convertProductionSgaExpenses",
    convertFn
  );
  const appliedLaborCostByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, appliedLaborCostByMonthArray);
  // #endregion

  // #region 国内工場販管費
  const domesticSgaExpensesCurrency = currencyData?.find(
    c => c.currencyId === projectData?.domesticSgaExpensesCurrencyId
  )?.currencyName;

  const domesticSgaCostByMonth = convertMonthlyExpenseData(
    monthlyExpenseData,
    "domesticSgaExpenses",
    domesticSgaExpensesCurrency,
    targetCurrencyName,
    currencyData,
    yearMonthListByFiscalYear,
    decimalCurrency,
    "convertDomesticSgaExpenses",
    convertFn
  );
  const domesticSgaCostByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, domesticSgaCostByMonth);
  // #endregion

  // #region 本社・販社販管費
  const salesCompanySgaCurrency = currencyData?.find(
    c => c.currencyId === projectData?.salesCompanySgaCurrencyId
  )?.currencyName;

  const salesManagementCostByMonthArray = convertMonthlyExpenseData(
    monthlyExpenseData,
    "salesCompanySga",
    salesCompanySgaCurrency,
    targetCurrencyName,
    currencyData,
    yearMonthListByFiscalYear,
    decimalCurrency,
    "convertSalesCompanySga",
    convertFn
  );
  const salesManagementCostByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, salesManagementCostByMonthArray);
  // #endregion

  // #region 開発費
  let devCostByMonth: { year: number; month: number; value: Decimal }[] = [];
  if (projectData?.devCost) {
    const devCost: MonthValue[] = JSON.parse(projectData.devCost);
    const devCostCurrency = currencyData?.find(
      c => c.currencyId === projectData?.devCostCurrencyId
    )?.currencyName;

    const devCostByMonthNew = convertMonthlyValueData(
      devCost,
      devCostCurrency,
      targetCurrencyName,
      currencyData,
      yearMonthListByFiscalYear,
      decimalCurrency,
      "convertDevCost",
      convertFn
    );
    devCostByMonth = convertToYearMonthObject(yearMonthListByElapsedYears, devCostByMonthNew);
  }
  // #endregion

  // #region ロイヤリティ
  const royaltyCurrency = currencyData?.find(
    c => c.currencyId === projectData?.royaltyCurrencyId
  )?.currencyName;

  const royaltyByMonth = convertMonthlyExpenseData(
    monthlyExpenseData,
    "royalty",
    royaltyCurrency,
    targetCurrencyName,
    currencyData,
    yearMonthListByFiscalYear,
    decimalCurrency,
    "convertRoyalty",
    convertFn
  );
  const royaltyByYearMonth = convertToYearMonthObject(yearMonthListByElapsedYears, royaltyByMonth);
  // #endregion

  // #region 販売費及び一般管理費
  const sGAExpensesByMonth = mergeYearMonthValues(
    appliedLaborCostByYearMonth,
    domesticSgaCostByYearMonth,
    salesManagementCostByYearMonth,
    devCostByMonth,
    royaltyByYearMonth,
  );
  // #endregion

  // #region その他の経費
  let otherCostByMonth: { year: number; month: number; value: Decimal }[] = [];
  if (projectData?.otherCost) {
    const otherCost: MonthValue[] = JSON.parse(projectData.otherCost);
    const otherCostCurrency = currencyData?.find(
      c => c.currencyId === projectData?.otherCostCurrencyId
    )?.currencyName;

    const otherCostByMonthNew = convertMonthlyValueData(
      otherCost,
      otherCostCurrency,
      targetCurrencyName,
      currencyData,
      yearMonthListByFiscalYear,
      decimalCurrency,
      "convertOtherCost",
      convertFn
    );
    otherCostByMonth = convertToYearMonthObject(yearMonthListByElapsedYears, otherCostByMonthNew);
  }
  // #endregion

  // #region 営業利益
  // No.411対応（B案）: ダウンサイドの生産期間短縮時、コスト区分ごとに集計期間を変える。
  // - 減価償却費系（投資対象設備・工場建物・その他設備・国内設備等）: フル期間で計上
  // - 稼働連動費系（材料費・外注費・労務費・償却費以外の工場経費・仕損費・販管費・その他経費）:
  //   短縮後の期間（labels.length）で打ち切る
  // ※返却する各コスト明細（salesCost/factoryExpenses等）は表示期間内のみを切り出すため、
  //   ここでは営業利益算出専用の集計を別途組み立てる（明細表示への影響はない）。
  const activeMonthLength = labels.length;

  // 生産工場経費（営業利益算出用）= 償却費以外（打ち切り） + 減価償却費（フル期間）
  const factoryExpensesForProfit = mergeYearMonthValues(
    truncateOperationalCostArray(overHeadCostByYearMonth, activeMonthLength),
    depreciationByMonth,
  );

  // 国内工場経費（営業利益算出用）= 償却費以外（打ち切り） + 減価償却費（フル期間）
  const domesticFactoryExpensesForProfit = mergeYearMonthValues(
    truncateOperationalCostArray(domesticOverHeadCostByYearMonth, activeMonthLength),
    domesticDepreciationByYearMonth,
  );

  // 売上原価（営業利益算出用）
  const salesCostForProfit = mergeYearMonthValues(
    truncateOperationalCostArray(totalMaterialCostByMonth, activeMonthLength),
    truncateOperationalCostArray(outsideCostByYearMonth, activeMonthLength),
    truncateOperationalCostArray(laborCostByYearMonth, activeMonthLength),
    truncateOperationalCostArray(domesticLaborCostByYearMonth, activeMonthLength),
    factoryExpensesForProfit,
    domesticFactoryExpensesForProfit,
    truncateOperationalCostArray(scrapCostByYearMonth, activeMonthLength),
  );

  // 販管費（営業利益算出用）: すべて稼働連動費のため打ち切り
  const sGAExpensesForProfit = mergeYearMonthValues(
    truncateOperationalCostArray(appliedLaborCostByYearMonth, activeMonthLength),
    truncateOperationalCostArray(domesticSgaCostByYearMonth, activeMonthLength),
    truncateOperationalCostArray(salesManagementCostByYearMonth, activeMonthLength),
    truncateOperationalCostArray(devCostByMonth, activeMonthLength),
    truncateOperationalCostArray(royaltyByYearMonth, activeMonthLength),
  );

  const profitByMonth = subtractYearMonthValues(
    totalSalesByMonth.map(({ year, month, sales: s }) => ({ year, month, value: s, })),
    salesCostForProfit,
    sGAExpensesForProfit,
    truncateOperationalCostArray(otherCostByMonth, activeMonthLength),
  );

  // 表示期間外（生産終了後）に残る減価償却費の合計。
  // 実質的営業利益率の分子（営業利益合計）へ反映するために別途返却する。
  const residualDepreciationTotal = profitByMonth
    .slice(activeMonthLength)
    .reduce((sum, entry) => sum.plus(entry.value), new Decimal(0));
  // #endregion

  // #region PJ単位回収（金型・開発費・設備費）
  // getProjectSalesByMonthを使用して正しく月次データを取得
  // targetCurrencyNameに変換（他のコスト項目と同様に対象通貨で統一する）
  // 振り返りモード: convertFnを渡して月別レートで変換
  const projectMoldSalesByMonth = getProjectSalesByMonth(
    startDateForPJ,
    moldSalesByProject,
    targetCurrencyName,
    currencyData,
    convertFn,
  );

  const projectDevSalesByMonth = getProjectSalesByMonth(
    startDateForPJ,
    developmentSalesByProject,
    targetCurrencyName,
    currencyData,
    convertFn,
  );

  const projectEquipmentSalesByMonth = getProjectSalesByMonth(
    startDateForPJ,
    equipmentSalesByProject,
    targetCurrencyName,
    currencyData,
    convertFn,
  );

  // PJ回収用のyearMonthListを生成（originalProjectStartDateベースで年マッピングを正しく）
  // partNoDataがスライス済みの場合、totalFiscalYearsByPartNoでは年数が不足するため、
  // labelsの最後の月をカバーできる年数を算出して使用
  const lastLabel = labels[labels.length - 1];
  const startDateForPJParts = startDateForPJ.split("-").map(Number);
  const lastLabelParts = lastLabel ? lastLabel.split("/").map(Number) : [0, 0];
  const totalFiscalYearsForPJ = Math.max(
    totalFiscalYearsByPartNo,
    lastLabelParts[0] - startDateForPJParts[0] + 2
  );
  const yearMonthListForPJElapsed = generateYearMonthList(startDateForPJ, totalFiscalYearsForPJ, true);
  const yearMonthListForPJCalendar = generateYearMonthList(startDateForPJ, totalFiscalYearsForPJ, false);
  const labelToElapsedYearForPJ = new Map<string, { year: number; month: number }>();
  yearMonthListForPJElapsed.forEach(({ year, month }, idx) => {
    const calEntry = yearMonthListForPJCalendar[idx];
    if (calEntry) {
      const label = `${calEntry.year}/${String(calEntry.month).padStart(2, "0")}`;
      labelToElapsedYearForPJ.set(label, { year, month });
    }
  });

  // PJ単位金型回収（月次）- labels → 経過年マッピングで正しくルックアップ
  const moldSalesByMonth: Decimal[] = labels.map(label => {
    const elapsed = labelToElapsedYearForPJ.get(label);
    if (!elapsed) return new Decimal(0);
    const found = projectMoldSalesByMonth.find(e => e.year === elapsed.year && e.month === elapsed.month);
    return found?.sales || new Decimal(0);
  });

  // PJ単位開発費回収（月次）
  const devSalesByMonth: Decimal[] = labels.map(label => {
    const elapsed = labelToElapsedYearForPJ.get(label);
    if (!elapsed) return new Decimal(0);
    const found = projectDevSalesByMonth.find(e => e.year === elapsed.year && e.month === elapsed.month);
    return found?.sales || new Decimal(0);
  });

  // PJ単位設備費回収（月次）
  const equipmentSalesByMonthResult: Decimal[] = labels.map(label => {
    const elapsed = labelToElapsedYearForPJ.get(label);
    if (!elapsed) return new Decimal(0);
    const found = projectEquipmentSalesByMonth.find(e => e.year === elapsed.year && e.month === elapsed.month);
    return found?.sales || new Decimal(0);
  });
  // #endregion

  // #region 製品売上（売上合計 - PJ単位回収）
  const productSalesByMonth = labels.map((_, index) => {
    const total = sales[index] || new Decimal(0);
    const mold = moldSalesByMonth[index] || new Decimal(0);
    const dev = devSalesByMonth[index] || new Decimal(0);
    const equip = equipmentSalesByMonthResult[index] || new Decimal(0);
    return total.minus(mold).minus(dev).minus(equip);
  });
  // #endregion

  // 結果をMonthlyProfitAndLossData形式で返す
  // 期間短縮はExcel出力側で既にlabelsをsliceして渡しているため、ここではlabels.lengthをそのまま使用
  //
  // No.411対応（9章）: extendedLabels指定時は出力長をそちらに合わせて延長する。
  // - 減価償却費系（フル期間で内部保持済み）: そのままoutputLengthまで切り出す（追加計算不要）
  // - 稼働連動費系・売上系（labelsの長さまでしか値がない）: 延長部分は0埋め
  // - profit（profitByMonth）: 既にactiveMonthLength以降が稼働連動費0＋減価償却費のみで
  //   フル期間分計算済みのため、そのままoutputLengthまで切り出せば区分ロジックを再利用できる
  const outputLength = extendedLabels && extendedLabels.length > labels.length
    ? extendedLabels.length
    : labels.length;
  const outputLabels = extendedLabels && extendedLabels.length > labels.length
    ? extendedLabels
    : labels;

  // 減価償却費系（フル期間で内部保持済み）: そのままoutputLengthまで切り出す
  const extractValues = (arr: { year: number; month: number; value: Decimal }[]): Decimal[] => {
    return arr.slice(0, outputLength).map(e => e.value);
  };

  // 延長時のみ有効: 稼働連動費と減価償却費の合成項目（生産工場経費・国内工場経費・販管費）は
  // 出力用の生値だと稼働連動費部分が打ち切られていないため、営業利益算出用の打ち切り済み合成値
  // （factoryExpensesForProfit等）を代わりに使う。未延長時は従来通りの生値を維持する。
  const extractMergedValues = (
    rawArr: { year: number; month: number; value: Decimal }[],
    truncatedArr: { year: number; month: number; value: Decimal }[]
  ): Decimal[] => extractValues(extendedLabels && extendedLabels.length > labels.length ? truncatedArr : rawArr);

  // 稼働連動費系・売上系（labels.length分しか値がない配列）: 延長部分を0埋めする
  const extractOperationalValues = (arr: { year: number; month: number; value: Decimal }[]): Decimal[] => {
    const values = arr.slice(0, labels.length).map(e => e.value);
    if (outputLength <= values.length) {
      return values;
    }
    return [...values, ...Array.from({ length: outputLength - values.length }, () => new Decimal(0))];
  };
  const extendOperationalDecimals = (values: Decimal[]): Decimal[] => {
    const sliced = values.slice(0, labels.length);
    if (outputLength <= sliced.length) {
      return sliced;
    }
    return [...sliced, ...Array.from({ length: outputLength - sliced.length }, () => new Decimal(0))];
  };

  // ゼロ配列を生成するヘルパー
  const zeroArray = (): Decimal[] => outputLabels.map(() => new Decimal(0));

  return {
    labels: outputLabels,
    totalSales: extendOperationalDecimals(sales),
    productSales: extendOperationalDecimals(productSalesByMonth),
    moldSales: extendOperationalDecimals(moldSalesByMonth),
    devSales: extendOperationalDecimals(devSalesByMonth),
    equipmentSales: extendOperationalDecimals(equipmentSalesByMonthResult),
    salesCost: extractMergedValues(salesCostByMonth, salesCostForProfit),
    materialCost: extendOperationalDecimals(convertedMaterialCosts),
    outsideCost: extractOperationalValues(outsideCostByYearMonth),
    laborCost: extractOperationalValues(laborCostByYearMonth),
    domesticLaborCost: extractOperationalValues(domesticLaborCostByYearMonth),
    factoryExpenses: extractMergedValues(factoryExpensesByMonth, factoryExpensesForProfit),
    overHeadCost: extractOperationalValues(overHeadCostByYearMonth),
    depreciation: extractValues(depreciationByMonth),
    equipmentDepreciation: extractValues(equipmentDepreciationByMonth),
    pastEquipmentCost: pastEquipmentCostByYearMonth.length > 0 ? extractValues(pastEquipmentCostByYearMonth) : zeroArray(),
    factoryCost: extractValues(factoryCostByMonth),
    otherEquipmentCost: otherEquipmentCostByMonth.length > 0 ? extractValues(otherEquipmentCostByMonth) : zeroArray(),
    domesticFactoryExpenses: extractMergedValues(domesticFactoryExpensesByYearMonth, domesticFactoryExpensesForProfit),
    domesticOverHeadCost: extractOperationalValues(domesticOverHeadCostByYearMonth),
    domesticDepreciation: extractValues(domesticDepreciationByYearMonth),
    domesticInvestmentDepreciation: extractValues(domesticInvestmentDepreciationByYearMonth),
    domesticFactoryBuildingDepreciation: extractValues(domesticFactoryBuildingDepreciationByYearMonth),
    domesticOtherDepreciation: extractValues(domesticOtherDepreciationByYearMonth),
    scrapCost: extractOperationalValues(scrapCostByYearMonth),
    sGAExpenses: extractOperationalValues(sGAExpensesByMonth),
    appliedLaborCost: extractOperationalValues(appliedLaborCostByYearMonth),
    domesticSgaCost: extractOperationalValues(domesticSgaCostByYearMonth),
    salesManagementCost: extractOperationalValues(salesManagementCostByYearMonth),
    devCost: devCostByMonth.length > 0 ? extractOperationalValues(devCostByMonth) : zeroArray(),
    royalty: extractOperationalValues(royaltyByYearMonth),
    otherCost: otherCostByMonth.length > 0 ? extractOperationalValues(otherCostByMonth) : zeroArray(),
    profit: extractValues(profitByMonth),
    customerSpecifiedPartsCost: extendOperationalDecimals(customerSpecifiedPartsCostByMonthCalc),
    // 表示期間外（生産終了後）に残る減価償却費の合計（No.411対応）。
    // 実質的営業利益率の分子へ加算し、営業利益合計と区分を揃えるために使用する
    residualDepreciationTotal,
  };
}